/**
 * Catalan (ca) resource catalog — Onada 1.
 *
 * Namespaces follow docs/plan/04-i18n-and-design.md. Keys are English code identifiers
 * (CLAUDE.md convention); only the values are localized. The full 118-term domain
 * vocabulary lives under the `domain.*` namespaces and is reused by all 3 apps.
 *
 * PARITY: the key set here MUST be identical to es.ts. The i18n/index.ts factory asserts
 * parity in development so the two catalogs never drift.
 */

import { existenciesCa } from './existencies/ca.js';
import { qualityCa } from './quality/ca.js';
import { compresCa } from './compres/ca.js';
import { catalegCa } from './cataleg/ca.js';

export const ca = {
  // ── Cross-cutting infra ────────────────────────────────────────────────────
  common: {
    appName: 'Obrava',
    save: 'Desa',
    cancel: 'Cancel·la',
    confirm: 'Confirma',
    ok: 'D’acord',
    delete: 'Elimina',
    edit: 'Edita',
    add: 'Afegeix',
    search: 'Cerca',
    back: 'Enrere',
    next: 'Següent',
    close: 'Tanca',
    loading: 'Carregant…',
    retry: 'Torna-ho a provar',
    yes: 'Sí',
    no: 'No',
    all: 'Tots',
    none: 'Cap',
    today: 'Avui',
    required: 'Obligatori',
    optional: 'Opcional',
    showPassword: 'Mostra la contrasenya',
    hidePassword: 'Amaga la contrasenya',
    updateAvailableTitle: 'Hi ha una actualització disponible',
    updateAvailableBody: 'Desa el que estiguis fent i actualitza per carregar l’última versió.',
    updateAction: 'Actualitza',
  },

  auth: {
    signIn: 'Inicia sessió',
    signOut: 'Tanca sessió',
    pin: 'PIN',
    enterPin: 'Introdueix el PIN',
    email: 'Correu electrònic',
    password: 'Contrasenya',
    wrongPin: 'PIN incorrecte',
    selectOperator: 'Selecciona operari',
    shift: 'Torn',
  },

  errors: {
    generic: 'Hi ha hagut un error',
    network: 'Error de connexió',
    notFound: 'No s’ha trobat',
    forbidden: 'No tens permís',
    conflict: 'Conflicte de dades',
    tryAgain: 'Torna-ho a provar',
  },

  validation: {
    required: 'Aquest camp és obligatori',
    invalidEmail: 'Correu electrònic no vàlid',
    invalidNumber: 'Número no vàlid',
    mustBePositive: 'Ha de ser positiu',
    reasonRequired: 'Cal indicar un motiu',
    tooShort: 'Massa curt',
    tooLong: 'Massa llarg',
  },

  sync: {
    online: 'En línia',
    offline: 'Sense connexió',
    syncing: 'Sincronitzant',
    syncError: 'Error de sincronització',
    pendingChanges: 'Canvis pendents',
    allSynced: 'Tot sincronitzat',
    savedOffline: 'Desat sense connexió',
    title: 'Sincronització',
    queued: '{{count}} operacions en cua',
    retrying: 'Reintentant automàticament…',
    stuck: 'No s’ha pogut sincronitzar. Continuem provant-ho; la feina està desada al dispositiu.',
    blocked: 'Sessió caducada. Torna a entrar per sincronitzar la feina pendent.',
    lastSync: 'Última sincronització',
    never: 'Encara no',
    lastError: 'Últim error',
    attempts: 'Intents',
    syncNow: 'Sincronitza ara',
    downloadStuck:
      'No s’ha pogut baixar cap dada nova. Continuem provant-ho; el que veus és l’última còpia desada al dispositiu.',
    downloadTenantMoved:
      'L’obrador actiu ha canviat des de l’oficina. Torna a triar obrador per continuar rebent dades.',
    lastDownload: 'Última baixada',
    lastDownloadError: 'Últim error de baixada',
    retryDownload: 'Torna a baixar ara',
  },

  notifications: {
    title: 'Notificacions',
    markRead: 'Marca com a llegida',
    empty: 'No hi ha notificacions',
    lowStock: 'Existències sota mínim',
    expirySoon: 'Caducitat propera',
    incidentHigh: 'Incidència greu',
  },

  // ── Domain vocabulary (the 118 shared terms, split per doc 04) ───────────────
  'domain.articles': {
    article: 'Article',
    rawMaterial: 'Matèria primera',
    'rawMaterial.abbr': 'MP',
    intermediateProduct: 'Producte semielaborat',
    finishedProduct: 'Producte acabat',
    packaging: 'Embalatge',
    bom: 'Llista d’ingredients',
    'bom.abbr': 'BOM',
    technicalSheet: 'Fitxa tècnica',
    'technicalSheet.abbr': 'FT',
    smartImporter: 'Importador intel·ligent',
  },

  'domain.recipes': {
    recipe: 'Recepta',
    recipeVersion: 'Versió de recepta',
    recipeStep: 'Pas d’elaboració',
    recipeScaling: 'Escalat de recepta',
    yield: 'Rendiment',
    leadTime: 'Temps d’antelació',
  },

  'domain.lots': {
    lot: 'Lot',
    finishedLot: 'Lot de producte acabat',
    supplierLot: 'Lot de proveïdor',
    internalLot: 'Lot intern',
    lotNumber: 'Número de lot',
    expiry: 'Caducitat',
    shelfLife: 'Vida útil',
    productionDate: 'Data d’elaboració',
    label: 'Etiqueta',
  },

  'domain.stock': {
    stock: 'Existències',
    stockMovement: 'Moviment d’existències',
    stockAdjustment: 'Ajust d’existències',
    inventory: 'Inventari',
    stockCount: 'Recompte',
    minimumStock: 'Estoc mínim',
    belowMinimum: 'Sota mínim',
    loss: 'Merma',
    'lossReason.burnt': 'Cremat',
    'lossReason.dropped': 'Caigut',
    'lossReason.expired': 'Caducat',
    warehouseZone: 'Zona de magatzem',
    'zone.dry': 'Sec',
    'zone.refrigerated': 'Refrigerat',
    'zone.frozen': 'Congelat',
    'zone.shipping': 'Expedicions',
    'zone.quarantine': 'Quarantena',
  },

  'domain.recipes.orders': {
    workOrder: 'Ordre de treball',
    'workOrder.abbr': 'OT',
    productionOrder: 'Ordre de fabricació',
    'productionOrder.abbr': 'OF',
    packagingOrder: 'Ordre d’envasat',
    cleaningOrder: 'Ordre de neteja',
    weighing: 'Pesatge',
    consumption: 'Consum',
    plannedQuantity: 'Quantitat prevista',
    realQuantity: 'Quantitat real',
    deviation: 'Desviació',
    closing: 'Tancament',
    closeWorkOrder: 'Tancar OT',
    agenda: 'Agenda de producció',
    planning: 'Planificació',
  },

  'domain.quality': {
    traceability: 'Traçabilitat',
    recall: 'Retirada de producte',
    haccp: 'APPCC',
    criticalControlPoint: 'Punt de control crític',
    'criticalControlPoint.abbr': 'PCC',
    nonConformity: 'No conformitat',
    correctiveAction: 'Acció correctora',
    incident: 'Incidència',
    severity: 'Gravetat',
    'severity.low': 'Baixa',
    'severity.medium': 'Mitjana',
    'severity.high': 'Alta',
    auditLog: 'Registre d’auditoria',
  },

  // Annex II of Reg. (EU) 1169/2011, verbatim: these are DENOMINATIONS, not tags.
  // Shortening one changes what the fitxa tècnica and the label declare, so they
  // stay whole — the sulphite threshold included. Lactose is NOT a separate
  // allergen: it lives inside the milk denomination.
  'domain.allergens': {
    allergen: 'Al·lergen',
    allergenTraces: 'Traces',
    gluten: 'Cereals amb gluten i derivats',
    crustaceans: 'Crustacis i derivats',
    eggs: 'Ous i derivats',
    fish: 'Peix i derivats',
    peanuts: 'Cacauets i derivats',
    soybeans: 'Soja i derivats',
    milk: 'Llet i derivats (inclosa la lactosa)',
    nuts: 'Fruits de closca i derivats',
    celery: 'Api i derivats',
    mustard: 'Mostassa i derivats',
    sesame: 'Sèsam i derivats',
    sulphites: 'Diòxid de sofre i sulfits (>10 mg/kg o >10 mg/l)',
    lupin: 'Tramussos i derivats',
    molluscs: 'Mol·luscs i derivats',
  },

  'domain.roles': {
    operator: 'Operari',
    productionManager: 'Responsable de producció',
    quality: 'Qualitat',
    purchasing: 'Compres',
    sales: 'Vendes',
    admin: 'Administrador',
    viewer: 'Consulta',
    section: 'Secció',
    workPoint: 'Punt de treball',
    device: 'Dispositiu',
    scale: 'Bàscula',
    labelPrinter: 'Etiquetadora',
  },

  'domain.units': {
    'unit.kg': 'kg',
    'unit.units': 'uds',
    'unit.boxes': 'caixes',
    'unit.loaves': 'barres',
    name_kg: 'Quilogram',
    name_L: 'Litre',
    name_Un: 'Unitat',
    handlingUnit: 'Unitat de manipulació',
    'handlingUnit.abbr': 'HU',
    fefo: 'FEFO',
  },

  'domain.status': {
    'status.planned': 'Planificada',
    'status.inProgress': 'En curs',
    'status.completed': 'Completada',
    'status.cancelled': 'Cancel·lada',
    'status.pending': 'Pendent',
    'connection.online': 'Sincronitzat',
    'connection.offline': 'Sense connexió',
    'connection.syncing': 'Sincronitzant',
    'connection.pending': '{{count}} per sincronitzar',
    'connection.pendingRetrying_one': '{{count}} pendent · reintentant',
    'connection.pendingRetrying_other': '{{count}} pendents · reintentant',
    'connection.sessionExpired': 'Sessió caducada',
    'connection.stale': 'Dades desactualitzades',
    'reason.required': 'Motiu',
    'shift.morning': 'Matí',
    'shift.afternoon': 'Tarda',
    'shift.night': 'Nit',
  },

  'domain.parties': {
    obrador: 'Obrador',
    tenant: 'Obrador',
    supplier: 'Proveïdor',
    client: 'Client',
    reception: 'Recepció',
    manualReception: 'Recepció manual',
    deliveryNote: 'Albarà',
    purchaseOrder: 'Ordre de compra',
    salesOrder: 'Comanda de venda',
    shipment: 'Expedició',
    shift: 'Torn',
    alert: 'Avís',
    notification: 'Notificació',
  },

  // Auditoria (F0.9) — accions, actor, columnes i labels d'entitat compartits pel
  // visor del backoffice i el de la consola.
  'domain.audit': {
    action_create: 'Alta',
    action_update: 'Modificació',
    action_delete: 'Baixa',
    action_restore: 'Reactivació',
    action_support_view: 'Accés de suport',
    action_tenant_switch: 'Canvi d’obrador',
    action_packaging_labels_lost: 'Tria d’etiquetes no desada',
    action_reception_exceptions_lost: 'Excepcions de recepció no obertes',
    action_reception_quality_nc_lost: 'No conformitats de recepció no obertes',
    action_reception_on_unavailable_order: 'Recepció d’una comanda no disponible',
    action_pin_issued: 'PIN emès',
    action_pin_regenerated: 'PIN regenerat',
    action_pin_revoked: 'PIN retirat',
    action_pin_reactivated: 'PIN reactivat',
    action_pin_converted: 'Conversió a PIN',
    action_pin_login: 'Entrada amb PIN',
    action_device_enrolled: 'Dispositiu enrolat',
    action_device_revoked: 'Dispositiu revocat',
    action_device_unlock: 'Dispositiu desbloquejat',
    action_stock_without_expiry: 'Existències sense caducitat',
    action_consumption_on_unavailable_lot: 'Consum d’un lot no disponible',
    action_stock_state_change: 'Canvi d’estat d’existències',
    action_reception_missing_data: 'Recepció sense proveïdor o sense lot',
    action_access_code_sent: 'Codi d’accés enviat',
    actorSystem: 'Sistema',
    actorSupport: 'Obrava (suport)',
    supportBadge: 'Suport Obrava',
    colWhen: 'Data i hora',
    colWho: 'Usuari',
    colAction: 'Acció',
    colEntity: 'Element',
    colDetails: 'Detalls',
    reasonLabel: 'Motiu',
    entity_article_categories: 'Categoria d’article',
    entity_articles: 'Article',
    entity_article_ingredients: 'Ingredient d’article',
    entity_article_food_safety_notes: 'Seguretat alimentària d’article',
    entity_clients: 'Client',
    entity_client_shipping_addresses: 'Adreça d’enviament de client',
    entity_compliance_items: 'Element de compliment',
    entity_conservation_conditions: 'Condició de conservació',
    entity_container_types: 'Tipus d’envàs',
    entity_devices: 'Dispositiu',
    entity_ingredients: 'Ingredient',
    entity_label_templates: 'Plantilla d’etiqueta',
    entity_legislation_notes: 'Nota de legislació',
    entity_packaging_units: 'Unitat d’embalatge',
    entity_preparation_modes: 'Mode de preparació',
    entity_processes: 'Procés',
    entity_process_resources: 'Recurs d’un procés',
    entity_recipes: 'Recepta',
    entity_recipe_versions: 'Versió de recepta',
    entity_resources: 'Recurs',
    entity_resource_types: 'Tipus de recurs',
    entity_sections: 'Secció',
    entity_shifts: 'Torn',
    entity_storage_locations: 'Ubicació',
    entity_supplier_articles: 'Article de proveïdor',
    entity_suppliers: 'Proveïdor',
    entity_tech_sheets: 'Fitxa tècnica',
    entity_taxes: 'Impost',
    entity_units: 'Unitat',
    entity_usage_types: 'Tipus d’ús',
    entity_warehouse_zones: 'Zona de magatzem',
    entity_work_points: 'Punt de treball',
    entity_users: 'Usuari',
    entity_user_roles: 'Rol d’usuari',
    entity_user_menu_access: 'Visibilitat de menú',
    entity_supplier_documents: 'Document de proveïdor',
    entity_non_conformities: 'No conformitat',
    entity_corrective_actions: 'Acció correctora',
    entity_self_check_templates: 'Plantilla d’autocontrol',
    entity_self_check_template_fields: 'Camp d’autocontrol',
    entity_worker_trainings: 'Formació de manipulador',
    entity_tenant_settings: 'Configuració',
    entity_purchase_orders: 'Comanda de compra',
    entity_purchase_order_lines: 'Línia de comanda',
    entity_purchase_order_templates: 'Plantilla de comanda',
    entity_purchase_order_template_lines: 'Línia de plantilla',
    entity_receptions: 'Recepció',
    entity_reception_exceptions: 'Excepció de recepció',
    entity_purchase_returns: 'Devolució de compra',
    entity_stock_state_changes: 'Estat d’un lot',
    entity_stock_counts: 'Recompte d’existències',
    entity_lot_label_reprints: 'Reimpressió d’etiqueta de lot',
    entity_reception_corrections: 'Rectificació de recepció',
    entity_work_orders: 'Ordre de treball',
    entity_work_order_resources: 'Recurs d’una OT',
    entity_work_order_packaging_labels: 'Etiquetes d’envàs demanades',
    entity_sales_orders: 'Comanda de venda',
    entity_sales_order_lines: 'Línia de comanda de venda',
    entity_shipments: 'Expedició',
    entity_shipment_lines: 'Línia d’expedició',
    entity_audit_log: 'Registre d’activitat',
  },

  // ── App-screen namespaces (Onada 1) ──────────────────────────────────────────
  'planta.login': {
    queuedForOtherObradorNamed:
      'Aquest dispositiu encara té feina pendent de sincronitzar de {{obrador}}. Entra a {{obrador}}, espera que se sincronitzi i després canvia d’obrador.',
    queuedForOtherObrador:
      'Aquest dispositiu encara té feina pendent de sincronitzar d’un altre obrador. Entra-hi, espera que se sincronitzi i després canvia d’obrador.',
    noPlantaAccessNamed:
      'Aquest usuari no té accés a l’App planta a {{obrador}}. Demana a l’administrador que t’hi afegeixi el rol d’Operari.',
    noPlantaAccess:
      'Aquest usuari no té accés a l’App planta. Demana a l’administrador que t’hi afegeixi el rol d’Operari.',
    noPlantaAccessOtherObradors: 'Si treballes en un altre obrador, canvia-hi des del Backoffice.',
    title: 'Obrava',
    subtitle: 'Everything, under control.',
    signInTitle: 'Inicia el torn',
    email: 'Correu electrònic',
    emailPlaceholder: 'operari@obrador.cat',
    password: 'Contrasenya',
    signInCta: 'Entra',
    signingIn: 'Entrant…',
    preparing: 'Preparant les teves ordres…',
    invalidCredentials: 'Correu o contrasenya incorrectes',
    noTenantTitle: 'Compte sense obrador',
    noTenantBody:
      'Aquest compte no té cap obrador assignat. Contacta amb el responsable del teu obrador.',
    selectObrador: 'Selecciona l’obrador',
    selectObradorBody: 'El teu compte té accés a més d’un obrador. Tria amb quin treballes en aquest torn.',
    continue: 'Continua',
    // Entrada amb PIN i enrolament de la tauleta (OBR-25)
    pinTitle: 'Entra amb el teu PIN',
    pinSubtitle: 'Escriu els {{total}} caràcters del teu PIN.',
    pinSlotsLabel: 'PIN, {{entered}} de {{total}} caràcters escrits',
    pinBackspace: 'Esborra l’últim caràcter',
    pinInvalid: 'PIN incorrecte',
    pinConnecting: 'Connectant…',
    pinChecking: 'Comprovant el PIN…',
    pinOffline: 'Sense connexió. Per entrar amb PIN cal tenir connexió.',
    pinTryLater: 'No s’ha pogut entrar ara mateix. Torna-ho a provar.',
    pinLockedTimed:
      'Aquesta tauleta s’ha bloquejat per massa PINs erronis. Torna-ho a provar d’aquí {{time}}.',
    pinLocked:
      'Aquesta tauleta s’ha bloquejat per massa PINs erronis. Demana a un responsable que la desbloquegi.',
    queuedForOtherOperator:
      'Aquesta tauleta encara té feina pendent de sincronitzar d’un altre operari. Que hi entri ell i la sincronitzi, o que un responsable hi entri amb correu i contrasenya.',
    useEmail: 'Accés amb correu',
    usePin: 'Entra amb PIN',
  },

  'planta.nav': {
    orders: 'Ordres',
    alerts: 'Avisos',
    reception: 'Recepció',
    sales: 'Vendes',
    settings: 'Més',
    badgeLabel: '{{label}}. Tasques pendents: {{count}}',
  },

  'planta.settings': {
    title: 'Preferències',
    language: 'Idioma',
    theme: 'Tema',
    themeLight: 'Clar',
    themeDark: 'Fosc',
    obrador: 'Obrador',
    version: 'Versió {{version}} · Build {{build}}',
    printer: 'Impressora d’etiquetes',
    printerChoose: 'Tria on s’imprimeixen les etiquetes',
    printerNone:
      'Cap impressora configurada. Un administrador n’ha d’afegir una al Backoffice (Configuració · Dispositius).',
    printerUnavailable: 'Aquesta versió de l’app no pot imprimir. Instal·la l’última versió.',
    printerTest: 'Prova d’impressió',
    printerTesting: 'Enviant…',
    printerTestOk: 'Prova enviada a {{printer}}',
    printerTestError:
      'No s’ha pogut imprimir a {{printer}}. Comprova que estigui encesa i connectada a la mateixa xarxa que la tauleta.',
  },

  'planta.obrador': {
    title: 'Obrador',
    change: 'Canvia d’obrador',
    sheetTitle: 'Obrador del torn',
    sheetBody:
      'Tria on treballes. Es tornaran a carregar les ordres i les dades d’aquest obrador.',
    active: 'Actiu',
    switching: 'Canviant d’obrador…',
    syncNow: 'Sincronitza ara',
    errorOffline: 'Sense connexió. Connecta’t per canviar d’obrador.',
    errorUnsynced:
      'Tens feina pendent de sincronitzar. Sincronitza-la abans de canviar d’obrador.',
    errorFailed: 'No s’ha pogut canviar d’obrador. Torna-ho a provar.',
    errorNotAMember: 'Ja no tens accés a aquest obrador.',
    errorNoAccess:
      'En aquest obrador no tens accés a l’App planta. Demana a l’administrador que t’hi afegeixi el rol d’Operari.',
    errorSignedOut: 'S’ha tancat el torn. Torna a entrar per continuar.',
  },

  'planta.home': {
    title: 'Inici',
    greeting: 'Hola, {{name}}',
    myWorkOrders: 'Les meves OT',
    today: 'Avui',
    pending: 'Pendents',
    inProgress: 'En curs',
    noWorkOrders: 'No tens ordres assignades',
    noWorkOrdersBody: 'Quan el responsable publiqui ordres per a tu, apareixeran aquí.',
    seeAll: 'Veu-les totes',
    refreshing: 'Actualitzant…',
    pullToRefresh: 'Estira per actualitzar',
    signOutConfirm: 'Vols tancar el torn?',
  },

  'planta.workOrders': {
    title: 'Ordres de treball',
    start: 'Comença',
    resume: 'Continua',
    detail: 'Detall de l’OT',
    ingredients: 'Ingredients',
    steps: 'Passos',
    filterAll: 'Totes',
    filterPending: 'Pendents',
    filterInProgress: 'En curs',
    filterCompleted: 'Fetes',
    summary: '{{pending}} pendents, {{inProgress}} en curs',
    offlineNotice: 'Pots continuar treballant. Les dades s’enviaran quan torni la connexió.',
    empty: 'No hi ha ordres en aquest estat',
    emptyBody: 'Canvia el filtre o estira per actualitzar.',
    emptyStale: 'No s’han pogut baixar les ordres',
    emptyStaleBody:
      'El que veus és l’última còpia desada al dispositiu. Continuem provant-ho; estira per tornar-hi ara.',
    quantityWithUnit: '{{quantity}} {{unit}}',
    number: 'OT {{number}}',
    executionPending: 'Execució disponible aviat',
    executionPendingBody:
      'L’execució de l’OT (pesatge, passos i tancament) arriba en una pròxima versió.',
  },

  'planta.execution': {
    title: 'Execució',
    startWorkOrder: 'Comença l’OT',
    resumeWorkOrder: 'Continua l’OT',
    starting: 'Començant…',
    step: 'Pas {{number}}',
    stepCount: 'Pas {{current}} de {{total}}',
    markStepDone: 'Marca el pas com a fet',
    stepDone: 'Pas fet',
    stepPending: 'Pendent',
    stepInProgress: 'En curs',
    stepBlockedByPcc: 'Cal registrar el control crític (PCC) per continuar',
    blockingStep: 'Pas bloquejant',
    allStepsDone: 'Tots els passos fets',
    recordControl: 'Registra el control',
    targetTemperature: 'Temperatura objectiu',
    targetTemperatureLabeled: 'Temperatura objectiu: {{value}}',
    estimatedDuration: 'Durada estimada',
    estimatedDurationLabeled: 'Durada estimada: {{value}}',
    unitCelsius: '°C',
    minutes: '{{count}} min',
    noSteps: 'Aquesta recepta no té passos',
    todayProduction: 'Producció d’avui',
    plannedVsConsumed: 'Previst / Consumit',
    notStartedTitle: 'OT a punt per començar',
    notStartedBody: 'Comença l’OT per desplegar els passos de la recepta.',
    progress_one: '{{done}} de {{count}} pas fet',
    progress_other: '{{done}} de {{count}} passos fets',
    controlBadgeWeight: 'Pesatge',
    controlBadgeTimer: 'Temporitzador',
    controlBadgeTemperature: 'Temperatura',
    controlBadgePcc: 'PCC',
    pccBlockingBadge: 'PCC bloquejant',
    controlRequired: 'Control obligatori',
    recordControlTitle: 'Registra el control',
    measurementValue: 'Valor mesurat',
    measurementUnit: 'Unitat',
    measurementNote: 'Observacions',
    measurementNotePlaceholder: 'Observacions (opcional)',
    saveMeasurement: 'Desa el control',
    savingMeasurement: 'Desant…',
    measurementSaved: 'Control registrat',
    lastMeasurement: 'Últim control: {{value}}',
    criticalLimits: 'Límits crítics: {{range}}',
    limitRangeMin: 'mín. {{min}}',
    limitRangeMax: 'màx. {{max}}',
    limitRangeBetween: '{{min}} – {{max}}',
    withinLimits: 'Dins de límits',
    outOfLimits: 'Fora de límits',
    nonConforming: 'No conforme',
    requireIncidentTitle: 'Mesura fora de límits',
    requireIncidentBody:
      'El control crític està fora dels límits. Cal registrar una incidència i una acció correctora abans de continuar.',
    logIncident: 'Registra incidència',
    goToWeighing: 'Ves al pesatge',
    reportIncidentLink: 'Incidència',
    blockedStepHint: 'Cal registrar el control crític per poder marcar aquest pas.',
      closedLockTitle: 'OT tancada',
    closedLockBody: 'Aquesta OT està finalitzada. Per modificar-ne res cal reobrir-la des del BackOffice.',
    goToRegister: 'Registre de pesades',
},

  'planta.weighing': {
    title: 'Pesatge i consum',
    subtitle: 'Registra el consum real de cada ingredient',
    planned: 'Previst',
    real: 'Real',
    deviation: 'Desviació',
    deviationValue: '{{value}} %',
    deviationWarning: 'Desviació superior al límit ({{threshold}} %)',
    deviationWarningShort: 'Desviació elevada',
    confirmWeight: 'Confirma el pes',
    weighing: 'Desant…',
    enterRealQuantity: 'Quantitat consumida real',
    selectLot: 'Selecciona el lot',
    selectArticle: 'Selecciona l’ingredient',
    ingredient: 'Ingredient',
    noIngredients: 'Aquesta recepta no té ingredients',
    recorded: 'Pes registrat',
    recordedAt: 'Registrat a les {{time}}',
    consumed: 'Consumit',
    remaining: 'Pendent',
    notStartedTitle: 'Comença l’OT abans de pesar',
    notStartedBody: 'Cal començar l’OT per registrar el consum dels ingredients.',
    weighIngredient: 'Pesa l’ingredient',
    plannedVsReal: 'Previst {{planned}} · Real {{real}}',
    todayProductionTitle: 'Producció d’avui',
    todayProductionEmpty: 'Encara no hi ha consum registrat',
    consumedVsPlanned: '{{consumed}} / {{planned}}',
    notWeighedYet: 'Sense pesar',
    source: 'Origen',
    sourceManual: 'Manual',
    sourceScale: 'Bàscula',
    save: 'Desa el consum',
    saving: 'Desant…',
    saved: 'Consum registrat',
      theoreticalQuantity: 'Quantitat teòrica',
    proposedFromRecipe: 'Proposat per la recepta',
    tolerance: 'Tolerància ±{{value}} %',
    overTolerance: 'Fora de tolerància (±{{tolerance}} %)',
    incidentAutoCreated: 'S’ha generat una incidència per desviació',
    lotsTitle: 'Lots consumits',
    lotsSubtitle: 'Tria d’on surt la matèria primera',
    lotAvailable: 'Disponible {{quantity}}',
    lotExpiry: 'Cad. {{date}}',
    lotNoExpiry: 'Sense caducitat',
    noLotsAvailable: 'No hi ha existències amb lot d’aquest ingredient',
    noLotsHint: 'Pots registrar el consum igualment; quedarà sense lot.',
    addLot: 'Afegeix un lot',
    removeLot: 'Treu el lot',
    lotQuantity: 'Quantitat d’aquest lot',
    totalSelected: 'Total seleccionat {{quantity}}',
    scanLabel: 'Escaneja l’etiqueta',
    scanTitle: 'Escaneja el QR de l’etiqueta',
    scanHint: 'Apunta la càmera al codi QR del lot',
    scanPermissionTitle: 'Cal permís de càmera',
    scanPermissionBody: 'Per llegir etiquetes, dona accés a la càmera.',
    scanPermissionGrant: 'Dona accés',
    applyTheoretical: 'Aplica la quantitat teòrica',
    applyTheoreticalHint: 'Es consumirà el que diu la recepta, sense pesar',
    sourceTheoretical: 'Teòric',
    registerOpen: 'Registre de pesades',
    registerTitle: 'Registre de pesades',
    registerSubtitle: 'Tot el que s’ha declarat en aquesta OT',
    registerEmpty: 'Encara no s’ha declarat cap consum',
    editWeighing: 'Corregeix',
    deleteWeighing: 'Elimina',
    voidTitle: 'Per què ho corregeixes?',
    voidBodyEdit: 'La pesada anterior es conservarà anul·lada, amb el motiu.',
    voidBodyDelete: 'La pesada es conservarà anul·lada, amb el motiu.',
    voidReason: 'Motiu',
    voidReasonPlaceholder: 'Ex.: error en teclejar la xifra',
    voidReasonRequired: 'Cal escriure el motiu',
    voidConfirm: 'Anul·la la pesada',
    voidedBadge: 'Anul·lada',
    correctedBadge: 'Correcció',
    voidedBy: 'Anul·lada per {{name}}',
    alreadyWeighed: 'Ja declarat',
    sequentialTitle: 'Preparació seqüencial',
    sequentialSubtitle: 'Un ingredient cada vegada, en l’ordre de la recepta',
    sequentialLocked: 'Acaba l’ingredient anterior per continuar',
    sequentialProgress: 'Ingredient {{current}} de {{total}}',
    sequentialAllDone: 'Tots els ingredients estan preparats',
    closedTitle: 'OT tancada',
    scaleNote: '×{{factor}} la recepta: les quantitats ja hi van multiplicades.',
    scaleUnknown:
      'Aquesta ordre no es pot escalar (la recepta i l’ordre no parlen de la mateixa unitat): les quantitats són les del lot base de la recepta.',
    closedBody: 'Els consums d’una OT tancada no es poden modificar.',
    noPermissionToEdit: 'No tens permís per corregir aquesta pesada',
},

  'planta.incidents': {
    photoUnavailable: 'Adjuntar foto encara no està disponible.',
    title: 'Incidència',
    subtitle: 'Notifica una incidència sense aturar la feina',
    newIncident: 'Nova incidència',
    type: 'Tipus',
    severity: 'Gravetat',
    comment: 'Comentari',
    photo: 'Foto (opcional)',
    addPhoto: 'Afegeix foto',
    photoAttached: 'Foto adjuntada',
    removePhoto: 'Treu la foto',
    photoDeferredHint: 'La foto es pujarà quan hi hagi connexió.',
    report: 'Notifica la incidència',
    reporting: 'Enviant…',
    reported: 'Incidència notificada',
    typeRawMaterial: 'Matèria primera',
    typeEquipment: 'Equip',
    typeProcess: 'Procés',
    typeQuality: 'Qualitat',
    typeOther: 'Altres',
    severityLow: 'Baixa',
    severityMedium: 'Mitjana',
    severityHigh: 'Alta',
    commentPlaceholder: 'Descriu què ha passat',
    nonBlockingHint: 'Notificar una incidència no atura l’OT.',
    selectType: 'Tria el tipus',
    selectSeverity: 'Tria la gravetat',
    reportAnother: 'Notifica’n una altra',
    backToOt: 'Torna a l’OT',
    recentIncidents: 'Incidències d’aquesta OT',
    reportedAt: 'Notificada a les {{time}}',
      takePhoto: 'Fes una foto',
    retakePhoto: 'Repeteix la foto',
    photoPermissionDenied: 'Sense accés a la càmera no es pot fer la foto',
    photoPendingUpload: 'La foto es pujarà en recuperar connexió',
    autoDeviationComment: 'Desviació de {{deviation}} en {{article}} (tolerància ±{{tolerance}} %)',
},

  'planta.closing': {
    title: 'Tancament d’OT',
    subtitle: 'Confirma la producció real i genera el lot',
    realQuantity: 'Quantitat real',
    plannedQuantity: 'Quantitat prevista',
    enterRealQuantity: 'Quantitat real produïda',
    realQuantityRequired: 'Indica la quantitat real produïda',
    merma: 'Merma',
    mermaValue: '{{quantity}} ({{percent}} %)',
    mermaValueNoPct: '{{quantity}}',
    overyield: 'Sobreproducció',
    mermaNone: 'Sense merma',
    mermaReason: 'Motiu de la merma',
    mermaReasonRequired: 'Cal indicar el motiu de la merma',
    mermaReasonPrompt: 'Tria el motiu de la merma',
    mermaWarning: 'Merma superior al límit ({{threshold}} %)',
    'mermaReason.burnt': 'Cremat',
    'mermaReason.dropped': 'Caigut',
    'mermaReason.expired': 'Caducat',
    'mermaReason.damage': 'Malmès',
    'mermaReason.loss': 'Pèrdua',
    suggestedExpiry: 'Caducitat suggerida',
    noExpiry: 'Sense caducitat definida',
    lotNumber: 'Número de lot',
    lotPending: 'Pendent d’assignar',
    lotPendingHint: 'El número definitiu s’assignarà en sincronitzar.',
    generateLot: 'Genera el lot',
    generatedLotSection: 'Lot que es generarà',
    closing: 'Tancant…',
    closed: 'OT tancada',
    completedTitle: 'OT tancada',
    completedBody: 'El lot s’ha generat i quedarà sincronitzat automàticament.',
    lotGenerated: 'Lot generat',
    printLabel: 'Imprimeix etiqueta',
    closeoutNote: 'Nota de tancament',
    packagingLabelsTitle: 'Etiquetes d’envàs',
    packagingLabelsHint:
      'Venen marcats tots els envasos de l’article. Desmarca el que no calgui.',
    packagingLabelCountUnknown: 'El nombre el posarà qui emeti l’etiqueta',
    packagingLabelCount_one: '{{count}} etiqueta',
    packagingLabelCount_other: '{{count}} etiquetes',
    closeoutNotePlaceholder: 'Nota de tancament (opcional)',
    consumptionSummary: 'Consum registrat',
    noConsumption: 'Encara no s’ha registrat cap pesatge.',
    confirmClose: 'Tanca l’OT i genera el lot',
    closeError: 'No s’ha pogut tancar l’OT. Torna-ho a provar.',
    backToOrder: 'Torna a l’OT',
    openCloseScreen: 'Tanca l’OT',
  },

  'planta.datePicker': {
    title: 'Tria una data',
    empty: 'Sense data',
    previousMonth: 'Mes anterior',
    nextMonth: 'Mes següent',
    clear: 'Esborra la data',
  },

  'planta.reception': {
    tasksTitle: 'Pendent',
    tasksNothing: 'Res pendent d’avui ni endarrerit',
    returnsTitle: 'Devolucions pendents',
    returnsEmpty: 'No hi ha cap devolució pendent de sortir.',
    taskOverdue_one: '{{count}} endarrerida',
    taskOverdue_other: '{{count}} endarrerides',
    taskToday_one: '{{count}} d’avui',
    taskToday_other: '{{count}} d’avui',
    taskFuture_one: '{{count}} futura',
    taskFuture_other: '{{count}} futures',
    taskUndated_one: '{{count}} sense data',
    taskUndated_other: '{{count}} sense data',
    groupOverdue: 'Endarrerides',
    groupToday: 'Avui',
    groupFuture: 'Properes',
    groupUndated: 'Sense data prevista',
    returnPlanned: 'Prevista: {{date}}',
    returnChipOverdue: 'Endarrerida',
    returnChipToday: 'Avui',
    returnDetailTitle: 'Sortida de devolució',
    returnLot: 'Lot',
    returnSupplierLot: 'Lot del proveïdor',
    returnReason: 'Motiu',
    returnObservations: 'Observacions',
    returnReception: 'Albarà {{number}}',
    returnOrder: 'Comanda {{number}}',
    returnVerifyHint: 'Comprova que l’article, el lot i la quantitat són els que carrega el transportista.',
    returnBlockedNote: 'Aquesta mercaderia és bloquejada fins que surti: producció no la pot gastar.',
    returnConfirm: 'Confirma la sortida',
    returnConfirming: 'Confirmant…',
    returnExited: 'Sortida confirmada: {{date}}, {{time}}',
    returnNumberKnown: 'Albarà de devolució {{number}}',
    returnNumberPending: 'L’albarà de devolució es numerarà quan es sincronitzi.',
    returnNotFound: 'Aquesta devolució ja no és a la llista: potser ja ha sortit.',
    returnBack: 'Torna a les devolucions',
    returnNoPermission: 'No tens permís per confirmar devolucions.',
    returnError: 'No s’ha pogut registrar la sortida. Torna-ho a provar.',
    title: 'Recepció de matèria primera',
    supplier: 'Proveïdor',
    article: 'Article',
    quantity: 'Quantitat',
    supplierLotCode: 'Lot del proveïdor',
    expiry: 'Caducitat',
    addLine: 'Afegeix article',
    confirm: 'Confirma recepció',
    selectMode: "Com vols registrar l'entrada?",
    newReception: 'Nova recepció',
    deliveryNote: 'Núm. albarà del proveïdor',
    deliveryNoteRequired: "Cal el número d'albarà del proveïdor",
    selectSupplier: 'Tria un proveïdor',
    selectArticle: 'Tria un article',
    lines: 'Línies',
    removeLine: 'Treu',
    lotOptional: 'Lot del proveïdor (opcional)',
    expiryOptional: 'Caducitat (opcional)',
    control: 'Control de recepció',
    controlOk: 'Conforme',
    controlNok: 'No conforme',
    confirmed: 'Recepció confirmada',
    pendingSync: 'Pendent de sincronitzar',
    recent: 'Recepcions recents',
    empty: 'Encara no has registrat cap recepció',
    noSuppliers: 'No hi ha proveïdors. Sincronitza amb connexió.',
    noArticles: 'No hi ha articles de compra disponibles.',
    noPendingOrders: 'Cap comanda pendent de rebre',
    addAtLeastOneLine: 'Afegeix almenys una línia amb quantitat',
    webUnsupported: "La recepció es fa des de l'app mòbil.",
    scan: 'Escaneja albarà',
    scanning: 'Escanejant…',
    scanFailed: "No s'ha pogut llegir l'albarà. Introdueix-lo manualment.",
    scanUnmatched_one: '{{count}} línia no reconeguda. Afegeix-la manualment.',
    scanUnmatched_other: '{{count}} línies no reconegudes. Afegeix-les manualment.',
    print: 'Imprimeix etiqueta',
    printUnsupported: "La impressió requereix una versió de l'app amb impressora.",
    printedTo: 'Etiqueta enviada a {{printer}}',
    printNoPrinter:
      'No hi ha cap impressora configurada. Un administrador n’ha d’afegir una al Backoffice.',
    printError:
      'No s’ha pogut imprimir a {{printer}}. Comprova que estigui encesa i a la mateixa xarxa.',
    ordered: 'Demanat',
    receivedSoFar: 'Ja rebut',
    pendingQuantity: 'Pendent',
    orderedConversion: '{{ordered}} × {{factor}} = {{total}}',
    quantityWithBase: '{{quantity}} ({{base}})',
    receivedQuantity: 'Quantitat rebuda',
    receivedQuantityIn: 'Quantitat rebuda ({{unit}})',
    equalsBase: '= {{total}}',
    entryUnit: 'Unitat de la quantitat',
    noConversion: "L'envàs «{{unit}}» no té conversió a la fitxa de l'article. Indica la quantitat en {{base}}.",
    receiveOrder: 'Rebre una comanda',
    receiveWithoutOrder: 'Recepció sense comanda',
    ordersToReceive: 'Comandes per rebre',
    windowDue: 'Fins avui',
    windowWeek: 'Aquesta setmana',
    windowAll: 'Totes',
    statusAll: 'Tots els estats',
    statusAwaiting: "Pendent d'entrega",
    statusPartial: 'Entregada parcialment',
    allSuppliers: 'Tots els proveïdors',
    expectedDelivery: 'Entrega prevista: {{date}}',
    noExpectedDelivery: "Sense data d'entrega prevista",
    overdue: 'Endarrerida',
    noOrdersMatch: 'Cap comanda amb aquests filtres',
    clearFilters: 'Treu els filtres',
    entry: 'Entrada {{number}}',
    removeEntry: "Treu l'entrada",
    addEntry: 'Afegeix una altra entrada',
    thisReceipt: 'En aquest albarà',
    willRemainPending: 'Quedarà pendent',
    expiryEstimatedLabel: 'Caducitat estimada',
    expiryEstimatedHint_one: 'Proposta: recepció + {{count}} dia de vida útil. Canvia-la si el proveïdor en indica una de real.',
    expiryEstimatedHint_other: 'Proposta: recepció + {{count}} dies de vida útil. Canvia-la si el proveïdor en indica una de real.',
    lineSummary: 'Demanat {{ordered}} · Ja rebut {{received}} · Pendent {{pending}}',
    entriesNone: 'Cap entrada encara',
    entriesSummary_one: '{{count}} entrada · {{total}}',
    entriesSummary_other: '{{count}} entrades · {{total}}',
    lineNeedsNote: 'Falta la nota',
    expandLine: 'Desplega la línia',
    collapseLine: 'Plega la línia',
    exceptionOverQuantity: 'Passa {{excess}} del que faltava. Es registrarà com a excepció.',
    exceptionOutsideOrder: 'Article no demanat però que recepciones. Es registrarà com a excepció i cal indicar-ne el motiu.',
    pickerTitle: 'Afegeix un article',
    pickerClose: 'Tanca',
    pickerSearch: 'Cerca per nom o codi',
    pickerUnrequestedHelp: 'Article no demanat però que recepciones. Cal indicar-ne el motiu.',
    pickerNoSupplierArticles: 'Aquest proveïdor no té articles vinculats.',
    pickerNoMatch: 'Cap article coincideix amb la cerca',
    addArticleRestricted: 'Només un responsable pot afegir un article que no és a la comanda.',
    unrequestedNote: 'Per què el recepciones?',
    unrequestedNoteHint: 'Ex.: el proveïdor l’ha afegit a l’albarà',
    scanUnrequestedSkipped_one: '{{count}} línia d’un article que no és a la comanda no s’ha afegit: només un responsable pot afegir-lo.',
    scanUnrequestedSkipped_other: '{{count}} línies d’articles que no són a la comanda no s’han afegit: només un responsable pot afegir-los.',
    flagException: 'Marca com a excepció',
    unflagException: 'Treu la marca',
    flaggedException: 'Excepció',
    exceptionNote: 'Què passa?',
    exceptionNoteHint: 'Ex.: sacs de 20 kg en lloc de 25',
    exceptionNoteRequired: 'Cal explicar què passa',
    exceptionNotesMissing: 'Explica què passa amb les entrades marcades com a excepció.',
    controlNote: 'Observació',
    controlNoteHint: 'Què té de no conforme?',
    createIncident: 'Crea una incidència',
    createIncidentHint: 'Quedarà lligada a aquesta recepció i la veurà el responsable.',
    controlNoteRequired: 'Escriu què passa per crear la incidència',
    controlNotesMissing: 'Escriu què passa a les entrades no conformes que obriran una incidència.',
    filterByWindow: 'Filtra per entrega prevista',
    filterByStatus: 'Filtra per estat',
    filterBySupplier: 'Filtra per proveïdor',
    orderNumber: 'Comanda {{number}}',
    orderNoLongerPending: 'Aquesta comanda ja no està pendent de rebre.',
    historyOpen: 'Historial de recepcions',
    historyTitle: 'Historial de recepcions',
    historySearch: 'Cerca per proveïdor, albarà o comanda',
    historyFilterByWindow: 'Filtra per data',
    historyWindowToday: 'Avui',
    historyWindowWeek: '7 dies',
    historyWindowMonth: '30 dies',
    historyWindowAll: 'Totes',
    historyEmpty: 'Encara no has fet cap recepció en aquest aparell',
    historyNoMatch: 'Cap recepció coincideix amb la cerca',
    historyDeliveryNote: 'Albarà {{number}}',
    historyNoSupplier: 'Sense proveïdor',
    historyCapped: 'Es mostren les {{count}} més recents. Afina la cerca per trobar-ne d’altres.',
    historyScopeNote: 'Hi surten les recepcions fetes en aquest aparell.',
    printCopies_one: 'Imprimeix {{count}} etiqueta',
    printCopies_other: 'Imprimeix {{count}} etiquetes',
    copiesLabel: 'Còpies',
    copiesLess: 'Menys còpies',
    copiesMore: 'Més còpies',
  },

  'planta.sales': {
    title: 'Vendes',
    prepareCta: 'Prepara comandes',
    newOrderCta: 'Nova comanda',
    recent: 'Activitat recent',
    empty: 'Cap comanda encara',
    newOrderTitle: 'Nova comanda de venda',
    client: 'Client',
    selectClient: 'Tria un client',
    noClients: 'No hi ha clients. Sincronitza amb connexió.',
    deliveryDate: 'Data d’entrega',
    notes: 'Notes',
    lines: 'Línies',
    addLine: 'Afegeix article',
    selectArticle: 'Tria un article',
    noArticles: 'No hi ha articles de venda disponibles.',
    quantity: 'Quantitat',
    removeLine: 'Treu',
    addAtLeastOneLine: 'Afegeix almenys una línia amb quantitat',
    create: 'Crea la comanda',
    created: 'Comanda creada',
    pendingSync: 'Pendent de sincronitzar',
    available: 'Disponible: {{quantity}}',
    committed: 'Compromès: {{quantity}}',
    insufficientStock: 'Estoc insuficient (avís, no bloqueja)',
    webUnsupported: 'Les vendes es gestionen des de l’app mòbil.',
  },

  'planta.picking': {
    noLotsToShip:
      'No hi ha cap lot disponible per expedir aquests articles. Comprova que les existències estiguin donades d’alta amb lot: les que s’han creat amb un ajust manual no es poden expedir.',
    title: 'Preparar comandes',
    pendingOrders: 'Comandes pendents',
    noPendingOrders: 'Cap comanda pendent de preparar',
    filterToday: 'Avui',
    filterWeek: 'Aquesta setmana',
    filterAll: 'Totes',
    deliveryFor: 'Entrega: {{date}}',
    ordered: 'Demanat',
    prepared: 'Preparat',
    lot: 'Lot',
    suggestedLot: 'Lot suggerit (FEFO)',
    changeLot: 'Canvia el lot',
    noLots: 'Sense lots disponibles',
    expiresOn: 'Caduca: {{date}}',
    available: 'Disponible: {{quantity}}',
    insufficientStock: 'Estoc insuficient (avís, no bloqueja)',
    ship: 'Expedeix',
    shipConfirmTitle: 'Expedir la comanda?',
    shipConfirmBody: 'Es donarà de baixa l’estoc i es generarà l’albarà de venda.',
    partialHint: 'Les quantitats no servides queden pendents (backorder).',
    shipped: 'Comanda expedida',
    pendingSync: 'Pendent de sincronitzar',
    webUnsupported: 'La preparació de comandes es fa des de l’app mòbil.',
  },

  'planta.config': {
    title: 'Configuració incompleta',
    body: "Falten variables d'entorn necessàries per connectar amb el servidor. L'app no pot arrencar fins que estiguin definides al desplegament.",
    missingLabel: 'Variables que falten:',
    hint: 'Defineix-les a la configuració del projecte (Vercel) i torna a desplegar.',
  },

  'backoffice.agenda': {
    title: 'Planificació',
    subtitle: 'Ordres de treball planificades per dia i torn',
    byShift: 'Per torn',
    byDay: 'Per dia',
    publish: 'Publica',
    publishing: 'Publicant…',
    draft: 'Esborrany',
    published: 'Publicada',
    create: 'Crea OT',
    date: 'Data',
    today: 'Avui',
    previousDay: 'Dia anterior',
    nextDay: 'Dia següent',
    noShift: 'Sense torn',
    unassigned: 'Sense assignar',
    quantityWithUnit: '{{quantity}} {{unit}}',
    empty: 'No hi ha cap ordre de treball per a aquest dia. Crea’n una per planificar producció.',
    emptyFiltered: 'Cap ordre de treball coincideix amb aquest filtre per a aquest dia.',
    loadError: 'No s’han pogut carregar les ordres de treball',
    publishError: 'No s’ha pogut publicar l’ordre de treball',
    // Filtres ràpids (accessos directes des del tauler)
    filterAll: 'Totes',
    filterInProgress: 'En curs',
    filterDrafts: 'Esborranys',
    // Calendari mensual de producció
    previousMonth: 'Mes anterior',
    nextMonth: 'Mes següent',
    dayHeading: 'Producció del {{date}}',
    ordersCount: '{{count}} OT',
    moreOrders: '+{{count}} més',
  },

  'backoffice.workOrders': {
    title: 'Ordres de treball',
    create: 'Crea OT',
    assign: 'Assigna',
    assignToUser: 'Assigna a operari',
    assignToSection: 'Assigna a secció',
    sectionWithoutMembers:
      'Ningú no té assignada la secció «{{name}}». Aquesta OT no apareixerà a cap dispositiu fins que hi assignis algú des de Configuració · Usuaris.',
    cancel: 'Cancel·la',
    cancellationReason: 'Motiu de cancel·lació',
    lowStockWarning: 'Existències insuficients (avís, no bloqueja)',
    // Create OT (screen 09)
    createTitle: 'Nova ordre de treball',
    createSubtitle: 'Planifica una OT a partir d’una recepta publicada',
    backToAgenda: 'Torna a l’agenda',
    sectionRecipe: 'Recepta i producte',
    sectionPlanning: 'Quantitat i planificació',
    sectionAssignment: 'Assignació',
    sectionResources: 'Recursos',
    sectionResourcesHint:
      'La recepta demana aquests recursos. Tria quin farà servir aquesta OT; si encara no ho saps, ho podràs fer des del detall de l’OT.',
    resourceNeed: '{{quantity}} × {{type}}',
    resourceDuration: '{{minutes}} min',
    resourceMachine: 'Recurs assignat',
    resourceNotChosen: 'Sense triar',
    recipe: 'Recepta',
    recipeHint: 'Només receptes amb una versió publicada activa',
    selectRecipe: 'Selecciona una recepta',
    pinnedVersionNote:
      'L’OT fixa la versió activa de la recepta en aquest moment; no canviarà encara que la recepta es revisi després.',
    workOrderType: 'Tipus d’OT',
    plannedQuantity: 'Quantitat planificada',
    plannedUnit: 'Unitat',
    plannedUnitHint: 'La posa la recepta: en rendeix {{quantity}} {{unit}}.',
    plannedUnitNoRecipe: 'La posa la recepta que triïs.',
    scaleHint: 'Això és {{factor}} vegades la recepta: l’operari pesarà cada ingredient multiplicat per aquest número.',
    selectUnit: 'Selecciona una unitat',
    productionDate: 'Data de producció',
    shift: 'Torn',
    noShift: 'Sense torn',
    assignTo: 'Assigna a',
    assignToHint: 'Una OT s’assigna a una persona O a una secció',
    selectMember: 'Selecciona un operari',
    selectSection: 'Selecciona una secció',
    saveDraft: 'Desa esborrany',
    saveAndPublish: 'Desa i publica',
    publishing: 'Publicant…',
    publishHint:
      'Desa com a esborrany per planificar sense que surti a l’agenda, o desa i publica perquè l’app de planta la pugui executar.',
    shortIngredient:
      '{{name}}: calen {{required}}, disponibles {{available}}',
    fixValidation: 'Revisa els camps marcats abans de desar.',
    createError: 'No s’ha pogut crear l’ordre de treball',
    assignmentError:
      'S’ha creat l’OT però no s’ha pogut desar l’assignació. Revisa-la des de l’agenda.',
    cancelError: 'No s’ha pogut cancel·lar l’ordre de treball',
    noUnits:
      'Encara no hi ha unitats definides per a l’obrador. Defineix-les a Configuració abans de crear OT.',
    noPlannableRecipes:
      'No hi ha cap recepta amb una versió publicada. Publica una recepta abans de crear una OT.',
    // Work order type enum labels
    'type.production': 'Producció',
    'type.packaging': 'Envasat',
    // Work order status enum labels
    'status.planned': 'Planificada',
    'status.in_progress': 'En curs',
    'status.completed': 'Completada',
    'status.cancelled': 'Cancel·lada',
    // Work order step status enum labels (OT detail / traçabilitat)
    'stepStatus.pending': 'Pendent',
    'stepStatus.in_progress': 'En curs',
    'stepStatus.completed': 'Fet',
    'stepStatus.skipped': 'Omès',
    // Weighing source enum labels
    'source.manual': 'Manual',
    'source.scale': 'Bàscula',
    'source.theoretical': 'Teòric',
  },

  'backoffice.workOrderDetail': {
    editTitle: 'Edita la planificació',
    editBody: 'Pots corregir la quantitat, la data, el torn i qui la fa mentre no s’hagi començat. La recepta i la seva versió no es poden canviar: per produir una altra cosa, cancel·la l’OT i crea’n una de nova.',
    subtitle: 'Traçabilitat de l’ordre de treball',
    backToAgenda: 'Torna a l’agenda',
    loadError: 'No s’ha pogut carregar l’ordre de treball',
    notFound: 'No s’ha trobat l’ordre de treball.',
    // Capçalera
    article: 'Producte',
    plannedQuantity: 'Quantitat planificada',
    realQuantity: 'Quantitat real',
    merma: 'Merma',
    mermaValue: '{{quantity}} {{unit}} ({{pct}}%)',
    assignment: 'Assignació',
    unassigned: 'Sense assignar',
    shift: 'Torn',
    noShift: 'Sense torn',
    productionDate: 'Data de producció',
    publishedAt: 'Publicada',
    startedAt: 'Iniciada',
    completedAt: 'Completada',
    cancellationReason: 'Motiu de cancel·lació',
    empty: '—',
    quantityWithUnit: '{{quantity}} {{unit}}',
    // Passos
    sectionSteps: 'Passos de producció',
    stepsEmpty: 'L’operari encara no ha activat cap pas.',
    sectionResources: 'Recursos',
    resourcesEmpty: 'La recepta d’aquesta OT no demana recursos.',
    resourceSaveError: 'No s’ha pogut desar el recurs. Torna-ho a provar.',
    stepBlocking: 'Bloquejant (PCC)',
    stepCompletedAt: 'Fet {{at}}',
    stepCompletedBy: 'per {{name}}',
    recordMeasured: 'Mesura',
    recordNote: 'Nota',
    // Pesatges
    sectionWeighings: 'Pesatges',
    weighingsEmpty: 'Encara no s’ha registrat cap pesatge.',
    colArticle: 'Matèria primera',
    colPlanned: 'Previst',
    colReal: 'Real',
    colDeviation: 'Desviació',
    colSource: 'Origen',
    deviationValue: '{{pct}}%',
    // Incidències
    sectionIncidents: 'Incidències',
    incidentsEmpty: 'Cap incidència reportada en aquesta OT.',
    incidentResolved: 'Resolta {{at}}',
    // Lot
    sectionLot: 'Lot produït',
    lotEmpty: 'Encara no s’ha generat cap lot (l’OT no s’ha tancat).',
    packagingLabelsTitle: 'Etiquetes d’envàs',
    packagingUnitUnknown: 'Envàs',
    packagingLabelPrint_one: 'Genera {{packaging}} ({{count}} etiqueta)',
    packagingLabelPrint_other: 'Genera {{packaging}} ({{count}} etiquetes)',
    packagingLabelPrinting: 'Generant…',
    packagingLabelIssued: 'Ja emesa',
    packagingLabelError:
      'No s’ha pogut desar la tria o generar l’etiqueta de {{packaging}}. Torna-ho a provar.',
    packagingLoadError:
      'No s’han pogut llegir les etiquetes d’envàs d’aquesta ordre. Mentre no es puguin llegir no es pot canviar res: recarrega la pàgina.',
    packagingNothingRequested:
      'Aquesta ordre d’envasat s’ha tancat sense cap envàs demanat. Marca’n els que calguin per poder-ne generar les etiquetes.',
    packagingNoPermission:
      'Emetre etiquetes és del responsable de producció o de l’administrador.',
    packagingCopiesRange: 'Escriu un nombre d’etiquetes entre 1 i {{max}}.',
    packagingTooManyCopies:
      'En calen {{total}} en total, més de les {{max}} que es poden generar d’un cop. Genera-les per tandes.',
    packagingFactorDrift:
      'Es van demanar {{requested}} unitats per envàs; l’article ara en diu {{configured}}.',
    packagingIssuedUnknown:
      'Encara no se sap si se n’han emès etiquetes',
    packagingLabelNoLotYet:
      'L’ordre encara no s’ha tancat: es poden marcar els envasos, però les etiquetes no es generen fins que hi hagi lot.',
    packagingLabelNoTemplate:
      'No hi ha cap format d’etiqueta utilitzable per a aquest article. Defineix-ne un a Configuració · Formats d’etiqueta o tria’n un a la fitxa de l’article.',
    packagingUnconfigured: 'Ja no és un envàs de l’article',
    packagingIssuedLocked: 'Ja se n’han emès etiquetes: la tria no es pot desfer',
    lotQuantity: 'Quantitat produïda',
    lotExpiry: 'Caducitat',
    lotNoExpiry: 'Sense caducitat',
    // Comú
    recordedBy: 'Registrat per',
    recordedAt: 'Registrat',
    by: 'per {{name}}',
      sourceTheoretical: 'Teòric',
    voided: 'Anul·lada',
    voidedReason: 'Motiu: {{reason}}',
    tolerance: 'Tolerància',
},

  'backoffice.dashboard': {
    title: 'Tauler',
    subtitle: 'Resum operatiu de l’obrador · {{date}}',
    createOt: 'Crea OT',
    loadError: 'No s’han pogut carregar algunes dades del tauler',
    // Planning KPIs (rol de planificació)
    planningSection: 'Planificació d’avui',
    otTodayLabel: 'OT d’avui',
    otTodayHint: 'Ordres de treball planificades per a avui',
    otByStatusLabel: 'OT per estat',
    draftsLabel: 'Esborranys pendents',
    goToAgenda: 'Veure agenda',
    // Read KPIs (qualsevol membre)
    operationsSection: 'Operacions',
    lowStockItems: 'Articles sota mínim',
    lowStockEmpty: 'Cap article per sota del mínim. Tot en ordre.',
    recentLots: 'Darrers lots produïts',
    recentLotsEmpty: 'Encara no s’ha produït cap lot.',
    goToExistences: 'Veure existències',
    // Visual dashboard (KPIs + gràfics)
    production: 'Producció',
    inProgressLabel: 'En curs',
    kpiActive: 'actives',
    kpiPending: 'pendents',
    productionChartSubtitle: 'Lots produïts · últims 14 dies',
    thisPeriod: 'Aquest període',
    otByStatusSubtitle_one: '{{count}} ordre · últims 30 dies',
    otByStatusSubtitle_other: '{{count}} ordres · últims 30 dies',
    donutTotal: 'total',
    lotsProduced: 'Lots produïts',
    lotsChartSubtitle: 'Per dia · últims 7 dies',
    lotsTotal_one: '{{count}} lot',
    lotsTotal_other: '{{count}} lots',
    // Personalització del tauler (mode edició, desat al navegador)
    customize: 'Personalitza el tauler',
    customizeDone: 'Fet',
    customizeReset: 'Restaura',
    customizeHint:
      'Amaga, mostra i reordena les targetes amb els controls de cada una. Els canvis es desen en aquest navegador.',
    moveEarlier: 'Mou abans',
    moveLater: 'Mou després',
    showCard: 'Mostra la targeta',
    hideCard: 'Amaga la targeta',
    // Vendes i Compres (KPIs + panells)
    salesTitle: 'Vendes',
    salesSubtitle: 'Comandes obertes · per data d’entrega',
    salesPendingLabel: 'Pendents de servir',
    salesEmpty: 'Cap comanda pendent de servir.',
    goToSales: 'Veure vendes',
    deliveriesToday: '{{count}} avui',
    purchasesTitle: 'Compres',
    purchasesSubtitle: 'Comandes de compra obertes · per data prevista',
    purchasesOpenLabel: 'Compres en curs',
    purchasesEmpty: 'Cap comanda de compra en curs.',
    goToPurchases: 'Veure compres',
    noDate: 'Sense data',
    // Incidències (KPI + panell, amb realtime)
    incidentsTitle: 'Incidències recents',
    incidentsSubtitle: 'Pendents de resolució',
    incidentsEmpty: 'Cap incidència pendent.',
    goToIncidents: 'Veure incidències',
    kpiAttention: 'atenció',
    kpiTitleOtToday: 'Ordres d’avui',
    kpiDescOtToday: 'Planificades per avui',
    kpiTitleInProgress: 'En curs',
    kpiDescInProgress: 'Ordres en producció',
    kpiTitleProduction: 'Producció',
    kpiDescProduction: 'Lots produïts avui',
    kpiTitleSales: 'Pendents de servir',
    kpiDescSales: 'Comandes obertes',
    kpiTitlePurchases: 'Compres',
    kpiDescPurchases: 'Compres en curs',
    kpiTitleDrafts: 'Receptes',
    kpiDescDrafts: 'Esborranys pendents',
    kpiTitleLowStock: 'Estoc',
    kpiDescLowStock: 'Articles sota mínim',
    kpiTitleIncidents: 'Incidències',
    kpiDescIncidents: 'Incidències obertes',
    // Llegat (no usat actualment)
    productionToday: 'Producció d’avui',
    openIncidents: 'Incidències obertes',
    expiringSoon: 'Caduquen aviat',
  },

  'backoffice.production': {
    title: 'Producció',
    subtitle: 'Lots produïts per dia',
    date: 'Data',
    today: 'Avui',
    previousDay: 'Dia anterior',
    nextDay: 'Dia següent',
    lotNumber: 'Lot',
    product: 'Producte',
    quantity: 'Quantitat',
    expiry: 'Caducitat',
    noExpiry: 'Sense caducitat',
    status: 'Estat',
    quantityWithUnit: '{{quantity}} {{unit}}',
    empty: 'No s’ha produït cap lot aquest dia.',
    loadError: 'No s’han pogut carregar els lots produïts',
  },

  'backoffice.incidents': {
    title: 'Incidències',
    subtitle: 'Incidències reportades des de planta',
    filterAll: 'Totes',
    filterOpen: 'Pendents',
    filterResolved: 'Resoltes',
    statusOpen: 'Pendent',
    statusResolved: 'Resolta',
    severity: 'Gravetat',
    severityLow: 'Baixa',
    severityMedium: 'Mitjana',
    severityHigh: 'Alta',
    type: 'Tipus',
    typeRawMaterial: 'Matèria primera',
    typeEquipment: 'Equip',
    typeProcess: 'Procés',
    typeQuality: 'Qualitat',
    typeOther: 'Altres',
    workOrder: 'OT',
    noWorkOrder: 'Sense OT',
    reception: 'Recepció',
    deliveryNoteRef: 'albarà {{number}}',
    supplier: 'Proveïdor',
    purchaseOrder: 'Comanda',
    article: 'Article',
    lot: 'Lot',
    supplierLotRef: 'lot del proveïdor {{code}}',
    reportedBy: 'Reportada per',
    reportedAt: 'Reportada',
    resolvedAt: 'Resolta',
    unknownUser: 'Operari',
    resolve: 'Marca com a resolta',
    resolving: 'Resolent…',
    resolveError: 'No s’ha pogut resoldre la incidència',
    live: 'En directe',
    empty: 'No hi ha incidències.',
    emptyOpen: 'No hi ha incidències pendents. Tot en ordre.',
    loadError: 'No s’han pogut carregar les incidències',
    toastTitle: 'Nova incidència',
    toastView: 'Veure',
    resolveTitle: 'Tanca la incidència',
    resolveBody:
      'Indica com s’ha resolt. Tancar-la no mou l’estoc: si cal tornar mercaderia, es genera una devolució a part.',
    resolutionLabel: 'Resolució',
    resolutionNoAction: 'Sense acció',
    resolutionAccepted: 'Acceptat',
    resolutionTotalReturn: 'Devolució total',
    resolutionPartialReturn: 'Devolució parcial',
    resolutionCredit: 'Abonament / compensació',
    resolvedWith: 'Resolta: {{resolution}}',
    resolutionNoteLabel: 'Nota (opcional)',
    resolutionNotePlaceholder: 'Què s’ha acordat amb el proveïdor',
    closeAction: 'Tanca la incidència',
    createReturn: 'Genera una devolució',
    resolveErrorResolutionRequired: 'Tria com s’ha resolt.',
    resolveErrorAlreadyResolved: 'Algú altre ja ha tancat aquesta incidència.',
    resolveErrorForbidden: 'No tens permís per tancar incidències.',
  },

  'backoffice.articles': {
    title: 'Articles',
    create: 'Crea article',
    sku: 'Codi',
    name: 'Nom',
    type: 'Tipus',
    shelfLife: 'Vida útil (dies)',
    allergens: 'Al·lèrgens',
    allergensDerived: 'Al·lèrgens derivats automàticament del BOM',
  },

  'backoffice.common': {
    appName: 'Obrava Backoffice',
    themeLight: 'Clar',
    themeDark: 'Fosc',
    saving: 'Desant…',
    creating: 'Creant…',
    actionError: 'No s’ha pogut completar l’acció',
    placeUnavailable: '{{place}} (no disponible)',
    supplierInactive: '{{name}} (inactiu)',
    supplierUnavailable: 'Proveïdor no disponible',
    loadError: 'No s’han pogut carregar les dades',
    readOnly: 'Només lectura',
    noPermission: 'No tens permís per a aquesta acció',
    optional: 'Opcional',
    none: 'Cap',
    dashboard: 'Tauler',
    agenda: 'Planificació',
    production: 'Producció',
    incidents: 'Incidències',
    catalog: 'Catàleg',
    articles: 'Articles',
    recipes: 'Receptes',
    existences: 'Existències',
    purchases: 'Compres',
    purchaseOrders: 'Comandes de compra',
    purchaseDeliveryNotes: 'Albarans de compra',
    receptionExceptions: 'Excepcions',
    purchaseReturns: 'Devolucions',
    purchaseTemplates: 'Plantilles',
    thirdParties: 'Tercers',
    clients: 'Clients',
    suppliers: 'Proveïdors',
    team: 'Equip',
    users: 'Usuaris',
    operators: 'Operaris',
    sales: 'Vendes',
    salesOrders: 'Comandes de venda',
    technicalSheet: 'Fitxa tècnica',
    import: 'Importació',
    settings: 'Configuració',
    language: 'Idioma',
    theme: 'Tema',
    account: 'Compte i perfil',
    passwordTitle: 'Contrasenya',
    passwordHint: 'Per canviar-la t’enviem un correu amb un enllaç; hi triaràs la contrasenya nova.',
    passwordCta: 'Envia’m un enllaç per canviar-la',
    copyVersion: 'Copia la versió',
    versionCopied: 'Versió copiada',
    collapse: 'Minimitza',
    navSectionOperative: 'Operativa',
    navSectionProduct: 'Producte',
    navSectionStock: 'Existències i moviments',
    navSectionPurchases: 'Compres',
    navSectionSales: 'Vendes',
    navSectionQuality: 'Qualitat',
    navNonConformities: 'No conformitats',
    navSelfChecks: 'Autocontrols',
    navEvidence: 'Evidències',
    navStockCounts: 'Recomptes d’existències',
    navSectionData: 'Dades',
    navSectionManagement: 'Gestió',
    openMenu: 'Obre el menú',
    closeMenu: 'Tanca el menú',
    comingSoon: 'Properament',
    obrador: 'Obrador',
    switchObrador: 'Canvia d’obrador',
    switchingObrador: 'Canviant d’obrador…',
    switchObradorError: 'No s’ha pogut canviar d’obrador',
    activeObrador: 'Obrador actiu',
    memberDeleted: '{{name}} (baixa)',
  },

  'backoffice.auth': {
    title: 'Obrava Backoffice',
    subtitle: 'Everything, under control.',
    signInTitle: 'Inicia sessió',
    email: 'Correu electrònic',
    password: 'Contrasenya',
    signInCta: 'Entra',
    signingIn: 'Entrant…',
    signInError: 'Credencials incorrectes',
    showPassword: 'Mostra la contrasenya',
    hidePassword: 'Amaga la contrasenya',
    noTenantTitle: 'Compte sense obrador',
    noTenantBody:
      'Aquest compte no té cap obrador assignat. Contacta amb l’administrador del teu obrador.',
    noBackofficeAccessTitle: 'Sense accés al Backoffice',
    noBackofficeAccessBody:
      'A {{obrador}}, els teus rols només obren l’App planta. Si també necessites el Backoffice, demana a l’administrador que t’hi afegeixi un rol d’oficina, com ara Consulta.',
    noRolesBody:
      'A {{obrador}} no tens cap rol assignat. Demana a l’administrador que te n’assigni un.',
    backToSignIn: 'Torna a iniciar sessió',
    rememberMe: 'Mantén la sessió en aquest equip',
    rememberMeHint:
      'Activa-ho només en ordinadors d’ús habitual. En equips compartits, desactiva-ho i la sessió es tancarà en sortir del navegador.',
    forgotPassword: 'Has oblidat la contrasenya?',
    resetTitle: 'Restableix la contrasenya',
    resetEmailStep: 'Introdueix el teu correu i t’enviarem un enllaç per triar una contrasenya nova.',
    resetSendCta: 'Envia’m l’enllaç',
    resetSending: 'Enviant…',
    resetNewPassword: 'Contrasenya nova',
    resetConfirmPassword: 'Confirma la contrasenya',
    resetSubmitCta: 'Desa la contrasenya nova',
    resetSubmitting: 'Desant…',
    resetSuccess: 'Contrasenya actualitzada. Ja pots iniciar sessió.',
    resetSendError: 'No s’ha pogut enviar el correu. Comprova l’adreça i torna-ho a provar.',
    resetLinkSent: 'T’hem enviat un correu a {{email}}. Obre l’enllaç que hi trobaràs per triar una contrasenya nova (revisa també el correu brossa).',
    resetLinkInvalid: 'Aquest enllaç ha caducat o ja s’ha fet servir. Demana’n un de nou.',
    resetRetry: 'No t’ha arribat? Torna-ho a provar',
    resetPasswordWeak: 'La contrasenya ha de tenir com a mínim 6 caràcters.',
    resetPasswordMismatch: 'Les contrasenyes no coincideixen.',
    resetGenericError: 'No s’ha pogut restablir la contrasenya.',
    accessLoadErrorTitle: 'No s’han pogut carregar els teus accessos',
    accessLoadErrorBody:
      'No hem pogut comprovar què pots obrir a {{obrador}} i, mentre no ho sapiguem, no et mostrem cap pantalla. Reintenta-ho; si el problema continua, avisa l’administrador.',
    accessLoadRetry: 'Reintenta',
    noMenuAccessTitle: 'Sense opcions disponibles',
    noMenuAccessBody:
      'A {{obrador}} tens totes les opcions del Backoffice amagades. Demana a l’administrador que te’n torni a activar alguna.',
  },

  'backoffice.catalog': {
    title: 'Catàleg',
    subtitle: 'Articles de l’obrador: compra, semielaborats i venda',
    newArticle: 'Nou article',
    import: 'Importa articles',
    importSupplierArticles: 'Importa relació article-proveïdor',
    searchPlaceholder: 'Cerca per codi o nom',
    filterType: 'Tipologia',
    typeAll: 'Totes',
    typePurchase: 'Compra',
    typeIntermediate: 'Semielaborat',
    typeSale: 'Venda',
    filterCategory: 'Categoria',
    filterTax: 'Impost',
    categoryAll: 'Totes',
    colSku: 'Codi',
    colName: 'Nom',
    colType: 'Tipologia',
    colCategory: 'Categoria',
    colActions: 'Accions',
    techSheetAction: 'Fitxa tècnica',
    colStock: 'Existències',
    empty: 'Encara no hi ha cap article',
    emptyFiltered: 'Cap article coincideix amb els filtres',
    showInactive: 'Mostra els donats de baixa',
    inactiveBadge: 'Baixa',
    colNature: 'Naturalesa',
    'nature.is_purchased': 'Es compra',
    'nature.is_manufactured': 'Es fabrica',
    'nature.is_sold': 'Es ven',
    noCategory: 'Sense categoria',
    noTax: 'Sense impost',
    selectAll: 'Selecciona tots els articles visibles',
    selectedCount: 'Seleccionats: {{count}}',
    clearSelection: 'Buida la selecció',
    bulkType: 'Canvia la tipologia',
    bulkNature: 'Canvia la naturalesa',
    bulkCategory: 'Canvia la categoria',
    bulkTax: 'Canvia l’impost',
    bulkApply: 'Aplica el canvi',
    bulkConfirm: 'Articles afectats pel canvi: {{count}}.',
    bulkNatureHint: 'Només es modifiquen les marques que canviïs; la resta es mantenen tal com estan.',
    bulkNatureManualNote:
      'En desactivar «es fabrica», la informació de seguretat alimentària d’aquests articles passa a manual.',
    bulkNatureKeep: 'Deixa-la com està',
    bulkNatureOn: 'Activa-la',
    bulkNatureOff: 'Desactiva-la',
    bulkMixed: 'Valors diferents',
  },

  'backoffice.articleForm': {
    linkStock: 'Existències',
    linkPurchases: 'Compres',
    linkSales: 'Vendes',
    unsavedChanges: 'Canvis sense desar',
    backToCatalog: 'Torna al catàleg',
    foodSafetyMode: 'Origen de la informació',
    foodSafetyModeHint: 'Com s’obtenen els ingredients, els al·lèrgens i la informació nutricional de la fitxa tècnica.',
    foodSafetyMode_manual: 'Manual — s’informa en aquesta fitxa',
    foodSafetyMode_derived: 'Calculada des de la recepta',
    foodSafetyModeManualOnly: 'Manual. Una matèria primera o un producte auxiliar no té recepta d’on derivar la informació.',
    safetyNotesLabel: 'Altres informacions',
    derivedNoRecipe: 'Aquest article encara no té cap versió de recepta publicada, així que no se’n pot calcular la informació. Publica la recepta o canvia l’origen a manual.',
    derivedFromRecipe: 'Calculat de la versió {{version}} de la recepta.',
    derivedNutritionEmpty: 'Cap matèria primera de la recepta declara informació nutricional.',
    derivedNutritionBasisYield: 'Per 100 g de producte acabat, sobre el rendiment de la recepta.',
    derivedNutritionBasisBom: 'Per 100 g, sobre la suma dels ingredients (la recepta no té rendiment en pes).',
    derivedNutritionCoverage: 'Només el {{percent}}% del pes de la recepta declara informació nutricional: els valors queden per sota del real.',
    titleNew: 'Nou article',
    titleEdit: 'Fitxa d’article',
    subtitleNew: 'Alta d’un article del catàleg',
    sectionIdentity: 'Identificació',
    sectionClassification: 'Classificació i comportament',
    sectionUnits: 'Unitats i conversions',
    sectionStock: 'Existències i aprovisionament',
    sectionAllergens: 'Al·lèrgens',
    sectionDanger: 'Baixa de l’article',
    sku: 'Codi (SKU)',
    name: 'Nom comercial',
    articleType: 'Tipologia',
    category: 'Categoria',
    barcode: 'Codi de barres',
    isPurchased: 'Es compra',
    isManufactured: 'Es fabrica',
    isSold: 'Es ven',
    baseUnit: 'Unitat base (existències)',
    baseUnitPlaceholder: 'Selecciona una unitat',
    recipeUnit: 'Unitat de recepta',
    recipeUnitHint:
      'La unitat en què la recepta demana aquesta matèria primera. Les existències i les compres continuen en la unitat base.',
    recipeUnitSameAsBase: 'La mateixa que la unitat base',
    recipeUnitSameAsBaseNamed: 'La mateixa que la base ({{code}})',
    recipeUnitUnavailable: 'Unitat no disponible',
    recipeFactor: 'Equivalència de recepta',
    recipeFactorLead: '1 {{base}} equival a',
    recipeFactorAria: 'Quantitat de {{recipe}} que equival a 1 {{base}}',
    recipeFactorExample:
      'Una recepta que gasti 1 {{recipe}} descomptarà {{quantity}} {{base}} de les existències.',
    recipeFactorRequired: 'Indica l’equivalència: la unitat de recepta és diferent de la base.',
    recipeFactorNotPositive: 'L’equivalència ha de ser un número més gran que zero.',
    recipeUnitChangeWarning_one:
      'Hi ha {{count}} línia de recepta que porta aquest article en {{unit}}. Si canvies la unitat de recepta, deixarà de tenir equivalència amb la unitat base i les existències es descomptaran 1:1.',
    recipeUnitChangeWarning_other:
      'Hi ha {{count}} línies de recepta que porten aquest article en {{unit}}. Si canvies la unitat de recepta, deixaran de tenir equivalència amb la unitat base i les existències es descomptaran 1:1.',
    validationError: 'No s’ha desat: revisa els camps marcats en vermell.',
    validationErrorField: 'No s’ha desat: revisa «{{field}}».',
    noUnits:
      'Encara no hi ha unitats definides per a l’obrador. Defineix-les a Configuració abans de crear articles.',
    isVariableConversion: 'Conversió variable',
    isVariableConversionHint:
      'Article sense equivalència fixa entre la unitat que es compta i la que es mesura (p. ex. peces tallades): es pesa en cada moviment.',
    shelfLifeDays: 'Vida útil (dies)',
    shelfLifeHint: 'Determina la caducitat suggerida del lot en tancar l’OT.',
    vatRate: 'IVA (%)',
    tax: 'Impost',
    taxHint: 'Es tria del mestre d’impostos (Configuració · Impostos)',
    taxNone: 'Sense impost',
    taxUnavailable: 'Impost donat de baixa',
    valueUnavailable: 'No disponible',
    noTaxesMaster: 'No hi ha impostos configurats. Crea’n a Configuració · Impostos.',
    minStock: 'Existències mínimes',
    maxStock: 'Existències màximes',
    minStockHint: 'Avís d’existències sota mínim (mai bloqueja).',
    defaultSupplier: 'Proveïdor per defecte',
    defaultPlace: 'Lloc per defecte',
    defaultPlaceHint: 'On queden les existències quan es reben o es produeixen, sense preguntar-ho a ningú. Pot ser una zona, una secció o una ubicació.',
    purchasePrice: 'Preu de compra',
    allergensManualHint:
      'Marca els al·lèrgens presents a la matèria primera o l’embalatge (conté / traces).',
    allergensDerivedHint:
      'Els al·lèrgens d’un producte acabat o semielaborat es deriven automàticament del BOM de la recepta. No s’editen manualment.',
    allergensDerivedEmpty:
      'Encara no hi ha al·lèrgens derivats. S’agregaran del BOM quan la recepta estigui definida (Onada 1 — M3).',
    presenceContains: 'Conté',
    presenceTraces: 'Traces',
    presenceNone: 'Absent',
    save: 'Desa l’article',
    saved: 'Article desat',
    saveError: 'No s’ha pogut desar l’article',
    allergensSaveError:
      'L’article s’ha desat, però no s’han pogut desar els al·lèrgens. Torna-ho a provar.',
    conversionsSaveError:
      'L’article s’ha desat, però no s’han pogut desar les conversions. Torna-ho a provar.',
    create: 'Crea article',
    created: 'Article creat',
    createdBody: 'L’article s’ha afegit al catàleg.',
    createError: 'No s’ha pogut crear l’article',
    cancel: 'Cancel·la',
    deactivate: 'Dóna de baixa',
    deactivateTitle: 'Dóna de baixa l’article',
    deactivateBody:
      'Baixa lògica reversible: l’article desapareix del catàleg actiu però es conserva per a la traçabilitat. Indica el motiu (obligatori).',
    deactivatedBanner: 'Aquest article està donat de baixa.',
    reactivate: 'Reactiva l’article',
    notFound: 'No s’ha trobat l’article',
    addIngredient: 'Afegeix ingredient',
    addPackagingUnit: 'Afegeix unitat d’embalatge',
    addSupplier: 'Afegeix proveïdor',
    complianceLabel: 'Legislació i compliment',
    complianceSaveError: 'No s’han pogut desar les declaracions de compliment.',
    conservationCondition: 'Condició de conservació',
    containerNotes: 'Notes de l’envàs',
    containerSize: 'Mida',
    containerType: 'Tipus d’envàs',
    containerUnitsPer: 'Unitats per envàs',
    containerWeight: 'Pes (g)',
    createIngredient: 'Crea ingredient',
    createIngredientError: 'No s’ha pogut crear l’ingredient.',
    defaultLabelTemplate: 'Format d’etiqueta per defecte',
    description: 'Descripció',
    descriptionHint: 'Breu descripció que apareix a la fitxa tècnica',
    image: 'Imatge del producte',
    imageHint: 'JPG o PNG, fins a 5 MB',
    imageInvalidType: 'El fitxer no és una imatge.',
    imageTooLarge: 'La imatge supera els 5 MB.',
    imageUploadError: 'No s’ha pogut pujar la imatge.',
    imageUploading: 'Pujant…',
    ingredient: 'Ingredient',
    ingredientPercentage: '%',
    ingredientsHint: 'En ordre decreixent de pes; apareixen a la fitxa tècnica',
    ingredientsLabel: 'Composició declarada',
    ingredientsSaveError: 'No s’ha pogut desar la composició.',
    isManufacturedHint: 'Es fabrica amb recepta pròpia',
    isPurchasedHint: 'Es compra a un proveïdor',
    isSoldHint: 'Es ven a client',
    isStockTracked: 'Controlar existències',
    isStockTrackedHint: 'Si es desactiva, l’article no genera moviments ni avisos d’estoc (p. ex. aigua de xarxa), però segueix disponible a receptes i fitxa tècnica.',
    lastPrice: 'Preu de compra (unitat base)',
    lastPriceUnit: 'Preu de compra ({{code}})',
    purchaseUnit: 'Unitat de compra',
    purchaseUnitHint: 'Format en què es compra a aquest proveïdor (Caixa, Sac…)',
    purchaseUnitBase: 'Unitat base',
    moveDown: 'Baixa',
    moveUp: 'Puja',
    newIngredientName: 'Nou ingredient',
    noComplianceItems: 'No hi ha declaracions de compliment configurades. Afegeix-ne a Configuració.',
    noIngredientsMaster: 'No hi ha ingredients al mestre. Crea’n un a sota o a Configuració.',
    noPackagingUnits: 'No hi ha unitats d’embalatge configurades. Afegeix-ne a Configuració.',
    noActivePrimarySupplier: 'El proveïdor principal d’aquest article ({{name}}) està donat de baixa. L’article no té cap proveïdor principal actiu fins que en triïs un altre.',
    noSuppliers: 'Encara no hi ha proveïdors vinculats.',
    noSuppliersMaster: 'No hi ha proveïdors al mestre. Crea’n a Configuració.',
    nutritionLabel: 'Informació nutricional (per 100 g)',
    packagingDefault: 'Compra per defecte',
    packagingDefaultBase: 'Compra per defecte: unitat base ({{code}})',
    packagingDefaultSales: 'Venda per defecte',
    packagingDefaultSalesBase: 'Venda per defecte: unitat base ({{code}})',
    packagingHint: '1 unitat d’embalatge = N {{code}} (sempre contra la unitat base)',
    packagingRecipeEquivalence: 'Equival a {{quantity}} {{unit}} per a recepta.',
    packagingQuantity: 'Equival a',
    packagingSaveError: 'No s’han pogut desar les unitats d’embalatge.',
    packagingUnit: 'Unitat d’embalatge',
    packagingUnitsLabel: 'Unitats d’embalatge',
    pdfInvalidType: 'El fitxer ha de ser un PDF.',
    pdfTooLarge: 'El PDF supera els 10 MB.',
    preparationMode: 'Mode de preparació',
    removeSupplier: 'Treu proveïdor',
    priceHistory: 'Històric de preus',
    removeSupplierBody: 'Cal indicar el motiu per treure aquest proveïdor. La vinculació es dona de baixa, no s’elimina.',
    removeSupplierTitle: 'Treure proveïdor',
    salePrice: 'Preu de venda',
    salePriceHint: 'Per unitat base. Pre-omple el preu a les línies de comanda de venda.',
    salePriceHintUnit: 'Per {{code}}. Pre-omple el preu a les línies de comanda de venda.',
    sectionConservation: 'Conservació i ús',
    sectionLabeling: 'Etiquetatge',
    sectionPackaging: 'Envasat i logística',
    sectionSafety: 'Seguretat alimentària',
    sectionSafetyHint: 'Escriu la informació de seguretat alimentària en blocs de text lliure. Apareixeran a la fitxa tècnica en aquest mateix ordre.',
    safetyBlockTitle: 'Títol',
    safetyBlockTitlePlaceholder: 'Ex.: Al·lèrgens',
    safetyBlockBody: 'Text',
    safetyBlockBodyPlaceholder: 'Escriu aquí el text que vols que aparegui a la fitxa tècnica',
    addSafetyBlock: 'Afegeix un bloc de text',
    removeSafetyBlock: 'Treu el bloc',
    moveSafetyBlockUp: 'Mou amunt',
    moveSafetyBlockDown: 'Mou avall',
    safetyEmpty: 'Encara no hi ha cap bloc de text. Afegeix-ne un per començar.',
    safetyBodyRequired: 'El text del bloc no pot quedar buit',
    sectionSuppliers: 'Proveïdors',
    skuAutoHint: 'Es generarà automàticament en desar',
    supplier: 'Proveïdor',
    supplierDescription: 'Descripció del proveïdor',
    supplierInactiveHint: 'Proveïdor donat de baixa: no es pot fer servir en compres noves. El vincle, el preu i la referència es conserven per a l’històric.',
    supplierPrimary: 'Proveïdor principal',
    supplierRef: 'Codi del proveïdor',
    suppliersHint: 'Un proveïdor principal com a màxim',
    suppliersSaveError: 'No s’han pogut desar els proveïdors.',
    techSheet: 'Fitxa tècnica del proveïdor',
    techSheetCreateHint: 'Desa l’article per adjuntar la fitxa tècnica',
    techSheetDownload: 'Descarrega',
    techSheetUploadError: 'No s’ha pogut pujar la fitxa tècnica.',
    techSheetUploading: 'Pujant…',
    usageType: 'Tipus d’ús',
    nutritionEnergyKj: 'Energia (kJ)',
    nutritionEnergyKcal: 'Energia (kcal)',
    nutritionFat: 'Greixos (g)',
    nutritionSaturatedFat: 'de les quals saturades (g)',
    nutritionCarbohydrate: 'Hidrats de carboni (g)',
    nutritionSugars: 'de què sucres (g)',
    nutritionProtein: 'Proteïnes (g)',
    nutritionSalt: 'Sal (g)',
  },

  'backoffice.technicalSheetScreen': {
    sourceManual: 'Informació declarada a la fitxa de l’article (origen manual).',
    sourceDerived: 'Calculat de la versió {{version}} de la recepta activa.',
    ingredientsEmpty: 'Encara no hi ha ingredients per declarar.',
    allergensEmpty: 'Cap al·lergen declarat.',
    title: 'Fitxa tècnica',
    subtitle: 'Document reglamentari derivat del BOM (Reg. UE 1169/2011)',
    generateVersion: 'Genera versió',
    generating: 'Generant…',
    generateError: 'No s’ha pogut generar la versió',
    versionHistory: 'Historial de versions',
    noVersions: 'Encara no hi ha cap versió de fitxa tècnica',
    version: 'Versió',
    versionLabel: 'v{{n}}',
    date: 'Data',
    responsible: 'Responsable',
    status: 'Estat',
    statusDraft: 'Esborrany',
    statusApproved: 'Aprovada',
    statusArchived: 'Arxivada',
    ingredientsTitle: 'Ingredients (ordre ponderal)',
    allergensTitle: 'Al·lèrgens (14 UE, agregats)',
    nutritionTitle: 'Informació nutricional / 100 g',
    foodSafetyTitle: 'Seguretat alimentària',
    foodSafetyHint: 'Textos de seguretat alimentària de l’article. S’editen a la fitxa d’article i queden congelats a cada versió de la fitxa tècnica.',
    foodSafetyEmpty: 'Aquest article encara no té textos de seguretat alimentària.',
    legislationTitle: 'Legislació i normativa',
    legislationHint: 'Textos legals de l’obrador (Configuració · Legislació i normativa). S’imprimeixen com a text final de la fitxa tècnica.',
    legislationEmpty: 'Encara no hi ha textos de legislació configurats.',
    nutritionPlaceholder: 'Pendent (Onada 2 — M15)',
    labelNote:
      'L’etiqueta NO es genera aquí: es genera en tancar l’OT o en envasar (amb lot i caducitat).',
    conservation: 'Conservació',
    netWeight: 'Pes net',
    legalDenomination: 'Denominació legal',
    shelfLifeDays: 'Vida útil (dies)',
    noAllergens: 'Sense al·lèrgens declarats',
    bomNoRecipe:
      'Aquest article no té cap recepta amb versió publicada. Defineix i publica la recepta perquè la fitxa en pugui treure els ingredients.',
    allergensFromBom: 'Al·lèrgens agregats automàticament del BOM de la recepta.',
    onlyFinishedHint: 'La fitxa tècnica només es genera per a articles fabricats (elaborats amb recepta pròpia).',
    nutritionHint: 'Valors per 100 g (Reg. UE 1169/2011). S’editen a la fitxa d’article i queden congelats a cada versió de la fitxa tècnica.',
    nutritionEmpty: 'Encara no hi ha informació nutricional.',
    nutritionEnergyKj: 'Energia (kJ)',
    nutritionEnergyKcal: 'Energia (kcal)',
    nutritionFat: 'Greixos (g)',
    nutritionSaturatedFat: 'dels quals saturats (g)',
    nutritionCarbohydrate: 'Hidrats de carboni (g)',
    nutritionSugars: 'dels quals sucres (g)',
    nutritionProtein: 'Proteïnes (g)',
    nutritionSalt: 'Sal (g)',
    diffModalTitle: 'Canvis de la nova versió',
    diffModalIntro: 'Revisa què canvia respecte a la versió vigent abans de generar l’esborrany.',
    diffFirstVersion: 'No hi ha cap versió anterior: es crearà la primera versió de la fitxa tècnica.',
    diffNoChanges: 'Cap canvi respecte a la versió vigent.',
    diffKindAdded: 'Afegit',
    diffKindRemoved: 'Eliminat',
    diffKindChanged: 'Modificat',
    diffKindUnchanged: 'Sense canvis',
    diffFieldIngredients: 'Ingredients',
    diffFieldAllergens: 'Al·lèrgens',
    diffFieldShelfLife: 'Vida útil (dies)',
    diffFieldNutrition: 'Informació nutricional',
    diffFieldRecipeVersion: 'Versió de recepta',
    confirmGenerate: 'Genera i aprova la versió',
    colActions: 'Accions',
    archive: 'Arxiva',
    archiving: 'Arxivant…',
    archiveError: 'No s’ha pogut arxivar la versió',
    frozenError: 'Una versió aprovada és un document congelat pel servidor: no es pot modificar. Genera una versió nova.',
    pdfDocumentTitle: 'Fitxa tècnica de producte',
    pdfVersion: 'Versió',
    pdfRevisionDate: 'Data de revisió',
    pdfArticleName: 'Nom article',
    pdfProductCode: 'Codi producte',
    pdfDescription: 'Descripció producte',
    pdfUsage: 'Tipus d\'ús',
    pdfNutritionTitle: 'Informació nutricional per 100 g',
    pdfConservationTitle: 'Condicions de conservació',
    pdfContainerType: 'Tipus d\'envàs',
    pdfShelfLife: 'Consum preferent',
    pdfLogisticsTitle: 'Dades logístiques',
    pdfContainerFormatTitle: 'Format de l\'envàs',
    pdfUnitsPerBox: 'Ut./caixa',
    pdfBoxesPerPallet: 'Caixes/palet',
    pdfDimensions: 'Dimensions (H×L×P)',
    pdfContains: 'Conté',
    pdfMayContain: 'Pot contenir',
    pdfLegislationTitle: 'Legislació',
    pdfDays: 'dies',
    downloadPdf: 'Descarrega PDF',
    generatingPdf: 'Generant…',
    pdfGenerationError: 'No s\'ha pogut generar el PDF de la fitxa tècnica.',
  },

  'backoffice.articleTypeLabel': {
    raw_material: 'Matèria primera',
    intermediate: 'Producte semielaborat',
    finished: 'Producte acabat',
    packaging: 'Producte auxiliar',
  },

  'backoffice.articleCategoryLabel': {
    raw_material: 'Matèria primera',
    auxiliary: 'Auxiliar',
    intermediate: 'Semielaborat',
    finished: 'Acabat',
    packaging: 'Embalatge',
  },

  'backoffice.recipes': {
    // List (screen 13)
    title: 'Receptes',
    subtitle: 'Receptes de l’obrador amb versionat immutable',
    newRecipe: 'Nova recepta',
    searchPlaceholder: 'Cerca per nom o article',
    colName: 'Recepta',
    colOutput: 'Article produït',
    colVersion: 'Versió activa',
    empty: 'Encara no hi ha cap recepta',
    emptyFiltered: 'Cap recepta coincideix amb la cerca',
    showInactive: 'Mostra les donades de baixa',
    inactiveBadge: 'Baixa',
    noActiveVersion: 'Sense versió activa',
    draftBadge: 'Esborrany',
    create: 'Crea recepta',
    newVersion: 'Nova versió',
    // Detail (screen 13)
    detailSubtitle: 'Versió activa, BOM en ordre ponderal i historial',
    backToList: 'Torna a receptes',
    activeVersion: 'Versió activa',
    versionLabel: 'Versió {{n}}',
    noActiveVersionTitle: 'Aquesta recepta encara no té cap versió publicada',
    noActiveVersionBody:
      'Publica una versió esborrany perquè aparegui al catàleg i es pugui crear OT.',
    sectionMeta: 'Dades de la versió',
    sectionIngredients: 'Ingredients (ordre ponderal)',
    sectionSteps: 'Elaboració',
    sectionAllergens: 'Al·lèrgens (14 UE, agregats del BOM)',
    sectionScaling: 'Escalat de recepta',
    sectionVersions: 'Historial de versions',
    recipeType: 'Tipus de recepta',
    recipeTypeHint:
      'Una d’envasat consumeix el granel i l’envàs, i a planta demana de quins envasos vols etiqueta.',
    recipeType_normal: 'Normal',
    recipeType_packaging: 'Envasat',
    leadTimeDays: 'Temps d’antelació (dies)',
    notes: 'Notes',
    colIngredient: 'Ingredient',
    colQuantity: 'Quantitat',
    colUnit: 'Unitat',
    colStep: 'Pas',
    colDescription: 'Nom',
    colDuration: 'Durada (min)',
    colTemperature: 'Temperatura (°C)',
    colControl: 'Control',
    blockingBadge: 'Bloquejant',
    expandIntermediate: 'Desplega el BOM',
    collapseIntermediate: 'Replega',
    intermediateNoRecipe: 'Aquest semielaborat no té recepta publicada',
    noIngredients: 'Aquesta versió no té ingredients',
    noSteps: 'Aquesta versió no té passos d’elaboració',
    noAllergens: 'Sense al·lèrgens declarats',
    presenceTraces: 'Traces',
    // Scaling
    scalingHint:
      'Introdueix un rendiment objectiu o un factor per veure les quantitats escalades. No modifica la recepta.',
    scalingTargetYield: 'Rendiment objectiu',
    scalingFactor: 'Factor',
    scalingResultTitle: 'Quantitats escalades',
    scalingInvalid: 'Introdueix un valor positiu',
    // Version history
    versionNumber: 'Versió',
    versionStatus: 'Estat',
    versionDate: 'Data',
    statusDraft: 'Esborrany',
    statusPublished: 'Publicada',
    statusActive: 'Activa',
    openDraft: 'Edita l’esborrany',
    viewVersion: 'Consulta',
    // Actions
    newVersionFromActive: 'Nova versió',
    editDraft: 'Edita',
    publish: 'Publica',
    publishing: 'Publicant…',
    discard: 'Descarta',
    discarding: 'Descartant…',
    publishHint:
      'En publicar es congela aquesta versió, s’agreguen els al·lèrgens del BOM i passa a ser la versió activa de la recepta.',
    fixValidation: 'Revisa les files marcades abans de desar o publicar.',
    packagingNotDeclared:
      'Els envasos que hi hagi en aquesta llista els consumeix l\u2019ordre de treball com qualsevol altra l\u00ednia, per\u00f2 NO surten a la declaraci\u00f3 d\u2019ingredients de la fitxa t\u00e8cnica ni de l\u2019etiqueta: all\u00e0 nom\u00e9s hi va el que es menja.',
    publishNeedsIngredients:
      'Una versi\u00f3 sense ingredients no es pot publicar: d\u2019aquesta llista en surten els al\u00b7l\u00e8rgens, la fitxa t\u00e8cnica i l\u2019etiqueta. Afegeix-hi almenys un ingredient. L\u2019esborrany el pots desar igualment.',
    rescaleOffer:
      'El rendiment ha passat de {{from}} a {{to}}. Vols multiplicar els ingredients per {{factor}}? Si el que has fet \u00e9s corregir un rendiment mal escrit, deixa\u2019ls com estan.',
    rescaleApply: 'Reescala els ingredients',
    rescaleKeep: 'Deixa\u2019ls com estan',
    // Editor
    editorTitleNew: 'Nova recepta',
    editorTitleEdit: 'Edita esborrany',
    editorSubtitleNew: 'Crea una recepta i la seva versió 1 (esborrany)',
    editorSubtitleEdit: 'Edita la versió esborrany abans de publicar',
    editorSectionMeta: 'Planificació',
    editorSectionOutputs: 'Resultat de la recepta',
    editorSectionIngredients: 'Matèria primera',
    editorSectionSteps: 'Elaboració',
    editorSectionResources: 'Recursos',
    name: 'Nom de la recepta',
    // Resultat de la recepta (outputs table). The first line is the recipe's
    // main product; the rest are additional products of the same elaboració.
    outputProduct: 'Producte',
    outputsHint:
      'La primera línia és el producte principal de la recepta. Afegeix-ne més si l’elaboració en produeix d’altres.',
    addOutput: 'Afegeix línia',
    removeOutput: 'Elimina',
    addIngredient: 'Afegeix ingredient',
    removeIngredient: 'Elimina',
    moveUp: 'Amunt',
    moveDown: 'Avall',
    ingredient: 'Ingredient',
    quantity: 'Quantitat',
    unit: 'Unitat',
    addStep: 'Afegeix un pas en blanc',
    // Recursos (el que ocupa l'elaboració i no és matèria primera)
    resource: 'Tipus de recurs',
    resourceQuantity: 'Quantitat',
    resourceDuration: 'Temps (min)',
    selectResource: 'Selecciona un tipus',
    addResource: 'Afegeix recurs',
    removeResource: 'Elimina',
    noResources: 'Encara no hi ha recursos en aquesta recepta.',
    noResourcesMaster:
      'No hi ha tipus de recurs definits per a l’obrador. Defineix-ne a Configuració · Recursos per poder-los demanar a la recepta.',
    colResource: 'Tipus de recurs',
    colResourceDuration: 'Temps (min)',
    resourceLegacyMachine: 'recurs fixat: {{name}}',
    removeStep: 'Elimina',
    process: 'Procés',
    selectProcess: 'Selecciona un procés',
    // Passos d'elaboració (OBR-17): instàncies editables, amb ingredients i recursos propis
    stepLabel: 'Pas {{n}}',
    stepName: 'Nom del pas',
    stepNamePlaceholder: 'p. ex. Amassat inicial',
    stepInstructions: 'Instruccions',
    stepInstructionsPlaceholder: 'Què s’ha de fer en aquest pas (opcional)',
    stepControl: 'Control',
    stepProcessHint:
      'Opcional. Un pas es pot escriure a mà; si en tries un procés, se n’emplenen els valors com a punt de partida i després els adaptes a la recepta.',
    noProcess: 'Sense procés',
    processRetired: 'Procés donat de baixa',
    addStepFromProcess: 'Afegeix un pas des d’un procés',
    stepFromProcessPlaceholder: 'Tria un procés…',
    reapplyProcess: 'Torna a aplicar els valors del procés',
    reapplyConfirmTitle: 'Torna a aplicar el procés «{{process}}»',
    reapplyConfirmBody: 'Aquest pas ja té valors escrits que no coincideixen amb els del procés. Si continues, se sobreescriuran amb els del procés i es perdrà el que hi havia escrit en aquests camps:',
    reapplyConfirmAction: 'Sobreescriu el pas',
    removeStepConfirmTitle: 'Elimina el {{step}}',
    removeStepConfirmBody: 'S’eliminarà el pas «{{name}}» amb les instruccions, el control i els recursos que hi has definit.',
    removeStepConfirmIngredients_one: 'L’ingredient que hi entra es queda al BOM, sense pas: {{names}}.',
    removeStepConfirmIngredients_other: 'Els {{count}} ingredients que hi entren es queden al BOM, sense pas: {{names}}.',
    removeStepConfirmUnsaved: 'Encara no s’ha desat: si t’hi penedeixes, surt sense desar i es manté la versió desada.',
    removeStepConfirmAction: 'Elimina el pas',
    duplicateOutput: 'Ja existeix una recepta per a «{{name}}». Una recepta només es pot tenir un cop per article: obre-la i crea’n una versió nova.',
    duplicateOutputGeneric: 'Ja existeix una recepta per a aquest article. Una recepta només es pot tenir un cop per article: obre-la i crea’n una versió nova.',
    openExistingRecipe: 'Obre la recepta existent',
    duplicateStep: 'Duplica',
    stepResources: 'Recursos necessaris',
    noStepResources: 'Aquest pas no necessita cap recurs.',
    stepIngredients: 'Ingredients que s’hi incorporen',
    noStepIngredients: 'Cap. S’assignen des de la secció de matèria primera.',
    stepOrderHint:
      'Arrossega un pas pel nom o fes servir les fletxes per canviar-ne l’ordre. La numeració només és de visualització.',
    noStepsYet: 'Encara no hi ha cap pas. Afegeix-ne un en blanc o des d’un procés.',
    ingredientStep: 'S’incorpora al pas',
    ingredientStepNone: 'Sense pas',
    ingredientStepHint:
      'Opcional: el pas en què s’incorpora cada ingredient, perquè l’App ho pugui ensenyar quan toqui.',
    stepMissing: 'El pas assignat a aquest ingredient ja no existeix',
    looseResourcesTitle: 'Recursos sense pas assignat',
    looseResourcesHint:
      'Línies de recursos que la recepta demanava en conjunt, d’abans que els recursos anessin per pas. Es mantenen tal com són; per assignar-les a un pas, afegeix-les al pas i elimina-les d’aquí.',
    saveNothing: 'No hi ha canvis per desar',
    stepDuration: 'Durada (min)',
    stepTemperature: 'Temperatura (°C)',
    isBlocking: 'Bloquejant (només PCC)',
    save: 'Desa l’esborrany',
    saved: 'Esborrany desat',
    cancel: 'Cancel·la',
    noUnits:
      'Encara no hi ha unitats definides per a l’obrador. Defineix-les a Configuració abans de crear receptes.',
    noOutputArticles:
      'No hi ha articles fabricats o acabats al catàleg. Crea’n un abans de definir una recepta.',
    noProcesses:
      'Encara no hi ha processos definits. Pots escriure els passos a mà, o definir-ne a Configuració · Processos perquè serveixin de plantilla.',
    selectArticle: 'Selecciona un article',
    selectUnit: 'Selecciona una unitat',
    readOnly: 'Només lectura',
    // Errors / validation
    requiredIngredient: 'Cal almenys un ingredient',
    blockingNeedsPcc: 'Només un pas amb control PCC pot ser bloquejant',
    outputCannotBeIngredient:
      'Una recepta no pot portar com a ingredient un article que ella mateixa produeix',
    createError: 'No s’ha pogut crear la recepta',
    loadError:
      'No s’han pogut carregar les dades de la recepta. Torna-ho a provar abans d’editar-la.',
    saveError: 'No s’ha pogut desar l’esborrany',
    publishError: 'No s’ha pogut publicar la versió',
    publishErrorActivate:
      'La versió s’ha publicat però no s’ha pogut marcar com a activa. Torna a provar de publicar.',
    discardError: 'No s’ha pogut descartar l’esborrany',
    unresolvedAllergensWarning:
      'Alguns semielaborats del BOM no tenen recepta publicada; els seus al·lèrgens no s’han pogut agregar.',
    // Discard dialog
    discardTitle: 'Descarta l’esborrany',
    discardBody:
      'S’eliminarà aquesta versió esborrany i el seu BOM i passos. Si és l’única versió, també es donarà de baixa la recepta. Indica el motiu (obligatori).',
    notFound: 'No s’ha trobat la recepta',
    // Step control type enum labels
    'controlType.none': 'Cap',
    'controlType.weight': 'Pesatge',
    'controlType.timer': 'Temporitzador',
    'controlType.temperature': 'Temperatura',
    'controlType.pcc': 'PCC',
      tolerancePct: 'Tolerància (%)',
    tolerancePctHint: 'Buit = s’aplica la tolerància de l’obrador',
    lineStockEquivalent: 'Existències: {{quantity}} {{unit}} de consum teòric.',
    lineNoEquivalenceUseBase:
      '«{{unit}}» no té equivalència amb la unitat base de l’article ({{base}}): les existències es descomptarien 1:1. Posa la línia en {{base}}.',
    lineNoEquivalence:
      '«{{unit}}» no té equivalència amb la unitat base de l’article ({{base}}): les existències es descomptarien 1:1. Configura la unitat de recepta a la fitxa de l’article.',
    tolerancePctPlaceholder: 'Obrador',
    sequentialPreparation: 'Preparació seqüencial',
    sequentialPreparationHint: 'L’operari prepara els ingredients un a un, en aquest ordre',
    dragToReorder: 'Arrossega per reordenar',
    dragHandle: 'Reordena l’ingredient',
    ingredientOrderHint: 'L’ordre d’aquesta llista és l’ordre de preparació a planta',
},

  'backoffice.stock': {
    articleTitle: 'Existències — {{article}}',
    articleCard: 'Fitxa de l’article',
    allArticles: 'Totes les existències',
    articleEmpty: 'Aquest article encara no té existències registrades.',
    title: 'Existències',
    subtitle: 'Existències per article, lot i caducitat',
    available: 'Disponible',
    blocked: 'Bloquejat',
    blockedChip: 'Bloquejat: {{quantity}} {{unit}}',
    blockedHint:
      'Excepcions de recepció pendents de decisió i devolucions pendents de retornar: no compta com a disponible per a producció.',
    blockedSummary:
      'Físic: {{physical}} {{unit}} · Bloquejat: {{blocked}} {{unit}} · Disponible: {{free}} {{unit}}',
    byLot: 'Per lot',
    byZone: 'Per zona',
    belowMinimum: 'Sota mínim',
    // Article list
    searchPlaceholder: 'Cerca per SKU o nom',
    filterType: 'Tipologia',
    filterStatus: 'Estat',
    statusAll: 'Tots',
    statusExpiring: 'Caduca aviat',
    statusExpired: 'Caducat',
    expiringBadge: 'Caduca aviat',
    expiredBadge: 'Caducat',
    typeAll: 'Totes',
    typePurchase: 'Compra',
    typeIntermediate: 'Semielaborat',
    typeSale: 'Venda',
    colSku: 'SKU',
    colName: 'Article',
    colType: 'Tipologia',
    colAvailable: 'Disponible',
    colCommitted: 'Compromès',
    colUnit: 'Unitat',
    empty: 'Encara no hi ha existències. Es generaran amb les recepcions i la producció.',
    emptyFiltered: 'Cap article coincideix amb el filtre.',
    loadError: 'No s’han pogut carregar les existències',
    // Lot breakdown
    lotBreakdown: 'Desglossament per lot',
    colLot: 'Lot',
    colExpiry: 'Caducitat',
    colPlace: 'Lloc',
    colQuantity: 'Quantitat',
    colStatus: 'Estat',
    noLots: 'Aquest article no té existències per lot.',
    noPlace: 'Sense lloc',
    // Existències per lloc (filtre, OBR-90)
    filterPlace: 'Lloc',
    placeAll: 'Tots els llocs',
    colInPlace: 'En aquest lloc',
    placeNoteZone: 'Existències a {{place}}, incloses les seves seccions i ubicacions.',
    placeNoteSection: 'Existències a {{place}}, incloses les seves ubicacions.',
    placeNoteLocation: 'Existències a {{place}}.',
    placeNoteNone: 'Existències que encara no tenen lloc. Des del desglossament per lot, «Mou» les porta on toca.',
    emptyPlace: 'No hi ha existències en aquest lloc.',
    noLotsInPlace: 'Aquest article no té lots en aquest lloc.',
    noExpiry: 'Sense caducitat',
    supplierLotCode: 'Lot del proveïdor: {{code}}',
    expand: 'Mostra els lots',
    collapse: 'Amaga els lots',
    'lotStatus.available': 'Disponible',
    'lotStatus.quarantined': 'En quarantena',
    'lotStatus.consumed': 'Consumit',
    'lotStatus.expired': 'Caducat',
    notTracked: 'Sense control d’existències',
    inBaseUnit: '(= {{quantity}} {{unit}})',
    // Moure existències (traspàs entre llocs de l’obrador)
    moveAction: 'Mou',
    moveTitle: 'Mou existències',
    moveBody: 'Canvia on són les existències. El total de l’article no varia: queden dos moviments de traspàs, un de sortida i un d’entrada.',
    moveFrom: 'Origen',
    moveTo: 'Destí',
    moveToPlaceholder: 'Tria on van',
    moveQuantity: 'Quantitat',
    moveAvailable: 'Disponible a l’origen: {{quantity}} {{unit}}',
    moveQuantityInvalid: 'Ha de ser més gran que zero i no pot passar del que hi ha a l’origen.',
    moveNote: 'Nota',
    moveNotePlaceholder: 'Opcional',
    moveConfirm: 'Mou',
    moving: 'S’està movent…',
    moveError: 'No s’han pogut moure les existències.',
    moveErrorInsufficient: 'A l’origen ja no n’hi ha prou. Torna a obrir el lot per veure el que hi queda.',
    moveNoDestinations: 'No hi ha cap altre lloc on moure-les. Configura l’estructura de l’obrador.',
  },

  'backoffice.inventory': {
    title: 'Inventari',
    startCount: 'Inicia recompte',
    adjust: 'Ajusta',
    adjustReason: 'Motiu de l’ajust',
    // Adjust dialog (screen 14)
    adjustTitle: 'Ajusta existències',
    adjustBody:
      'Els moviments d’existències no s’editen mai. Un ajust crea un moviment nou amb el motiu documentat.',
    delta: 'Variació (signe inclòs)',
    deltaHint: 'Positiu per afegir existències, negatiu per restar-ne.',
    deltaInvalid: 'Indica una variació diferent de zero.',
    reason: 'Motiu de l’ajust',
    reasonNote: 'Nota del motiu',
    reasonNotePlaceholder: 'Descriu el motiu de l’ajust',
    confirm: 'Registra l’ajust',
    adjusting: 'Registrant…',
    adjustError: 'No s’ha pogut registrar l’ajust',
    selectReason: 'Selecciona un motiu',
  },

  'backoffice.movements': {
    title: 'Moviments d’existències',
    type: 'Tipus',
    reason: 'Motiu',
    reverse: 'Contraresta',
    appendOnlyNote: 'Els moviments no s’editen; les correccions són contramoviments',
    // Ledger (screen 14)
    colDate: 'Data',
    colType: 'Tipus',
    colQuantity: 'Quantitat',
    colReason: 'Motiu',
    colPlace: 'Lloc',
    colWho: 'Registrat per',
    empty: 'Encara no hi ha cap moviment per a aquest article.',
    loadError: 'No s’han pogut carregar els moviments',
    quantityWithUnit: '{{quantity}} {{unit}}',
  },

  'backoffice.movementType': {
    reception: 'Recepció',
    o01_consumption: 'Consum d’OT',
    production_output: 'Producció',
    adjustment: 'Ajust',
    write_off: 'Baixa',
    reversal: 'Contramoviment',
    shipment: 'Expedició',
    transfer: 'Traspàs',
    purchase_return: 'Devolució de compra',
  },

  'backoffice.movementReason': {
    recount: 'Recompte',
    loss: 'Merma',
    damage: 'Dany',
    expired: 'Caducat',
    burnt: 'Cremat',
    dropped: 'Caigut',
    correction: 'Correcció',
    reception: 'Recepció',
    production: 'Producció',
    consumption: 'Consum',
  },

  'backoffice.purchaseOrders': {
    articleTitle: 'Compres — {{article}}',
    articleCard: 'Fitxa de l’article',
    allOrders: 'Totes les comandes',
    articleEmpty: 'D’aquest article no se n’ha comprat mai: cap comanda ni cap albarà de compra.',
    articleLinesTitle: 'Línies de comanda',
    articleNoLines: 'Aquest article no apareix en cap comanda de compra.',
    articleReceptionsTitle: 'Recepcions',
    articleNoReceptions: 'Aquest article no s’ha rebut en cap albarà.',
    totalOrdered: 'Demanat',
    totalReceived: 'Rebut',
    totalPending: 'Pendent de rebre',
    unmeasurableLines_one:
      '{{count}} línia no entra als totals: el seu format no té conversió configurada per a l\'article.',
    unmeasurableLines_other:
      '{{count}} línies no entren als totals: el seu format no té conversió configurada per a l\'article.',
    notVisibleToPlanta:
      'Aquesta comanda encara no és visible a l’app de planta. Confirma-la perquè l’operari la pugui rebre.',
    title: 'Comandes de compra',
    newOrder: 'Nova comanda',
    allSuppliers: 'Tots els prove\u00efdors',
    searchSupplier: 'Cerca un prove\u00efdor',
    noSupplierMatches: 'Cap prove\u00efdor que hi coincideixi',
    expectedFrom: 'Entrega prevista des de',
    expectedTo: 'Entrega prevista fins a',
    clearFilters: 'Treu els filtres',
    searchLabel: 'Cerca per número o proveïdor',
    emptyFiltered: 'Cap comanda encaixa amb aquests filtres. Prova d\u2019eixamplar-los o treu-los.',
    selectAll: 'Selecciona totes les comandes visibles',
    selectedCount_one: '{{count}} comanda seleccionada',
    selectedCount_other: '{{count}} comandes seleccionades',
    clearSelection: 'Buida la selecció',
    colSent: 'Enviada',
    finishOrder: 'Finalitza',
    finishOrderLabel: 'Finalitza la comanda {{number}}',
    closedWithPending: 'Amb pendent',
    closedWithPendingHint: 'Finalitzada amb pendent de rebre: el que faltava ja no s’espera.',
    bulkConfirm: 'Confirma ({{count}})',
    bulkSend_one: 'Envia ({{count}})',
    bulkSend_other: 'Envia ({{count}})',
    bulkApply: 'Aplica l\u2019acció',
    bulkConfirmTitle: 'Confirma les comandes',
    bulkSendTitle: 'Envia les comandes',
    bulkConfirmBody_one:
      'Es confirmar\u00e0 {{count}} de les {{selected}} marcades: passar\u00e0 a \u00abpendent d\u2019entrega\u00bb. La resta es queden com estan i et dir\u00e0 per qu\u00e8.',
    bulkConfirmBody_other:
      'Es confirmaran {{count}} de les {{selected}} marcades: passaran a \u00abpendent d\u2019entrega\u00bb. La resta es queden com estan i et dir\u00e0 per qu\u00e8.',
    bulkSendBody_one: 'S’enviarà {{count}} de les {{selected}} marcades pel canal del seu proveïdor: per correu si en té, o marcada com a enviada si les rep per altres mitjans. Les de proveïdors que les reben per WhatsApp no surten soles: te les oferirem per obrir-les i enviar-les tu. La resta es queden com estan i et dirà per què.',
    bulkSendBody_other: 'S’enviaran {{count}} de les {{selected}} marcades pel canal del seu proveïdor: per correu si en té, o marcades com a enviades si les rep per altres mitjans. Les de proveïdors que les reben per WhatsApp no surten soles: te les oferirem per obrir-les i enviar-les tu. La resta es queden com estan i et dirà per què.',
    bulkFinish: 'Finalitza ({{count}})',
    bulkFinishTitle: 'Finalitza les comandes',
    bulkFinishBody_one:
      'Es finalitzarà {{count}} de les {{selected}} marcades: confirmes que no n’esperes la resta. Passarà a «Entregada» amb el que s’ha rebut; els albarans i les existències no canvien, i el que falta queda registrat com a no rebut amb el teu nom, la data i el motiu. La resta es queden com estan i et dirà per què.',
    bulkFinishBody_other:
      'Es finalitzaran {{count}} de les {{selected}} marcades: confirmes que no n’esperes la resta. Passaran a «Entregada» amb el que s’ha rebut; els albarans i les existències no canvien, i el que falta de cadascuna queda registrat com a no rebut amb el teu nom, la data i el mateix motiu. La resta es queden com estan i et dirà per què.',
    bulkFinishConfirm_one: 'Finalitza {{count}} comanda',
    bulkFinishConfirm_other: 'Finalitza {{count}} comandes',
    bulkFinishBlockedClosed: '{{order}}: ja ho té tot rebut, no hi ha res a finalitzar.',
    bulkFinishBlockedNothing: '{{order}}: encara no s’ha rebut res; si no ha d’arribar, cancel·la-la.',
    bulkFinished_one: '{{count}} comanda finalitzada',
    bulkFinished_other: '{{count}} comandes finalitzades',
    skip_nothing_received_one: '{{count}} encara no ha rebut res (si no ha d’arribar, es cancel·la)',
    skip_nothing_received_other: '{{count}} encara no han rebut res (si no han d’arribar, es cancel·len)',
    bulkApplied_one: '{{count}} comanda actualitzada',
    bulkApplied_other: '{{count}} comandes actualitzades',
    skip_already_confirmed_one: '{{count}} ja estava confirmada',
    skip_already_confirmed_other: '{{count}} ja estaven confirmades',
    skip_not_confirmed_one: '{{count}} encara \u00e9s un esborrany sense confirmar',
    skip_not_confirmed_other: '{{count}} encara s\u00f3n esborranys sense confirmar',
    skip_already_sent_one: '{{count}} ja constava enviada',
    skip_already_sent_other: '{{count}} ja constaven enviades',
    skip_closed_one: '{{count}} ja ha arribat',
    skip_closed_other: '{{count}} ja han arribat',
    skip_cancelled_one: '{{count}} est\u00e0 cancel\u00b7lada',
    skip_cancelled_other: '{{count}} estan cancel\u00b7lades',
    bulkFailed_one: '{{count}} ha fallat: {{orders}}',
    bulkFailed_other: '{{count}} han fallat: {{orders}}',
    orderNumber: 'Núm.',
    supplier: 'Proveïdor',
    orderDate: 'Data',
    expectedDate: 'Entrega prevista',
    status: 'Estat',
    lines: 'Línies',
    empty: 'Encara no hi ha comandes de compra',
    searchPlaceholder: 'Cerca per número o proveïdor…',
    bulkEmailed_one: '{{count}} enviada per correu',
    bulkEmailed_other: '{{count}} enviades per correu',
    bulkMarked_one: '{{count}} marcada com a enviada',
    bulkMarked_other: '{{count}} marcades com a enviades',
    bulkWhatsapp_one: '{{count}} és d’un proveïdor que la rep per WhatsApp: no s’ha enviat, obre-la des d’aquí',
    bulkWhatsapp_other: '{{count}} són de proveïdors que les reben per WhatsApp: no s’han enviat, obre-les des d’aquí',
    bulkWhatsappOpen: 'Obre-les per WhatsApp',
    whatsappQueueTitle: 'Comandes per WhatsApp',
    whatsappQueueBody: 'Cada comanda s’obre a WhatsApp amb el missatge escrit i l’envies tu. WhatsApp no ens diu si s’ha enviat: quan ho hagis fet, prem «Ja l’he enviat» perquè en quedi constància.',
    whatsappQueueOpen: 'Obre WhatsApp',
    whatsappQueueSent: 'Ja l’he enviat',
    whatsappQueueDone: 'Consta com a enviada',
    whatsappQueueNoNumber: 'Sense número usable: cal el prefix del país a la fitxa del proveïdor',
    whatsappQueueLoading: 'Preparant els missatges…',
    whatsappQueueError: 'No s’ha pogut registrar l’enviament d’aquesta comanda.',
    whatsappQueueClose: 'Tanca',
    skip_no_recipients_one: '{{count}} no té cap correu del proveïdor',
    skip_no_recipients_other: '{{count}} no tenen cap correu del proveïdor',
    skip_not_found_one: '{{count}} no s’ha trobat',
    skip_not_found_other: '{{count}} no s’han trobat',
  },

  'backoffice.purchaseTemplates': {
    title: 'Plantilles',
    newTemplate: 'Nova plantilla',
    editTemplate: 'Edita la plantilla',
    empty:
      'Encara no hi ha cap plantilla. Una plantilla desa la comanda que es repeteix —proveïdor, articles, unitats i quantitats— perquè no s\'hagi de tornar a muntar cada vegada.',
    colName: 'Plantilla',
    colNotes: 'Notes',
    name: 'Nom de la plantilla',
    nameHint: 'Com l\'anomeneu entre vosaltres: «Setmanal», «Reposició urgent».',
    nameRequired: 'Posa-li un nom a la plantilla.',
    nameTaken: 'Aquest proveïdor ja té una plantilla amb aquest nom. Posa-li\u2019n un altre.',
    notesHint: 'Viatgen a la comanda que es generi; allà es poden canviar.',
    usualQuantity: 'Quantitat habitual',
    emptyLines: 'Afegeix almenys un article a la plantilla.',
    createOrder: 'Crea comanda',
    saveAsTemplate: 'Desa com a plantilla',
    deleteTitle: 'Dona de baixa la plantilla',
    deleteBody:
      'Deixarà de sortir a la llista i al camp «Plantilla» de Nova comanda. Les comandes que se n\'hagin fet no es toquen.',
  },
  'backoffice.priceHistory': {
    title: 'Històric de preus',
    subtitle: '{{supplier}} · preus per {{unit}}, sense IVA',
    subtitleNoUnit: '{{supplier}} · preus per unitat base, sense IVA',
    intro: 'Cada canvi del preu de la fitxa queda registrat. El que es paga a una comanda concreta no hi surt: es queda al document.',
    loadError: 'No s’ha pogut carregar l’històric de preus.',
    empty: 'Encara no hi ha cap canvi de preu per a aquesta relació.',
    colWhen: 'Data',
    colBefore: 'Preu anterior',
    colAfter: 'Preu nou',
    colChange: 'Canvi',
    colSource: 'Origen',
    colWho: 'Qui',
    kindInitial: 'Primera càrrega',
    kindCleared: 'Preu esborrat',
    sourceManual: 'Fitxa de l’article',
    sourceImport: 'Importació',
    sourcePurchaseOrder: 'Comanda {{number}}',
    sourcePurchaseOrderUnknown: 'Comanda',
    sourceLegacy: 'Anterior a l’històric',
    system: 'Sistema',
    close: 'Tanca',
  },
  'backoffice.purchaseOrderDetail': {
    newTitle: 'Nova comanda de compra',
    editTitle: 'Comanda {{number}}',
    supplier: 'Proveïdor',
    selectSupplier: 'Selecciona un proveïdor',
    orderDate: 'Data de comanda',
    expectedDate: 'Data d\'entrega prevista',
    deliveryDaysHint: 'Reparteix {{days}}',
    template: 'Plantilla',
    templatePlaceholder: 'Parteix d’una plantilla (opcional)',
    templateHint:
      'Omple el proveïdor, els articles, les unitats i les quantitats. Tot es pot canviar abans de desar.',
    noTemplateMatches: 'Cap plantilla que hi coincideixi',
    templateSupplierInactive: 'El proveïdor d’aquesta plantilla està donat de baixa i no s’hi poden fer comandes noves. Reactiva’l o canvia’n el proveïdor a la plantilla.',
    replaceTemplateTitle: 'Substituir per la plantilla?',
    replaceTemplateBody:
      'La comanda passarà a ser la de la plantilla «{{name}}»: proveïdor, articles, quantitats i notes. El que hi ha ara es perdrà.',
    replaceTemplateConfirm: 'Substitueix',
    deliveryNotUsualDay:
      'No és dia de repartiment d’aquest proveïdor (reparteix {{days}}). Es pot desar igualment.',
    deliveryBeforeOrder: 'L’entrega no pot ser anterior a la data de comanda.',
    notes: 'Notes',
    linesTitle: 'Línies',
    addLine: 'Afegeix article',
    article: 'Article',
    selectArticle: 'Selecciona un article',
    orderedQuantity: 'Quantitat',
    purchaseUnit: 'Unitat de compra',
    baseUnit: 'Unitat base',
    agreedPrice: 'Preu pactat',
    agreedPriceUnit: 'Preu (\u20ac/{{unit}})',
    pricePerUnit: '{{price}} \u20ac/{{unit}}',
    lineAmount: '= {{amount}} \u20ac',
    priceNeedsConversion: 'Cal la conversió de l’envàs per informar el preu per unitat base.',
    colPrice: 'Preu pactat',
    priceCatalog: 'Vigent al catàleg: {{price}} €/{{unit}}',
    priceCatalogNone: 'Sense preu vigent al catàleg',
    updateSupplierPrice: 'Actualitza el preu del proveïdor',
    updatePriceTitle: 'Actualitza el preu del proveïdor',
    updatePriceBody: '«{{article}}» a «{{supplier}}»: el preu vigent passarà de {{from}} a {{to}} €/{{unit}}. El canvi quedarà a l’històric de preus amb aquesta comanda com a origen. Les comandes ja fetes no canvien.',
    updatePriceBodyFirst: '«{{article}}» a «{{supplier}}» encara no té preu vigent: passarà a {{to}} €/{{unit}}. El canvi quedarà a l’històric de preus amb aquesta comanda com a origen. Les comandes ja fetes no canvien.',
    updatePriceConfirm: 'Actualitza el preu',
    updatePriceError: 'No s’ha pogut actualitzar el preu.',
    updatePriceDone: 'Preu del proveïdor actualitzat. Consta a l’històric de preus de l’article.',
    priceMissingHint: 'Sense preu pactat al catàleg per a aquest proveïdor: indica’l per poder valorar la línia.',
    lineQuantityTimes: '{{quantity}} \u00d7 {{unit}}',
    lineEquivalenceShort: '{{quantity}} {{pack}} \u00d7 {{factor}} {{base}}',
    lineNotes: 'Nota de la l\u00ednia',
    lineNotesHint: 'Opcional, nom\u00e9s per a aquest article. La nota de la comanda va a dalt.',
    unknownQuantityHint:
      'No es pot donar per entregada: hi ha línies amb un format sense conversió configurada i no se sap quant se n\'ha demanat. Configura la conversió a la fitxa de l\'article, a Unitats i conversions, o finalitza la comanda deixant el que falta sense rebre.',
    unknownQuantityBanner:
      'Hi ha línies que no es podran comparar amb el que arribi: el seu format no té conversió configurada per a l\'article. Es mostren en la unitat de compra.',
    pickSupplierFirst: 'Tria primer el proveïdor',
    searchArticle: 'Cerca per codi o descripció',
    noArticleMatches: 'Cap article que hi coincideixi',
    noSupplierMatches: 'Cap proveïdor que hi coincideixi',
    supplierHasNoArticles:
      'Aquest proveïdor no té cap article vinculat. Vincula-l\'hi des de la fitxa de l\'article, a Proveïdors.',
    articleUnlinked: 'sense relació amb aquest proveïdor',
    droppedArticles_one:
      'Ha sortit de la comanda perquè aquest proveïdor no el serveix: {{articles}}.',
    droppedArticles_other:
      'Han sortit de la comanda perquè aquest proveïdor no els serveix: {{articles}}.',
    supplierArticlesUnavailable:
      'No s\'ha pogut llegir quins articles serveix aquest proveïdor. Torna-ho a provar; mentrestant la llista pot sortir buida.',
    missingConversion:
      'Aquest format no té conversió configurada per a l\'article: la comanda no podrà comparar el que arribi amb el que s\'ha demanat. Configura-la a la fitxa de l\'article, a Unitats i conversions.',
    ordered: 'Demanat',
    received: 'Rebut',
    pending: 'Pendent',
    remove: 'Treu',
    save: 'Desa',
    confirm: 'Confirma comanda',
    confirmHint: 'La comanda passa a «Pendent d\'entrega».',
    closeOrder: 'Finalitza la comanda',
    registerReception: 'Registra la recepció',
    registerReceptionHint:
      'Per a quan el gènere ja ha arribat però no s’ha registrat a planta: escriu el que ha arribat de debò i en sortirà l’albarà.',
    markDelivered: 'Marca com a entregada',
    markDeliveredHint:
      'Ja ha arribat tot: això només ho deixa registrat a la comanda.',
    closeOrderHint:
      'Si el que falta ja no arribarà, finalitza la comanda amb el que s’ha rebut: el pendent queda registrat com a no rebut i deixa de ser pendent. Cal indicar-ne el motiu.',
    closeDialogTitle: 'Finalitza la comanda {{number}}',
    closeDialogBody:
      'Confirmes que no esperes la resta. La comanda passarà a «Entregada» amb el que s’ha rebut: els albarans i les existències no canvien, i el que falta queda registrat com a no rebut amb el teu nom, la data i el motiu.',
    closeDialogPendingTitle: 'Es deixa sense rebre',
    closeDialogUnknown: 'quantitat desconeguda (l’envàs no té conversió)',
    closeError: 'No s’ha pogut finalitzar la comanda. Torna-ho a provar.',
    closeErrorNotOpen:
      'Aquesta comanda ja no es pot finalitzar: potser s’ha servit o cancel·lat mentrestant. Refresca la pàgina.',
    closeErrorNothingPending:
      'No falta res per rebre: la comanda ja es pot marcar com a entregada. Refresca la pàgina.',
    closeErrorNothingReceived:
      'No s’ha rebut res d’aquesta comanda, i no es pot finalitzar. Si no ha d’arribar, cancel·la-la.',
    closedTitle: 'Finalitzada amb pendent de rebre',
    closedMeta: '{{user}} · {{date}}',
    closedReason: 'Motiu',
    closedLinesTitle: 'Es va deixar sense rebre',
    closedLine: '{{article}}: {{pending}} de {{ordered}}',
    closedLineUnknown: '{{article}}: quantitat desconeguda',
    nothingReceivedHint:
      'Encara no s’ha rebut res d’aquesta comanda. Una comanda no es pot donar per entregada sense quantitats rebudes: registra’n primer la recepció.',
    receptionsTitle: 'Albarans de compra',
    noReceptions: 'Cap recepció registrada encara.',
    emptyLines: 'Afegeix almenys una línia a la comanda.',
    lowStockWarning: 'Existències sota mínim per a alguns articles (avís, no bloqueja).',
    pdfTitle: 'Comanda de compra',
    pdfSupplierRef: 'Ref. proveïdor',
    pdfError: 'No s’ha pogut generar el PDF.',
    emailDefaultSubject: 'Comanda {{comanda}} · {{obrador}}',
    sendingTitle: 'Enviament al proveïdor',
    sendingDraftHint: 'Confirma la comanda per poder-la enviar i descarregar-ne el PDF.',
    sendingPending: 'Pendent d’enviar',
    sendingSent: 'Enviada',
    sendingWillEmail: 'S’enviarà per correu a {{recipients}}.',
    sendingManualChannel: 'Aquest proveïdor rep les comandes per altres mitjans: marca-la com a enviada quan l’hi hagis fet arribar.',
    sendingNoRecipients: 'Aquest proveïdor no té cap correu de comandes. Afegeix-lo a la seva fitxa (Configuració › Proveïdors), escriu-ne un en enviar-la o marca-la com a enviada. El correu general no s’hi fa servir.',
    sendingLastEmail: 'Enviada per correu el {{date}} a {{recipients}}.',
    sendingLastManual: 'Marcada com a enviada el {{date}}.',
    sendingWhatsappReady: 'Aquest proveïdor rep les comandes per WhatsApp al {{phone}}. «Envia per WhatsApp» obre WhatsApp amb la comanda escrita i l’envies tu: Obrava no pot saber si el missatge ha sortit ni si ha arribat, així que ho confirmes tu.',
    sendingWhatsappNoPhone: 'Aquest proveïdor rep les comandes per WhatsApp però no té cap número a la fitxa. Afegeix-lo a Tercers · Proveïdors, o marca la comanda com a enviada quan l’hi hagis fet arribar.',
    sendingWhatsappNoPrefix: 'El número de WhatsApp d’aquest proveïdor («{{phone}}») no es pot obrir: cal el prefix del país («+34…»). Corregeix-lo a la fitxa, o marca la comanda com a enviada quan l’hi hagis fet arribar.',
    sendWhatsappAction: 'Envia per WhatsApp',
    resendWhatsappAction: 'Reenvia per WhatsApp',
    whatsappDialogTitle: 'WhatsApp · comanda {{number}}',
    whatsappDialogBody: 'S’ha obert WhatsApp amb la comanda escrita, per al número {{phone}}. WhatsApp no ens diu si has enviat el missatge ni si ha arribat: quan l’hagis enviat, confirma-ho aquí i en quedarà constància a la comanda.',
    whatsappDialogNumber: 'Número de WhatsApp: +{{number}}',
    whatsappDialogOmitted_one: 'Una línia no hi cabia: el missatge diu que n’hi ha més. Completa-ho per un altre mitjà (p. ex. el PDF per correu).',
    whatsappDialogOmitted_other: '{{count}} línies no hi cabien: el missatge diu que n’hi ha més. Completa-ho per un altre mitjà (p. ex. el PDF per correu).',
    whatsappPreview: 'El missatge',
    whatsappReopen: 'Torna a obrir WhatsApp',
    whatsappConfirm: 'Ja l’he enviat',
    whatsappDefaultTemplate: 'Hola, us enviem la comanda {{comanda}} de {{obrador}}.\n\n{{linies}}\n\nEntrega prevista: {{entrega}}\n\nGràcies!',
    whatsappDefaultTemplateNoDelivery: 'Hola, us enviem la comanda {{comanda}} de {{obrador}}.\n\n{{linies}}\n\nGràcies!',
    whatsappLineRef: 'ref. {{ref}}',
    whatsappReviewBody: 'Això és el missatge que s’obrirà a WhatsApp, per al número {{phone}}. El pots retocar a WhatsApp abans d’enviar-lo; el text que hi quedi escrit és el que enviïs, no el d’aquí.',
    whatsappOpenAction: 'Obre WhatsApp',
    whatsappTooLong: 'El text és massa llarg per a l’enllaç de WhatsApp i potser no s’obrirà sencer. Escurça la plantilla de WhatsApp del proveïdor.',
    historyMessage: 'Veure el missatge',
    whatsappNotes: 'Notes: {{notes}}',
    whatsappMoreLines: '… i {{count}} línies més',
    sendingLastWhatsapp: 'Marcada com a enviada per WhatsApp el {{date}} (al {{phone}}). WhatsApp no confirma l’entrega.',
    resultWhatsapp: 'Marcada com a enviada · sense confirmació d’entrega',
    sendSkippedNoPhone: 'El proveïdor no té cap número de WhatsApp a la fitxa.',
    sendSkippedWhatsapp: 'Aquest proveïdor rep les comandes per WhatsApp: no s’envia des d’aquí. Obre WhatsApp des de la comanda.',
    sendingLegacy: 'Consta com a enviada el {{date}}.',
    sendAction: 'Envia al proveïdor',
    sendByEmailAction: 'Envia per correu',
    resendAction: 'Reenvia',
    markSentAction: 'Marca com a enviada',
    downloadPdf: 'Descarrega el PDF',
    sendDialogTitle: 'Envia la comanda {{number}}',
    resendDialogTitle: 'Reenvia la comanda {{number}}',
    sendTo: 'Per a',
    sendToHint: 'Separa les adreces amb comes. Si en canvies alguna, només val per a aquest enviament.',
    sendToInvalid: 'Hi ha adreces que no són vàlides: {{list}}',
    sendToEmpty: 'Posa almenys una adreça.',
    sendSubject: 'Assumpte',
    sendAttachPdf: 'Adjunta el PDF de la comanda',
    sendConfirm: 'Envia',
    resendConfirm: 'Reenvia',
    markSentTitle: 'Marca com a enviada',
    markSentBody: 'Quedarà constància que la comanda {{number}} s’ha fet arribar al proveïdor per altres mitjans, amb la data i l’hora d’ara. No s’envia cap correu.',
    sendResultSent: 'Comanda enviada a {{recipients}}.',
    sendResultMarked: 'Comanda marcada com a enviada.',
    sendResultFailed: 'El correu no ha sortit. Torna-ho a provar; el motiu és a l’historial d’enviaments.',
    sendSkipped: 'Aquesta comanda no es pot enviar ara.',
    sendSkippedNoRecipients: 'El proveïdor no té cap correu de comandes on enviar-la.',
    sendError: 'No s’ha pogut enviar la comanda. Torna-ho a provar.',
    confirmAndSend: 'Confirma i envia',
    autoSentBanner: 'Comanda confirmada i enviada a {{recipients}}.',
    autoSendFailed: 'Comanda confirmada, però no s’ha pogut enviar. Envia-la des d’«Enviament al proveïdor».',
    historyTitle: 'Historial d’enviaments',
    historyWhen: 'Quan',
    historyWho: 'Qui',
    historyChannel: 'Canal',
    historyTo: 'A qui',
    historyResult: 'Resultat',
    channelEmail: 'Correu',
    channelManual: 'Manual',
    channelWhatsapp: 'WhatsApp',
    resultSent: 'Enviada',
    resultMarked: 'Marcada',
    resultFailed: 'Ha fallat',
  },

  'backoffice.receptions': {
    title: 'Albarans de compra',
    receptionNumber: 'Núm. albarà',
    deliveryNote: 'Albarà proveïdor',
    supplier: 'Proveïdor',
    date: 'Data',
    order: 'Comanda',
    lines: 'Línies',
    invoiced: 'Facturat',
    notInvoiced: 'Pendent de facturar',
    markInvoiced: 'Marca com a facturat',
    markNotInvoiced: 'Desmarca facturat',
    invoiceNumber: 'Núm. factura proveïdor',
    invoiceNumberPrompt: 'Introdueix el número de factura del proveïdor.',
    downloadPdf: 'Descarrega PDF',
    empty: 'Encara no hi ha albarans de compra',
    searchLabel: 'Cerca per albarà, proveïdor o factura',
    emptyFiltered: 'Cap albarà coincideix amb la cerca.',
    searchPlaceholder: 'Cerca per núm. d\'albarà del proveïdor…',
    documentTitle: 'Albarà de compra',
    article: 'Article',
    supplierLot: 'Lot proveïdor',
    internalLot: 'Lot intern',
    expiry: 'Caducitat',
    quantity: 'Quantitat',
    notesLabel: 'Notes',
    exceptionPending: 'Excepció pendent',
    control: 'Control',
    corrected: 'Rectificat',
    cancelled: 'Anul·lat',
    correctedNote: 'Rectificat el {{date}}: {{reason}}',
    wasQuantity: 'abans {{quantity}}',
  },

  // Registre manual d'una recepció des del BackOffice (OBR-77): el gènere ha arribat i ningú no l'ha registrat a planta.
  'backoffice.receptionRegister': {
    title: 'Registra la recepció',
    subtitle: '{{order}} · {{supplier}}',
    intro:
      'Per a quan el gènere ja ha arribat però no s’ha registrat a planta. Escriu el que ha arribat de debò: res no es dona per rebut automàticament, i les existències pugen només amb el que hi posis.',
    notOpen:
      'Aquesta comanda no espera gènere: només es pot registrar una recepció d’una comanda pendent d’entrega o entregada parcialment.',
    existing_one: 'Aquesta comanda ja té {{count}} albarà: {{list}}. Comprova que no sigui la mateixa entrega.',
    existing_other: 'Aquesta comanda ja té {{count}} albarans: {{list}}. Comprova que no sigui la mateixa entrega.',
    existingItem: '{{number}} ({{date}}, albarà {{note}})',
    unsyncedWarning:
      'Si un aparell de planta té una recepció d’aquesta comanda pendent de sincronitzar, aquí no es veu: comprova-ho abans de registrar-la, o l’entrega es duplicaria.',
    headerTitle: 'L’albarà',
    deliveryNote: 'Núm. d’albarà del proveïdor',
    deliveryNoteHint: 'Obligatori: el número que porta imprès l’albarà del proveïdor.',
    sameNumber: 'Ja hi ha l’albarà {{number}} d’aquesta comanda amb aquest número.',
    receptionDate: 'Data de recepció',
    receptionDateHint: 'El dia que va arribar el gènere. La caducitat estimada se’n calcula.',
    dateInvalid: 'Posa una data real, d’avui o anterior.',
    notes: 'Notes',
    notesHint: 'Opcional.',
    linesTitle: 'Què ha arribat',
    ordered: 'Demanat',
    receivedSoFar: 'Ja rebut',
    pending: 'Pendent',
    orderedConversion: '{{ordered}} × {{factor}} = {{total}}',
    quantityWithBase: '{{quantity}} ({{base}})',
    entryUnit: 'Unitat de la quantitat',
    entry: 'Entrada {{number}}',
    removeEntry: 'Treu l’entrada',
    addEntry: 'Afegeix una altra entrada',
    quantity: 'Quantitat rebuda',
    quantityIn: 'Quantitat rebuda ({{unit}})',
    equalsBase: '= {{total}}',
    noConversion: 'L’envàs «{{unit}}» no té conversió a la fitxa de l’article. Indica la quantitat en {{base}}.',
    lot: 'Lot del proveïdor (opcional)',
    expiry: 'Caducitat (opcional)',
    expiryEstimated: 'Caducitat estimada',
    expiryEstimatedHint_one: 'Proposta: recepció + {{count}} dia de vida útil. Canvia-la si el proveïdor en indica una de real.',
    expiryEstimatedHint_other: 'Proposta: recepció + {{count}} dies de vida útil. Canvia-la si el proveïdor en indica una de real.',
    control: 'Control de recepció',
    controlNone: 'Sense control',
    controlOk: 'Conforme',
    controlNok: 'No conforme',
    flagException: 'Marca com a excepció',
    unflagException: 'Treu la marca',
    flaggedException: 'Excepció',
    exceptionNote: 'Què passa?',
    exceptionNoteHint: 'Ex.: sacs de 20 kg en lloc de 25',
    exceptionNoteRequired: 'Cal explicar què passa',
    thisReceipt: 'En aquest albarà',
    willRemainPending: 'Quedarà pendent',
    excess: 'Passa {{excess}} del que faltava. Es registrarà com a excepció.',
    confirmLabel: 'Confirmo que aquesta entrega encara no s’ha registrat, ni a l’App ni com a albarà.',
    submit: 'Registra la recepció',
    submitting: 'Registrant…',
    missingList: 'Per registrar-la falta: {{list}}.',
    missing_deliveryNote: 'el número d’albarà',
    missing_date: 'una data de recepció real, d’avui o anterior',
    missing_nothingEntered: 'la quantitat d’almenys una entrada',
    missing_flagNotes: 'la nota de les entrades marcades com a excepció',
    missing_notConfirmed: 'confirmar que l’entrega no s’ha registrat',
    errorGeneric:
      'No s’ha pogut registrar la recepció. Mira a la llista d’albarans de la comanda si ha arribat a quedar registrada abans de tornar-ho a provar.',
    openPicker: 'Registra una recepció',
    pickerTitle: 'Registra una recepció',
    pickerOrder: 'Comanda',
    pickerPlaceholder: 'Tria la comanda que ha arribat',
    pickerHint: 'Només les comandes pendents d’entrega o entregades parcialment.',
    pickerEmpty: 'Cap comanda pendent de rebre',
    pickerConfirm: 'Continua',
    articleMissing:
      'Aquest article ja no és al catàleg: no se’n pot registrar la recepció des d’aquí.',
    errorTenant:
      'L’obrador actiu del teu compte ha canviat (potser en una altra pestanya). No s’ha registrat res: recarrega la pàgina i torna-ho a provar.',
  },

  // Excepcions de recepció (OBR-83): el que ha arribat diferent del que es va demanar.
  'backoffice.receptionExceptions': {
    title: 'Excepcions',
    subtitle:
      'El que ha arribat diferent del que es va demanar. Aquí es revisa i es deixa constància de la decisió.',
    filterLabel: 'Estat',
    filterPending: 'Pendents',
    filterResolved: 'Resoltes',
    filterAll: 'Totes',
    empty: 'No hi ha excepcions pendents de revisar.',
    searchLabel: 'Cerca per article, albarà o proveïdor',
    emptyFiltered: 'Cap excepció coincideix amb la cerca.',
    emptyAll: 'Encara no hi ha cap excepció de recepció.',
    kindOverQuantity: 'Més del demanat',
    kindUnorderedArticle: 'Article que no és a la comanda',
    kindOperatorFlagged: "Marcada per l'operari",
    statusPending: 'Pendent de decisió',
    statusAccepted: 'Acceptada',
    statusRejected: 'Rebutjada',
    deliveryNoteMeta: 'Albarà del proveïdor {{number}}',
    ordered: 'Demanat',
    received: 'Rebut en aquest albarà',
    excess: 'Excés',
    operatorNote: "Nota de l'operari",
    resolutionAccepted: 'Acceptada per {{user}} · {{date}}',
    resolutionRejected: 'Rebutjada per {{user}} · {{date}}',
    resolutionNote: 'Nota',
    accept: 'Accepta',
    reject: 'Rebutja',
    acceptTitle: "Acceptar l'excepció",
    acceptBody:
      'La mercaderia passa a estoc disponible. Queda constància de qui ho ha decidit i quan; la comanda no canvia.',
    acceptNoteLabel: 'Nota (opcional)',
    acceptNotePlaceholder: "Per què s'accepta, si cal explicar-ho",
    rejectTitle: "Rebutjar l'excepció",
    rejectBody:
      'La mercaderia no podrà quedar disponible per a producció: se’n genera una devolució. Tria què se’n fa i explica per què es rebutja.',
    rejectNoteLabel: 'Motiu del rebuig',
    rejectNotePlaceholder: 'Per què es rebutja',
    resolveErrorGone: 'Aquesta excepció ja no existeix. Actualitza la pantalla.',
    resolveErrorAlreadyResolved: 'Algú altre ja ha resolt aquesta excepció.',
    resolveErrorNoteRequired: 'Cal escriure el motiu per rebutjar-la.',
    resolveErrorForbidden: 'No tens permís per resoldre excepcions.',
    resolveError: "No s'ha pogut desar la decisió. Torna-ho a provar.",
    resolutionLabel: 'Què se’n fa de la mercaderia',
    resolutionImmediate: 'Retorn immediat al proveïdor',
    resolutionImmediateHint:
      'El transportista se l’emporta ara. No queda a l’estoc: la devolució es registra com a retornada.',
    resolutionPending: 'Pendent de devolució',
    resolutionPendingHint:
      'Queda a l’obrador, bloquejada (no disponible per a producció), fins que es confirmi el retorn físic.',
    allocationsTitle: 'Lots que es retornen',
    allocationsHint:
      'Cal retornar {{quantity}} {{unit}}. S’ha repartit entre els lots d’aquest albarà, de l’entrada més recent cap enrere; es pot canviar.',
    allocationLot: 'Lot {{lot}} · fins a {{max}} {{unit}}',
    allocationSum: 'Suma: {{sum}} de {{required}} {{unit}}',
    allocationMismatch: 'La suma ha de ser {{required}} {{unit}}.',
    allocationShortfall:
      'Els lots d’aquest albarà no en tenen prou per tornar {{required}} {{unit}}.',
    allocationNoLots: 'No s’han trobat els lots d’aquest albarà.',
    resolutionRejectedImmediate: 'Retorn immediat al proveïdor',
    resolutionRejectedPending: 'Pendent de devolució',
    returnsLink: 'Veure les devolucions',
    blockedNote:
      'Mentre no es decideixi, {{quantity}} {{unit}} no compten com a disponibles per a producció.',
    resolveErrorResolutionRequired: 'Tria què se’n fa de la mercaderia.',
    resolveErrorAllocation: 'El repartiment en lots no quadra amb la quantitat que cal retornar.',
    resolveErrorExceedsLot:
      'No es pot tornar més del que va arribar d’un lot (potser ja hi ha devolucions d’aquest lot).',
    resolveErrorWithoutSupplier: 'L’albarà no té proveïdor: no es pot crear la devolució.',
  },

  // Devolucions de compra (OBR-117): la mercaderia que torna al proveïdor.
  'backoffice.purchaseReturns': {
    title: 'Devolucions',
    subtitle:
      'La mercaderia que torna al proveïdor. Una devolució pendent és encara a l’obrador i no compta com a disponible; en confirmar-ne el retorn surt de les existències.',
    filterStatusLabel: 'Estat',
    filterPending: 'Pendents de retornar',
    filterReturned: 'Retornades',
    filterAll: 'Totes',
    filterSupplierLabel: 'Proveïdor',
    allSuppliers: 'Tots els proveïdors',
    empty: 'No hi ha devolucions pendents de retornar.',
    searchLabel: 'Cerca per article, proveïdor, lot o albarà',
    emptyFiltered: 'Cap devolució coincideix amb la cerca.',
    emptyAll: 'Encara no hi ha cap devolució de compra.',
    loadError: 'No s’han pogut carregar les devolucions.',
    statusPending: 'Pendent de retornar',
    statusReturned: 'Retornada',
    originException: 'Excepció rebutjada',
    originIncident: 'Incidència',
    originReception: 'Albarà',
    supplier: 'Proveïdor',
    reception: 'Albarà',
    purchaseOrder: 'Comanda',
    article: 'Article',
    lot: 'Lot',
    quantity: 'Quantitat',
    reason: 'Motiu',
    observations: 'Observacions',
    createdBy: 'Creada per {{user}} · {{date}}',
    returnedBy: 'Retornada per {{user}} · {{date}}',
    blockedNote: 'Aquesta quantitat és bloquejada: no compta com a disponible fins que es confirmi el retorn.',
    confirmReturn: 'Confirma el retorn',
    confirmTitle: 'Confirmar el retorn',
    confirmBody:
      '{{quantity}} {{unit}} de {{article}} (lot {{lot}}) deixaran de formar part de les existències. Confirma-ho només quan la mercaderia ha sortit de l’obrador.',
    confirm: 'Confirma',
    confirmErrorGone: 'Aquesta devolució ja no existeix. Actualitza la pantalla.',
    confirmErrorAlreadyReturned: 'Aquesta devolució ja està confirmada.',
    confirmErrorForbidden: 'No tens permís per confirmar devolucions.',
    confirmError: 'No s’ha pogut confirmar el retorn. Torna-ho a provar.',
    create: 'Genera una devolució',
    createTitle: 'Nova devolució de compra',
    createBody:
      'D’un lot d’un albarà de compra. Si no la marques com a ja retornada, la mercaderia queda a l’obrador, bloquejada, fins que en confirmis el retorn.',
    lotLabel: 'Lot',
    lotOption: '{{lot}} · fins a {{max}} {{unit}}',
    quantityLabel: 'Quantitat ({{unit}})',
    reasonLabel: 'Motiu (obligatori)',
    reasonPlaceholder: 'Per què es torna',
    observationsLabel: 'Observacions (opcional)',
    alreadyReturned: 'Ja retornada: el transportista se l’ha emportat',
    alreadyReturnedHint: 'La mercaderia surt de les existències ara mateix.',
    createAction: 'Crea la devolució',
    noLots: 'Aquest albarà no té lots per tornar.',
    createErrorExceedsLot:
      'No es pot tornar més del que va arribar d’aquest lot (potser ja hi ha altres devolucions).',
    createErrorReasonRequired: 'Cal escriure el motiu.',
    createErrorWithoutSupplier: 'L’albarà no té proveïdor: no es pot crear la devolució.',
    createErrorForbidden: 'No tens permís per crear devolucions.',
    createError: 'No s’ha pogut crear la devolució. Torna-ho a provar.',
    reasonOfIncident: 'Incidència: {{comment}}',
    plannedDateLabel: 'Data prevista de recollida o retorn',
    plannedDateHint:
      'Quan es preveu que el transportista passi a recollir-ho. L’App de planta la mostra a Devolucions pendents com a d’avui, endarrerida o futura.',
    plannedShort: 'Recollida prevista',
    plannedNone: 'Sense data prevista',
    plannedChipOverdue: 'Endarrerida',
    plannedChipToday: 'Avui',
    returnNumber: 'Albarà de devolució {{number}}',
    downloadNote: 'Descarrega l’albarà (PDF)',
    downloadingNote: 'Generant el PDF…',
    downloadNoteError: 'No s’ha pogut generar l’albarà. Torna-ho a provar.',
    reschedule: 'Canvia la data',
    rescheduleTitle: 'Data prevista de la devolució',
    rescheduleBody:
      'Quan es preveu que el transportista passi a recollir-ho. Pots deixar-la en blanc si encara no se sap.',
    rescheduleAction: 'Desa la data',
    rescheduleErrorGone: 'Aquesta devolució ja no existeix. Actualitza la pantalla.',
    rescheduleErrorAlreadyReturned: 'Aquesta devolució ja ha sortit: la data no es pot canviar.',
    rescheduleErrorForbidden: 'No tens permís per canviar la data de les devolucions.',
    rescheduleError: 'No s’ha pogut desar la data. Torna-ho a provar.',
    exitHint: 'També es pot confirmar des de l’App de planta, a Recepció › Devolucions pendents.',
    noteTitle: 'Albarà de devolució',
    noteNumber: 'Núm.',
    noteDate: 'Data de sortida',
    noteSupplier: 'Proveïdor',
    noteOrigin: 'Document d’origen',
    noteReception: 'Albarà de recepció',
    noteOrder: 'Comanda',
    noteSupplierDeliveryNote: 'Albarà del proveïdor',
    noteArticle: 'Article',
    noteLot: 'Lot',
    noteSupplierLot: 'Lot del proveïdor',
    noteQuantity: 'Quantitat',
    noteReason: 'Motiu',
    noteObservations: 'Observacions',
    noteIssuedBy: 'Sortida confirmada per',
    noteFooter:
      'Aquest document acredita la sortida física de la mercaderia i la devolució al proveïdor. No és una factura rectificativa ni una nota d’abonament.',
    noteSignatureOut: 'Lliurat per',
    noteSignatureCarrier: 'Rebut pel transportista',
  },


  // Detall d'un albarà de recepció i la seva rectificació (OBR-86).
  'backoffice.receptionDetail': {
    title: 'Albarà {{number}}',
    titleFallback: 'Albarà de compra',
    notFound: "No s'ha trobat aquest albarà.",
    returnsTitle: 'Devolucions d’aquest albarà',
    returnsEmpty: 'Aquest albarà no té devolucions.',
    returnsCreate: 'Genera una devolució',
    returnsOpen: 'Veure-les a Devolucions',
    linesTitle: 'Entrades',
    exceptionsTitle: 'Excepcions',
    exceptionsOpen: 'Revisa-les a Excepcions',
    correctionsTitle: 'Rectificacions',
    noCorrections: "Aquest albarà no s'ha rectificat.",
    receivedAs: 'Rebut: {{quantity}}',
    counted: 'Comptat: {{quantity}} {{pack}} \u00d7 {{factor}} {{base}}',
    writtenAs: 'En rebre es va escriure: {{code}}',
    noLotCode: 'Sense lot',
    noExpiry: 'sense data',
    correct: "Corregeix l'albarà",
    correctHint:
      "Per corregir la quantitat, el lot del proveïdor, la caducitat o el número de l'albarà. L'entrada original no es toca: la correcció queda registrada.",
    correctionMeta: '{{user}} · {{date}}',
    correctionReason: 'Motiu',
    quantityChange: '{{before}} → {{after}}',
    lotCodeChange: 'Lot del proveïdor: {{before}} → {{after}}',
    expiryChange: 'Caducitat: {{before}} → {{after}}',
    movement: 'Moviment correctiu: {{delta}}',
    lotBalance: 'Existència del lot després: {{balance}}',
    lotBalanceNegative: "L'existència del lot queda en negatiu: ja s'havia gastat.",
    headerDeliveryNote: 'Albarà del proveïdor: {{from}} → {{to}}',
    headerDate: 'Data: {{from}} → {{to}}',
    orderEffect: 'Comanda {{order}}: {{from}} → {{to}}',
    cancelledNote: "Totes les entrades han quedat a zero: l'albarà s'ha anul·lat.",
    dialogTitle: "Corregir l'albarà {{number}}",
    dialogBody:
      "L'entrada original no es toca: la correcció queda registrada amb el motiu, i les existències es corregeixen amb un moviment amb la diferència.",
    fieldDeliveryNote: 'Albarà del proveïdor',
    fieldDate: "Data de l'albarà",
    fieldQuantity: 'Quantitat ({{unit}})',
    fieldLotCode: 'Lot del proveïdor',
    fieldExpiry: 'Caducitat',
    currentHint: 'Ara: {{quantity}}',
    annulEntry: "Anul·la l'entrada",
    entryTitle: '{{article}} · {{lot}}',
    reasonLabel: 'Motiu de la correcció',
    reasonPlaceholder: 'Què ha passat i per què es corregeix',
    impactTitle: 'Què passarà',
    impactNothing: 'Encara no hi ha cap canvi.',
    impactLine: '{{article}}: {{before}} → {{after}} ({{delta}})',
    impactNegative: "El lot {{lot}} ja s'ha gastat: la seva existència quedarà en {{balance}}.",
    impactCancelled: "Totes les entrades queden a zero: l'albarà quedarà anul·lat.",
    impactOrder: 'La comanda {{order}} passarà de «{{from}}» a «{{to}}».',
    impactOrderClosed: 'La comanda {{order}} es va tancar a mà amb motiu: no es reobre.',
    confirm: 'Confirma la correcció',
    invalidQuantity: 'Escriu una quantitat vàlida (zero o més).',
    invalidDate: 'Escriu una data vàlida.',
    errorReasonRequired: 'Cal indicar el motiu de la correcció.',
    errorNotFound: 'Aquest albarà ja no existeix. Actualitza la pantalla.',
    errorNotConfirmed: "Aquest albarà ja no es pot corregir (està anul·lat o no és finalitzat).",
    errorLotNotFound: "Una de les entrades ja no és d'aquest albarà. Actualitza la pantalla.",
    errorNothingToChange: 'No hi ha cap canvi per desar.',
    errorBadQuantity: 'Una de les quantitats no és vàlida.',
    errorBadHeader: "El número o la data de l'albarà no són vàlids.",
    errorForbidden: 'No tens permís per corregir albarans.',
    errorGeneric: "No s'ha pogut desar la correcció. Torna-ho a provar.",
  },

  'backoffice.purchaseOrderStatus': {
    in_progress: 'En curs',
    pending_dispatch: 'Pendent d\'enviar',
    awaiting_delivery: 'Pendent d\'entrega',
    receiving: 'Entregada parcialment',
    delivered: 'Entregada',
    cancelled: 'Cancel·lada',
  },

  'backoffice.receptionControlResult': {
    conforme: 'Conforme',
    no_conforme: 'No conforme',
  },

  'backoffice.salesOrders': {
    articleTitle: 'Vendes — {{article}}',
    articleCard: 'Fitxa de l’article',
    allOrders: 'Totes les comandes',
    articleEmpty: 'D’aquest article no se n’ha venut mai: cap comanda ni cap albarà de venda.',
    articleLinesTitle: 'Línies de comanda',
    articleNoLines: 'Aquest article no apareix en cap comanda de venda.',
    articleShipmentsTitle: 'Expedicions',
    articleNoShipments: 'Aquest article no s’ha expedit en cap albarà.',
    totalOrdered: 'Demanat',
    totalShipped: 'Servit',
    totalPending: 'Pendent de servir',
    title: 'Comandes de venda',
    newOrder: 'Nova comanda',
    orderNumber: 'Núm.',
    client: 'Client',
    orderDate: 'Data',
    deliveryDate: 'Entrega',
    status: 'Estat',
    lines: 'Línies',
    empty: 'Encara no hi ha comandes de venda',
    searchLabel: 'Cerca per número o client',
    emptyFiltered: 'Cap comanda coincideix amb la cerca.',
    searchPlaceholder: 'Cerca per número o client…',
  },

  'backoffice.salesOrderDetail': {
    newTitle: 'Nova comanda de venda',
    editTitle: 'Comanda {{number}}',
    client: 'Client',
    selectClient: 'Selecciona un client',
    orderDate: 'Data de comanda',
    deliveryDate: 'Data d’entrega',
    deliveryAddress: 'Adreça d’entrega',
    deliveryAddressHint: 'Si es deixa buida, s’usa l’adreça del client.',
    deliveryPoint: 'Punt d’entrega',
    deliveryPointFiscal: 'Adreça fiscal del client',
    deliveryPointCustom: 'Una altra adreça',
    notes: 'Notes',
    linesTitle: 'Línies',
    addLine: 'Afegeix article',
    article: 'Article',
    selectArticle: 'Selecciona un article',
    orderedQuantity: 'Quantitat',
    salesUnit: 'Unitat de venda',
    baseUnit: 'Unitat base',
    unitPrice: 'Preu (opcional)',
    ordered: 'Demanat',
    shipped: 'Servit',
    pending: 'Pendent',
    remove: 'Treu',
    save: 'Desa',
    confirm: 'Confirma comanda',
    confirmHint: 'La comanda passa a «Confirmada», pendent de preparar.',
    closeOrder: 'Tanca comanda',
    closeOrderHint: 'Tanca la comanda deixant el romanent sense servir.',
    cancelOrder: 'Cancel·la comanda',
    shipmentsTitle: 'Albarans de venda',
    noShipments: 'Cap expedició registrada encara.',
    emptyLines: 'Afegeix almenys una línia a la comanda.',
    availableStock: 'Disponible',
    committedStock: 'Compromès',
    insufficientStockWarning:
      'Existències insuficients per a alguns articles (avís, no bloqueja).',
  },

  'backoffice.shipments': {
    title: 'Albarans de venda',
    shipmentNumber: 'Núm. albarà',
    client: 'Client',
    date: 'Data',
    order: 'Comanda',
    lines: 'Línies',
    downloadPdf: 'Descarrega PDF',
    cancel: 'Cancel·la expedició',
    cancelPrompt:
      'Motiu de la cancel·lació (obligatori). Es generaran moviments contraris d’estoc.',
    empty: 'Encara no hi ha albarans de venda',
    status: 'Estat',
    searchPlaceholder: 'Cerca per número o client…',
    documentTitle: 'Albarà de venda',
    article: 'Article',
    lot: 'Lot',
    expiry: 'Caducitat',
    quantity: 'Quantitat',
    unitPrice: 'Preu',
    amount: 'Import',
    notesLabel: 'Notes',
  },

  'backoffice.salesOrderStatus': {
    in_progress: 'En curs',
    confirmed: 'Confirmada',
    preparing: 'En preparació',
    shipped: 'Expedida',
    cancelled: 'Cancel·lada',
    partially_shipped: 'Servida parcialment',
  },

  'backoffice.shipmentStatus': {
    confirmed: 'Confirmada',
    cancelled: 'Cancel·lada',
  },

  'backoffice.technicalSheet': {
    title: 'Fitxa tècnica',
    generate: 'Genera FT',
    legalDenomination: 'Denominació legal',
    ingredientsText: 'Ingredients',
    allergensText: 'Al·lèrgens',
    approve: 'Aprova',
    netWeight: 'Pes net',
    conservation: 'Conservació',
  },

  'backoffice.labels': {
    title: 'Etiquetes',
    template: 'Plantilla',
    print: 'Imprimeix',
    internalLot: 'Lot intern',
    generatedOnClose: 'Generada en tancar l’OT',
  },

  'backoffice.smartImporter': {
    title: 'Importació',
    subtitle:
      'Porta les dades del teu full de càlcul a l’obrador: puja, mapa, revisa i confirma',
    back: 'Enrere',
    // Step indicator
    stepUpload: 'Puja',
    stepMap: 'Mapa',
    stepPreview: 'Revisa',
    stepResult: 'Resultat',
    // Upload step
    uploadFileTitle: 'Puja un fitxer',
    uploadFileHint:
      'Tria un fitxer CSV, XLS o XLSX exportat del teu full de càlcul. La primera fila ha de ser la capçalera.',
    chooseFile: 'Tria un fitxer',
    // Templates
    templatesTitle: 'Plantilles',
    templatesHint:
      'Descarrega un full amb els noms de columna que l’importador reconeix i una pestanya «Instruccions» que explica què va a cada una. Comença de zero, o baixa’t el catàleg actual, edita’l i torna’l a pujar.',
    downloadTemplateBlank: 'Plantilla buida',
    downloadTemplateCatalog: 'Plantilla amb les dades actuals ({{count}})',
    downloadTemplateCatalogLoading: 'Carregant el catàleg…',
    downloadTemplateCatalogHint:
      'La plantilla amb dades porta els articles actius del catàleg, ja emplenats. Les files que no toquis tornaran com a «sense canvis» i no escriuran res.',
    templateFilename: 'plantilla-articles.xlsx',
    templateCatalogFilename: 'articles-obrador.xlsx',
    templateSheetName: 'Articles',
    templateExampleName: 'Farina de blat',
    templateExamplePackaging: 'Sac=25; Palet=1000',
    booleanTrue: 'Sí',
    booleanFalse: 'No',
    // Column guide (OBR-9): the template's instructions sheet + the in-app guide
    guideSheetName: 'Instruccions',
    guideTitle: 'Com s’omple la plantilla d’articles',
    guideShow: 'Què va a cada columna',
    guideHide: 'Amaga la guia de columnes',
    guideRequiredChip: 'Obligatòria',
    guideColColumn: 'Columna',
    guideColRequired: 'Obligatòria',
    guideColDescription: 'Què és',
    guideColFormat: 'Format',
    guideColBlank: 'Si es deixa buida',
    guideColValues: 'Valors acceptats',
    guideRuleSheet:
      'Una fila per article al full «{{sheet}}», que ha de continuar sent el primer: és l’únic que llegeix Obrava. No canviïs el nom de les columnes; les que no et calguin, les pots esborrar.',
    guideRuleRequired:
      'Només són obligatòries {{fields}}. Si en falta alguna, la fila no s’importa.',
    guideRuleExisting:
      'Si el codi ja és al catàleg, la fila actualitza aquell article (o el deixa com està, si ho tries a la revisió). Una cel·la buida no esborra mai cap dada.',
    guideRuleNumbers: 'Els números, sense punts de milers i amb coma als decimals: 1,5.',
    guideRuleReview:
      'Abans d’escriure res, Obrava ensenya què farà amb cada fila, i les que tenen un error, amb el número de fila i el motiu.',
    guideValuesAny: '—',
    guideValuesNone: 'Encara no n’hi ha cap.',
    guideValuesYesNo: 'Sí i No; també 1 i 0, i una X per dir sí.',
    guideValuesComposition:
      'Qualsevol ingredient; els que no són a Configuració · Ingredients es creen en importar.',
    guideDesc_sku:
      'El codi de l’article a l’obrador. Si ja existeix, la fila actualitza aquell article.',
    guideFormat_sku: 'Text. No es pot repetir dins el full.',
    guideBlank_sku: 'La fila no s’importa.',
    guideDesc_name: 'El nom de l’article, tal com el veurà tothom a Obrava.',
    guideFormat_name: 'Text.',
    guideBlank_name: 'La fila no s’importa.',
    guideDesc_article_type: 'Quin tipus d’article és.',
    guideFormat_article_type:
      'Un valor de la llista, sencer o abreujat. Amb un altre valor, la fila no s’importa.',
    guideBlank_article_type: 'Matèria primera.',
    guideDesc_base_unit: 'La unitat en què es compten les existències de l’article.',
    guideFormat_base_unit:
      'El codi d’una unitat de Configuració · Unitats. Amb un altre, la fila no s’importa.',
    guideBlank_base_unit: 'La fila no s’importa.',
    guideDesc_recipe_unit:
      'La unitat en què la recepta demana aquesta matèria primera, quan no és la unitat base (ous: base Un, recepta kg).',
    guideFormat_recipe_unit:
      'El codi d’una unitat de Configuració · Unitats. Només per a matèries primeres. Amb un codi desconegut, la fila no s’importa; igual que la unitat base vol dir que la recepta ja hi va.',
    guideBlank_recipe_unit: 'La recepta va en la unitat base.',
    guideDesc_recipe_unit_factor:
      'Quantes unitats de recepta equivalen a UNA unitat base: 1 Un = 0,06 kg s’escriu 0,06.',
    guideFormat_recipe_unit_factor:
      'Un número més gran que zero, amb coma o punt. Obligatori si la unitat de recepta és diferent de la base; sense unitat de recepta no es pot posar.',
    guideBlank_recipe_unit_factor: 'Sense equivalència: la recepta va en la unitat base.',
    guideDesc_category: 'La categoria del catàleg on va l’article.',
    guideFormat_category:
      'El nom d’una categoria de Configuració · Categories. Si no hi és, s’avisa i l’article queda sense categoria.',
    guideBlank_category: 'Sense categoria.',
    guideDesc_barcode: 'El codi de barres de l’article (EAN), si en té.',
    guideFormat_barcode: 'Les xifres tal com surten a l’etiqueta, com a text o com a número.',
    guideBlank_barcode: 'Sense codi de barres.',
    guideDesc_shelf_life_days: 'Quants dies dura l’article. Proposa la caducitat dels lots.',
    guideFormat_shelf_life_days: 'Nombre enter de dies, 0 o més.',
    guideBlank_shelf_life_days: 'Sense vida útil.',
    guideDesc_min_stock: 'Per sota d’aquesta quantitat, Obrava avisa que en queda poc.',
    guideFormat_min_stock: 'Número en la unitat base, 0 o més.',
    guideBlank_min_stock: 'Sense mínim.',
    guideDesc_max_stock: 'El màxim d’existències que vols tenir de l’article.',
    guideFormat_max_stock: 'Número en la unitat base, 0 o més.',
    guideBlank_max_stock: 'Sense màxim.',
    guideDesc_tax: 'L’impost que s’aplica a l’article.',
    guideFormat_tax:
      'El nom o el tipus d’un impost de Configuració · Impostos. Amb un altre, la fila no s’importa.',
    guideBlank_tax: 'Sense impost; es pot triar després a la fitxa.',
    guideDesc_purchase_price: 'El preu de compra, per unitat base.',
    guideFormat_purchase_price: 'Número, 0 o més.',
    guideBlank_purchase_price: 'Sense preu de compra.',
    guideDesc_sale_price:
      'El preu de venda, per unitat base. Només es fa servir si l’article es ven.',
    guideFormat_sale_price: 'Número, 0 o més.',
    guideBlank_sale_price: 'Sense preu de venda.',
    guideDesc_is_purchased: 'Si l’article es compra a un proveïdor.',
    guideFormat_is_purchased: 'Sí o No.',
    guideBlank_is_purchased: 'No.',
    guideDesc_is_manufactured: 'Si l’article es fa a l’obrador amb una recepta.',
    guideFormat_is_manufactured: 'Sí o No.',
    guideBlank_is_manufactured: 'No.',
    guideDesc_is_sold: 'Si l’article es ven a clients.',
    guideFormat_is_sold: 'Sí o No.',
    guideBlank_is_sold: 'No.',
    guideDesc_is_variable_weight:
      'Si l’article no té una equivalència fixa i es pesa a cada moviment.',
    guideFormat_is_variable_weight: 'Sí o No.',
    guideBlank_is_variable_weight: 'No.',
    guideDesc_default_zone: 'La zona del magatzem on queda l’article quan es rep o es produeix.',
    guideFormat_default_zone:
      'El codi d’una zona de Configuració · Estructura de l’obrador. Amb un altre, la fila no s’importa.',
    guideBlank_default_zone: 'Sense zona per defecte.',
    guideDesc_packaging_units:
      'Com ve embalat l’article: quantes unitats base porta cada embalatge.',
    guideFormat_packaging_units:
      'Embalatge=quantitat, separats per punt i coma: Sac=25; Palet=1000. Afegeix o actualitza; no n’esborra cap.',
    guideBlank_packaging_units: 'Sense embalatges.',
    guideDesc_composition: 'Els ingredients que declara l’etiqueta, de més a menys quantitat.',
    guideFormat_composition:
      'Ingredients en ordre, separats per punt i coma; el % és opcional: Farina=62; Aigua=30; Sal. Substitueix la llista sencera.',
    guideBlank_composition: 'Sense composició.',
    pasteTitle: 'O enganxa les dades',
    pasteLabel: 'Enganxa CSV o TSV',
    pasteHint:
      'Copia les cel·les des d’Excel o Numbers i enganxa-les aquí. La primera fila ha de ser la capçalera.',
    parsePaste: 'Continua',
    noRows: 'No s’ha trobat cap fila de dades. Comprova que hi ha una capçalera i almenys una fila.',
    fileReadError: 'No s’ha pogut llegir el fitxer',
    // Map step
    mapTitle: 'Mapa les columnes',
    mapHint:
      'Indica a quin camp correspon cada columna. Hem proposat un mapatge a partir de les capçaleres i d’importacions anteriors.',
    mapToLabel: 'Camp de destí',
    unmapped: 'No mapar',
    sampleValue: 'Exemple: {{value}}',
    mapMissingRequired: 'Falten camps obligatoris: {{fields}}',
    toPreview: 'Revisa les files',
    checkingCatalog: 'Comprovant el catàleg…',
    // Mappable target fields
    field_sku: 'Codi (SKU)',
    field_name: 'Nom',
    field_article_type: 'Tipologia',
    field_base_unit: 'Unitat base',
    field_recipe_unit: 'Unitat de recepta',
    field_recipe_unit_factor: 'Equivalència de recepta',
    field_category: 'Categoria',
    field_barcode: 'Codi de barres',
    field_shelf_life_days: 'Vida útil (dies)',
    field_min_stock: 'Existències mínimes',
    field_max_stock: 'Existències màximes',
    field_vat_rate: 'IVA (%)',
    field_tax: 'Impost (IVA)',
    field_purchase_price: 'Preu de compra',
    field_sale_price: 'Preu de venda',
    field_is_purchased: 'Es compra',
    field_is_manufactured: 'Es fabrica',
    field_is_sold: 'Es ven',
    field_is_variable_conversion: 'Conversió variable',
    field_default_zone: 'Zona per defecte (codi)',
    field_packaging_units: 'Unitats d’embalatge',
    field_composition: 'Composició declarada',
    // Validation messages (per row)
    msgRequiredMissing: 'Falta el camp obligatori «{{field}}»',
    msgUnknownTax:
      'L’impost «{{value}}» no existeix. Fes servir el nom o el tipus d’un impost de Configuració · Impostos.',
    msgUnknownUnit: 'Unitat desconeguda: «{{code}}»',
    msgRecipeUnitFactorMissing:
      'Falta l’equivalència de recepta: la unitat de recepta «{{code}}» és diferent de la base. Indica quantes unitats de recepta equivalen a una unitat base.',
    msgRecipeUnitFactorInvalid:
      'L’equivalència de recepta «{{value}}» ha de ser un número més gran que zero.',
    msgRecipeFactorWithoutUnit:
      'S’ha indicat una equivalència de recepta sense unitat de recepta: no es pot aplicar.',
    msgRecipeUnitNotRawMaterial:
      'La unitat de recepta «{{code}}» només és per a matèries primeres: s’ometrà en aquest article.',
    msgUnknownCategory: 'Categoria desconeguda «{{name}}»; s’ometrà',
    msgUnknownZone: 'La zona «{{code}}» no existeix. Fes servir el codi d’una zona del magatzem.',
    msgSalePriceNotSold:
      'Preu de venda «{{value}}» en un article marcat com a no venible: es desarà, però la fitxa no l’ensenyarà.',
    msgUnknownPackagingUnit:
      'La unitat d’embalatge «{{name}}» no existeix al mestre; crea-la a Configuració abans d’importar.',
    msgPackaging_syntax:
      'La {{position}}a unitat d’embalatge («{{part}}») no té la forma «Unitat=factor».',
    msgPackaging_factor:
      'La {{position}}a unitat d’embalatge («{{part}}») no té un factor numèric més gran que zero.',
    msgPackaging_duplicate:
      'La {{position}}a unitat d’embalatge («{{part}}») repeteix una unitat: un article només pot tenir-ne una mida.',
    msgComposition_syntax:
      'El {{position}}è ingredient («{{part}}») no té la forma «Ingredient» o «Ingredient=%».',
    msgComposition_percentage:
      'El {{position}}è ingredient («{{part}}») no té un percentatge numèric entre 0 i 100.',
    msgComposition_duplicate:
      'El {{position}}è ingredient («{{part}}») ja surt abans a la llista: un article no pot declarar-lo dues vegades.',
    msgNewIngredients_one: 'Es crearà l’ingredient «{{names}}» al mestre.',
    msgNewIngredients_other: 'Es crearan {{count}} ingredients nous al mestre: {{names}}.',
    msgCompositionDerived:
      'Aquest article treu els ingredients de la recepta, no de la fitxa: la composició es desarà, però la fitxa tècnica no la farà servir.',
    msgCompositionReplaces:
      'Substituirà els {{before}} ingredients que l’article té ara pels {{after}} del full: la cel·la val per la llista sencera, perquè l’ordre és el que mana a l’etiqueta.',
    msgInvalidArticleType: 'Tipologia no vàlida: «{{value}}»',
    msgInvalidNumber: 'Valor numèric no vàlid a «{{field}}»: «{{value}}»',
    msgInvalidBoolean: 'Valor sí/no no vàlid a «{{field}}»: «{{value}}»',
    msgSchemaIssue: '{{path}}: {{message}}',
    msgDuplicateInFile: 'Codi repetit al fitxer (ja hi és a la fila {{row}})',
    msgSkuFormat:
      'El codi «{{code}}» no segueix el format de codis d’article de l’obrador (com {{example}}). No es corregeix sol: arregla’l al fitxer.',
    // Preview step
    previewTitle: 'Revisió de files',
    previewSummary_one:
      '{{count}} fila · {{created}} a crear · {{updated}} a actualitzar · {{unchanged}} sense canvis · {{skipped}} a ometre · {{error}} amb error',
    previewSummary_other:
      '{{count}} files · {{created}} a crear · {{updated}} a actualitzar · {{unchanged}} sense canvis · {{skipped}} a ometre · {{error}} amb error',
    colRow: 'Fila',
    colAction: 'Acció',
    colSkuName: 'Codi · Nom',
    colMessages: 'Observacions',
    noMessages: '—',
    // What to do with codes that already exist
    strategyTitle: 'Codis que ja existeixen',
    strategyLabel: 'Què fem amb els articles que ja hi són?',
    strategyHint:
      'Només s’escriuen les columnes que has mapat i omplert. Una cel·la buida no esborra mai una dada existent, i una columna que no has mapat no es toca.',
    strategy_update_existing: 'Actualitza’ls amb les dades del full',
    strategy_skip_existing: 'Deixa’ls tal com estan',
    // Per-row outcome
    actionCreated: 'Nou',
    actionUpdated: 'Actualitza',
    actionUnchanged: 'Sense canvis',
    actionSkipped: 'Omesa',
    actionError: 'Error',
    noteWillUpdate: 'Canvia: {{fields}}',
    noteWillSkip: 'Ja existeix; no es tocarà (canviaria: {{fields}})',
    noteUnchanged: 'Ja existeix amb la mateixa informació',
    nothingToChange:
      'Cap fila no canvia res: tots aquests articles ja hi són amb la mateixa informació.',
    nothingToImport: 'No hi ha cap fila vàlida per importar. Revisa el mapatge o el fitxer.',
    commitCreateOnly_one: 'Crea {{count}} article',
    commitCreateOnly_other: 'Crea {{count}} articles',
    commitUpdateOnly_one: 'Actualitza {{count}} article',
    commitUpdateOnly_other: 'Actualitza {{count}} articles',
    commitBoth: 'Crea {{created}} i actualitza {{updated}}',
    committing: 'Escrivint…',
    noRunError: 'No s’ha trobat la importació; torna a revisar les files.',
    // Result step
    resultSummary_one: 'Importació completada · {{count}} article escrit al catàleg',
    resultSummary_other: 'Importació completada · {{count}} articles escrits al catàleg',
    resultNoChanges_one:
      'No s’ha canviat res al catàleg: la fila del fitxer no ha creat ni actualitzat cap article.',
    resultNoChanges_other:
      'No s’ha canviat res al catàleg: cap de les {{count}} files del fitxer no ha creat ni actualitzat cap article.',
    resultTitle: 'Resultat de la importació',
    resultCreated: 'Creats',
    resultUpdated: 'Actualitzats',
    resultUnchanged: 'Sense canvis',
    resultSkipped: 'Omesos',
    resultErrors: 'Errors',
    resultTotal_one: '{{count}} fila al fitxer',
    resultTotal_other: '{{count}} files al fitxer',
    newImport: 'Nova importació',
    viewCatalog: 'Ves al catàleg',
    // ── Existences importer (entity 'stock') ──────────────────────────────────
    entityTitle: 'Què vols importar?',
    entityLabel: 'Tipus de dades',
    entityHint: 'Canviar de tipus buida el que tinguis a mig importar.',
    entity_articles: 'Catàleg d’articles',
    entity_stock: 'Existències',
    // Mappable stock fields
    field_quantity: 'Quantitat',
    field_unit: 'Unitat',
    field_lot_code: 'Lot',
    field_expiry_date: 'Caducitat',
    // Stock template
    templatesHintStock:
      'Descarrega un full amb les columnes que l’importador reconeix, amb un exemple amb lot i un altre sense.',
    downloadTemplateStock: 'Plantilla d’existències',
    downloadTemplateStockHint:
      'Escriu la quantitat TOTAL que tens de cada article. Deixa la unitat en blanc per fer servir la unitat base de l’article, i escriu la caducitat com 2026-12-31.',
    templateStockSheetName: 'Existències',
    templateStockFilename: 'plantilla-existencies.xlsx',
    // Stock validation messages
    msgUnknownArticle: 'No hi ha cap article amb el codi «{{sku}}»; crea’l abans al catàleg',
    msgArticleNotStockTracked:
      'L’article «{{sku}}» no gestiona existències; es carregaran igualment',
    msgNegativeQuantity: 'La quantitat no pot ser negativa: «{{value}}»',
    msgNoUnitConversion:
      'No hi ha cap equivalència declarada de «{{code}}» a la unitat base de «{{sku}}»',
    msgInvalidDate: 'Data no vàlida: «{{value}}»',
    msgExpiryNeedsLot:
      'La caducitat «{{value}}» necessita un lot; informa el lot o treu la caducitat',
    msgAmbiguousDate: '«{{value}}» s’ha llegit com a {{date}} (dia/mes); comprova-ho',
    msgExpiryInPast: 'La caducitat {{date}} ja ha passat',
    msgLotExpiryKept:
      'El lot «{{lot}}» ja hi és amb caducitat {{stored}}; es manté la que ja hi consta',
    msgDuplicateCoordinateInFile: 'Aquest article i lot ja surten a la fila {{row}}',
    // Stock preview
    stockLotNote:
      'El codi de lot del fitxer es desa com a «Lot del proveïdor», perquè coincideixi amb el del proveïdor. El número de lot intern (MP-L-…) el genera Obrava sol i no es pot informar al fitxer: a Existències hi surt amb el codi del proveïdor al costat.',
    strategyHintStock:
      'Cada fila diu el TOTAL que hi ha. Només s’escriu la diferència amb el que ja consta, així que tornar a importar el mateix fitxer no mou res.',
    strategyStock_update_existing: 'Ajusta’ls a la quantitat del full',
    strategyStock_skip_existing: 'Deixa’ls tal com estan',
    noteStockLot: 'lot {{lot}}',
    noteStockCreate: 'Es carregaran {{quantity}}',
    noteStockUpdate: 'Passarà de {{from}} a {{to}}',
    noteStockSkip: 'Es queda amb {{current}}',
    noteStockUnchanged: 'Ja hi ha {{quantity}}',
    commitCreateOnlyStock_one: 'Carrega {{count}} existència',
    commitCreateOnlyStock_other: 'Carrega {{count}} existències',
    commitUpdateOnlyStock_one: 'Ajusta {{count}} existència',
    commitUpdateOnlyStock_other: 'Ajusta {{count}} existències',
    commitBothStock: 'Carrega {{created}} i ajusta {{updated}}',
    // Stock commit + result
    stockContextMissing:
      'Encara no s’han pogut llegir les existències actuals; torna-ho a provar en un moment.',
    stockReceptionReference: 'SALDO-INICIAL',
    stockReceptionNote: 'Càrrega inicial d’existències (importador)',
    resultSummaryStock_one: 'Importació completada · {{count}} línia d’existències escrita',
    resultSummaryStock_other: 'Importació completada · {{count}} línies d’existències escrites',
    resultNoChangesStock_one:
      'No s’ha mogut res: la fila del fitxer ja coincidia amb les existències actuals.',
    resultNoChangesStock_other:
      'No s’ha mogut res: cap de les {{count}} files no ha canviat les existències actuals.',
    viewStock: 'Ves a existències',
  },

  'backoffice.supplierImport': {
    title: 'Importa proveïdors',
    subtitle: 'Carrega la llista de proveïdors des d’un full de càlcul',
    // Passa 1 — plantilla i fitxer
    templateTitle: '1. Descarrega la plantilla',
    templateHint:
      'La plantilla porta les columnes que Obrava reconeix i una fila d’exemple. Omple-la i torna-la a pujar.',
    downloadTemplate: 'Plantilla buida',
    downloadCurrent: 'Plantilla amb els proveïdors actuals ({{count}})',
    downloadCurrentHint:
      'Descarrega els proveïdors que ja tens, edita’ls a Excel i torna’ls a pujar: les files que no toquis no escriuran res.',
    templateFilename: 'plantilla-proveidors.xlsx',
    currentFilename: 'proveidors-obrador.xlsx',
    templateSheetName: 'Proveïdors',
    uploadTitle: '2. Puja el fitxer emplenat',
    uploadHint:
      'Accepta CSV, XLS i XLSX. La primera fila ha de ser la capçalera, amb els noms de columna de la plantilla.',
    chooseFile: 'Tria un fitxer',
    reading: 'Llegint el fitxer…',
    fileReadError: 'No s’ha pogut llegir el fitxer.',
    noRows: 'El fitxer no té cap fila de dades. Comprova que hi ha una capçalera i almenys una fila.',
    missingColumns:
      'No s’hi ha trobat la columna obligatòria «{{fields}}». Descarrega la plantilla i fes servir els seus noms de columna.',
    ignoredColumns: 'Columnes que no es faran servir: {{columns}}',
    legacyTermsColumn_one: 'La columna «{{column}}» ja no s’importa: Obrava ja no té «Condicions comercials». {{count}} fila hi té contingut: passa’l a «Notes» i torna a pujar el fitxer.',
    legacyTermsColumn_other: 'La columna «{{column}}» ja no s’importa: Obrava ja no té «Condicions comercials». {{count}} files hi tenen contingut: passa’l a «Notes» i torna a pujar el fitxer.',
    columnsGuide: 'Dies d’entrega: dies de la setmana separats per comes, p. ex. «Dl, Dc, Dv» (també «Dilluns, Dimecres» o «Dl-Dv»). Canal de comandes: «Correu», «WhatsApp» o «Altres mitjans»; amb canal Correu cal el correu de comandes, i amb canal WhatsApp el telèfon/WhatsApp de comandes (el correu i el telèfon generals no els substitueixen). La informació lliure, com les condicions de pagament, va a «Notes». Els tres correus —general, de comandes i de qualitat— són columnes independents: cap no se suposa a partir d’una altra.',
    guideSheetName: 'Instruccions',
    guideColField: 'Camp',
    guideColWhat: 'Què hi va',
    guideColExample: 'Exemple',
    guide_code:
      'Opcional. En blanc, Obrava el genera en crear el proveïdor. Un codi nou ha de seguir el format de codis de l’obrador (Configuració) i no es pot repetir; un codi que ja existeix identifica el proveïdor.',
    guide_name: 'Obligatori.',
    guide_commercial_name: 'Opcional. El nom amb què es coneix, si no és la raó social.',
    guide_tax_id: 'Opcional. No es pot repetir entre proveïdors actius.',
    guide_contact_name: 'Opcional.',
    guide_phone: 'Opcional. Telèfon general de contacte; el de comandes va a part.',
    guide_email: 'Opcional. Correu general de contacte; els de comandes i de qualitat van a part.',
    guide_address: 'Opcional. Adreça principal (fiscal).',
    guide_delivery_weekdays: 'Opcional. Dies de la setmana en què passa, separats per comes: Dl, Dt, Dc, Dj, Dv, Ds, Dg (també els noms sencers o un interval com Dl-Dv).',
    guide_order_channel: 'Opcional. Correu, WhatsApp o Altres mitjans (telèfon, en persona…).',
    guide_order_emails: 'Un o més correus separats per comes (màxim 10). Obligatori si el canal és Correu.',
    guide_order_phone: 'Telèfon o WhatsApp de comandes, amb el prefix del país si és possible. Obligatori si el canal és WhatsApp.',
    guide_quality_email: 'Opcional. Un correu: on van les sol·licituds i renovacions de fitxes tècniques.',
    guide_notes: 'Opcional. Informació lliure: condicions de pagament, observacions…',
    guide_status: 'Opcional. Actiu o Inactiu.',
    recognizedColumns: 'Columnes reconegudes: {{columns}}',
    // Camps
    field_code: 'Codi de proveïdor',
    field_name: 'Nom / raó social',
    field_commercial_name: 'Nom comercial',
    field_tax_id: 'NIF / CIF',
    field_contact_name: 'Persona de contacte',
    field_phone: 'Telèfon',
    field_email: 'Correu electrònic (general)',
    field_address: 'Adreça principal',
    field_delivery_weekdays: 'Dies habituals entrega',
    field_order_channel: 'Canal de comandes',
    field_order_emails: 'Correu electrònic de comandes',
    field_order_phone: 'Telèfon/WhatsApp de comandes',
    field_quality_email: 'Correu electrònic de qualitat',
    field_notes: 'Notes',
    field_status: 'Estat',
    statusActive: 'Actiu',
    statusInactive: 'Inactiu',
    channelEmail: 'Correu',
    channelWhatsapp: 'WhatsApp',
    channelManual: 'Altres mitjans',
    templateExampleName: 'Farines Puig, SL',
    templateExampleCommercialName: 'Farines Puig',
    templateExampleContact: 'Anna Puig',
    templateExampleAddress: 'Carrer del Molí, 12 · 17800 Olot',
    templateExampleNotes: 'Pagament a 30 dies · ports pagats',
    // Observacions per fila
    msgRequiredMissing: 'Falta el camp obligatori «{{field}}»',
    msgInvalidEmail: 'Correu electrònic no vàlid a «{{field}}»: «{{value}}»',
    msgInvalidStatus: 'Estat no reconegut: «{{value}}». Fes servir «Actiu» o «Inactiu».',
    msgInvalidBoolean: 'Valor sí/no no vàlid a «{{field}}»: «{{value}}»',
    msgInvalidNumber: 'Valor numèric no vàlid a «{{field}}»: «{{value}}»',
    msgAmbiguousTaxId:
      'Hi ha més d’un proveïdor actiu amb el NIF «{{value}}»: informa el codi per dir quin és.',
    msgUnidentifiable:
      'Sense codi ni NIF no es pot comprovar si ja hi és; es crearà com a proveïdor nou.',
    msgDuplicateInFile: 'Repetit al fitxer (ja hi és a la fila {{row}})',
    msgCodeTaken: 'El codi «{{code}}» ja és d’un altre proveïdor ({{name}}).',
    msgCodeFormat:
      'El codi «{{code}}» de «{{field}}» no segueix el format de codis de l’obrador (com {{example}}). No es corregeix sol: arregla’l al fitxer o deixa’l buit i se’n generarà un.',
    msgTaxIdTaken: 'El NIF «{{value}}» ja és d’un altre proveïdor ({{name}}).',
    msgTaxIdInFile: 'El NIF «{{value}}» ja surt a la fila {{row}} del fitxer.',
    msgInvalidWeekdays: 'Dies d’entrega no reconeguts a «{{field}}»: «{{value}}». Escriu-los com «Dl, Dc, Dv» (també «Dilluns, Dimecres» o «Dl-Dv»).',
    msgInvalidChannel: 'Canal no reconegut a «{{field}}»: «{{value}}». Valors admesos: Correu, WhatsApp, Altres mitjans.',
    msgChannelUnavailable: 'El canal «{{value}}» encara no està disponible a Obrava. Valors admesos: Correu, WhatsApp, Altres mitjans.',
    msgInvalidOrderEmails: 'Correu de comandes no vàlid: «{{value}}»',
    msgTooManyOrderEmails: 'Com a molt {{max}} correus de comandes per proveïdor.',
    msgInvalidPhone: 'Telèfon no vàlid a «{{field}}»: «{{value}}». Fes servir de 7 a 15 xifres, amb espais i el prefix + si cal.',
    msgOrderEmailRequired: 'El canal és Correu: falta «{{field}}». El correu general no el substitueix.',
    msgOrderPhoneRequired: 'El canal és WhatsApp: falta «{{field}}». El telèfon general no el substitueix.',
    // Passa 3 — revisió
    previewTitle: '3. Revisa abans d’importar',
    previewSummary_one:
      '{{count}} fila · {{created}} a crear · {{updated}} a actualitzar · {{unchanged}} sense canvis · {{skipped}} a ometre · {{error}} amb error',
    previewSummary_other:
      '{{count}} files · {{created}} a crear · {{updated}} a actualitzar · {{unchanged}} sense canvis · {{skipped}} a ometre · {{error}} amb error',
    colRow: 'Fila',
    colAction: 'Acció',
    colSupplier: 'Proveïdor',
    colMessages: 'Observacions',
    noMessages: '—',
    codeWillBeGenerated: 'codi automàtic',
    strategyLabel: 'Què fem amb els proveïdors que ja hi són?',
    strategyHint:
      'Només s’escriuen les columnes que el full porta emplenades. Una cel·la buida no esborra mai una dada existent.',
    strategy_update_existing: 'Actualitza’ls amb les dades del full',
    strategy_skip_existing: 'Deixa’ls tal com estan',
    actionCreated: 'Nou',
    actionUpdated: 'Actualitza',
    actionUnchanged: 'Sense canvis',
    actionSkipped: 'Omesa',
    actionError: 'Error',
    noteWillUpdate: 'Canvia: {{fields}}',
    noteWillSkip: 'Ja existeix; no es tocarà (canviaria: {{fields}})',
    noteUnchanged: 'Ja existeix amb la mateixa informació',
    noteWillDeactivate: 'Es donarà de baixa',
    noteWillReactivate: 'Es reactivarà',
    deactivationReason: 'Importació de proveïdors: el fitxer el marca com a inactiu',
    nothingToChange: 'Cap fila canvia res: tots els proveïdors del fitxer ja hi són igual.',
    nothingToImport: 'Cap fila es pot importar. Corregeix els errors i torna a pujar el fitxer.',
    commitCreateOnly_one: 'Crea {{count}} proveïdor',
    commitCreateOnly_other: 'Crea {{count}} proveïdors',
    commitUpdateOnly_one: 'Actualitza {{count}} proveïdor',
    commitUpdateOnly_other: 'Actualitza {{count}} proveïdors',
    commitBoth: 'Crea {{created}} i actualitza {{updated}}',
    committing: 'Important…',
    back: 'Enrere',
    // Passa 4 — resultat
    resultTitle: 'Resultat de la importació',
    resultSummary_one: '{{count}} proveïdor importat',
    resultSummary_other: '{{count}} proveïdors importats',
    resultNoChanges_one: 'No s’ha canviat res: {{count}} fila processada.',
    resultNoChanges_other: 'No s’ha canviat res: {{count}} files processades.',
    resultCreated: 'Creats',
    resultUpdated: 'Actualitzats',
    resultUnchanged: 'Sense canvis',
    resultSkipped: 'Omesos',
    resultErrors: 'Errors',
    resultTotal_one: '{{count}} fila al fitxer',
    resultTotal_other: '{{count}} files al fitxer',
    newImport: 'Importa un altre fitxer',
    close: 'Tanca',
  },

  'backoffice.supplierArticleImport': {
    title: 'Importa relació article-proveïdor',
    subtitle: 'Carrega quins proveïdors serveixen cada article, a quin preu i en quina unitat',
    prerequisites:
      'Abans cal tenir donats d’alta els proveïdors, els articles i les unitats de compra de cada article. L’importador no en crea cap.',
    // Passa 1 — plantilla i fitxer
    templateTitle: '1. Descarrega la plantilla',
    templateHint:
      'Una fila per relació article ↔ proveïdor. Els codis han d’existir: l’importador enllaça, no crea articles ni proveïdors ni conversions.',
    downloadTemplate: 'Plantilla buida',
    downloadCurrent: 'Plantilla amb les relacions actuals ({{count}})',
    downloadCurrentHint:
      'Descarrega el que ja tens, edita’l a Excel i torna’l a pujar: les files que no toquis no escriuran res.',
    templateFilename: 'plantilla-relacio-article-proveidor.xlsx',
    currentFilename: 'relacio-article-proveidor.xlsx',
    templateSheetName: 'Relació article-proveïdor',
    uploadTitle: '2. Puja el fitxer emplenat',
    uploadHint:
      'Accepta CSV, XLS i XLSX. La primera fila ha de ser la capçalera, amb els noms de columna de la plantilla.',
    chooseFile: 'Tria un fitxer',
    reading: 'Llegint el fitxer…',
    fileReadError: 'No s’ha pogut llegir el fitxer.',
    noRows: 'El fitxer no té cap fila de dades. Comprova que hi ha una capçalera i almenys una fila.',
    missingColumns:
      'No s’hi ha trobat la columna obligatòria «{{fields}}». Descarrega la plantilla i fes servir els seus noms de columna.',
    ignoredColumns: 'Columnes que no es faran servir: {{columns}}',
    // Camps
    field_article_code: 'Codi article',
    field_supplier_code: 'Codi proveïdor',
    field_supplier_ref: 'Referència del proveïdor',
    field_supplier_description: 'Descripció del proveïdor',
    field_last_price: 'Preu de compra (per unitat base)',
    field_purchase_unit: 'Unitat de compra',
    field_purchase_unit_factor: 'Equivalència (informativa)',
    field_is_primary: 'Proveïdor principal',
    booleanTrue: 'Sí',
    booleanFalse: 'No',
    // Exemples de la plantilla
    exampleArticleCode: 'MP00001',
    exampleSupplierCode: 'PRV-0001',
    exampleSupplierRef: 'FAR-25',
    exampleDescription: 'Farina de força 25 kg',
    exampleUnit: 'Sac',
    // Observacions per fila
    msgRequiredMissing: 'Falta el camp obligatori «{{field}}»',
    msgInvalidNumber: 'Valor numèric no vàlid a «{{field}}»: «{{value}}»',
    msgInvalidBoolean: 'Valor sí/no no vàlid a «{{field}}»: «{{value}}»',
    msgUnknownArticle: 'El codi d’article «{{code}}» no existeix al catàleg.',
    msgUnknownSupplier: 'El codi de proveïdor «{{code}}» no existeix al mestre.',
    msgUnknownUnit:
      'La unitat «{{unit}}» no existeix. Fes servir el nom d’una unitat d’envàs o el codi de la unitat base de l’article.',
    msgUnitNotForArticle:
      'L’article «{{code}}» no té cap conversió per a «{{unit}}»: defineix-la a la fitxa de l’article.',
    msgDuplicateInFile:
      'Aquest article i aquest proveïdor ja surten junts a la fila {{row}}.',
    msgTwoPrimaries:
      'Aquest article ja té la fila {{row}} marcada com a proveïdor principal; només pot haver-n’hi un.',
    msgPrimaryMoves: 'Passarà a ser el proveïdor principal (deixarà de ser-ho {{name}})',
    // Passa 3 — revisió
    previewTitle: '3. Revisa abans d’importar',
    previewSummary_one:
      '{{count}} fila · {{created}} a crear · {{updated}} a actualitzar · {{unchanged}} sense canvis · {{skipped}} a ometre · {{error}} amb error',
    previewSummary_other:
      '{{count}} files · {{created}} a crear · {{updated}} a actualitzar · {{unchanged}} sense canvis · {{skipped}} a ometre · {{error}} amb error',
    colRow: 'Fila',
    colAction: 'Acció',
    colLink: 'Article · Proveïdor',
    colMessages: 'Observacions',
    noMessages: '—',
    strategyLabel: 'Què fem amb les relacions que ja hi són?',
    strategyHint:
      'Només s’escriuen les columnes que el full porta emplenades. Una cel·la buida no esborra mai una dada, i una relació que el full no menciona no es toca.',
    strategy_update_existing: 'Actualitza-les amb les dades del full',
    strategy_skip_existing: 'Deixa-les tal com estan',
    actionCreated: 'Nova',
    actionUpdated: 'Actualitza',
    actionUnchanged: 'Sense canvis',
    actionSkipped: 'Omesa',
    actionError: 'Error',
    noteWillUpdate: 'Canvia: {{fields}}',
    noteWillSkip: 'Ja existeix; no es tocarà (canviaria: {{fields}})',
    noteUnchanged: 'Ja existeix amb la mateixa informació',
    nothingToChange: 'Cap fila canvia res: tot el que porta el fitxer ja hi és igual.',
    nothingToImport: 'Cap fila es pot importar. Corregeix els errors i torna a pujar el fitxer.',
    commitCreateOnly_one: 'Crea {{count}} relació',
    commitCreateOnly_other: 'Crea {{count}} relacions',
    commitUpdateOnly_one: 'Actualitza {{count}} relació',
    commitUpdateOnly_other: 'Actualitza {{count}} relacions',
    commitBoth: 'Crea {{created}} i actualitza {{updated}}',
    committing: 'Important…',
    back: 'Enrere',
    // Passa 4 — resultat
    resultTitle: 'Resultat de la importació',
    resultSummary_one: '{{count}} relació importada',
    resultSummary_other: '{{count}} relacions importades',
    resultNoChanges_one: 'No s’ha canviat res: {{count}} fila processada.',
    resultNoChanges_other: 'No s’ha canviat res: {{count}} files processades.',
    resultCreated: 'Creades',
    resultUpdated: 'Actualitzades',
    resultUnchanged: 'Sense canvis',
    resultSkipped: 'Omeses',
    resultErrors: 'Errors',
    resultTotal_one: '{{count}} fila al fitxer',
    resultTotal_other: '{{count}} files al fitxer',
    newImport: 'Importa un altre fitxer',
    close: 'Tanca',
  },
  'backoffice.clientImport': {
    title: 'Importa clients',
    subtitle: 'Carrega els clients i els seus punts d’enviament des d’un full de càlcul',
    // Passa 1 — plantilla i fitxer
    templateTitle: '1. Descarrega la plantilla',
    templateHint:
      'La plantilla té dues pestanyes: «Clients» (una fila per client) i «Adreces d’enviament» (una fila per punt d’entrega, lligada al client pel seu codi).',
    downloadTemplate: 'Plantilla buida',
    downloadCurrent: 'Plantilla amb els clients actuals ({{count}})',
    downloadCurrentHint:
      'Descarrega els clients i els punts que ja tens, edita’ls a Excel i torna’ls a pujar: les files que no toquis no escriuran res.',
    templateFilename: 'plantilla-clients.xlsx',
    currentFilename: 'clients-obrador.xlsx',
    sheetClients: 'Clients',
    sheetPoints: 'Adreces d’enviament',
    uploadTitle: '2. Puja el fitxer emplenat',
    uploadHint:
      'Accepta XLSX i XLS amb les dues pestanyes. Un CSV també val, però només pot portar clients: un fitxer pla no té pestanyes.',
    chooseFile: 'Tria un fitxer',
    reading: 'Llegint el fitxer…',
    fileReadError: 'No s’ha pogut llegir el fitxer.',
    noRows: 'La pestanya «{{sheet}}» no té cap fila de dades. Comprova que hi ha una capçalera i almenys una fila.',
    missingColumns:
      'A la pestanya «{{sheet}}» no s’hi ha trobat la columna obligatòria «{{fields}}». Descarrega la plantilla i fes servir els seus noms de columna.',
    ignoredColumns: '{{sheet}} · columnes que no es faran servir: {{columns}}',
    noPointsSheet:
      'El fitxer no porta la pestanya «Adreces d’enviament»: s’importaran només els clients.',
    // Camps del client
    field_code: 'Codi client',
    field_name: 'Nom / raó social',
    field_commercial_name: 'Nom comercial',
    field_tax_id: 'NIF / CIF',
    field_contact_name: 'Persona de contacte general',
    field_phone: 'Telèfon general',
    field_email: 'Correu electrònic general',
    field_address: 'Adreça fiscal',
    field_notes: 'Notes',
    field_status: 'Estat',
    // Camps del punt d’enviament
    point_client_code: 'Codi client',
    point_code: 'Codi punt d’enviament',
    point_name: 'Nom del punt d’enviament',
    point_address: 'Adreça',
    point_postal_code: 'Codi postal',
    point_city: 'Població',
    point_province: 'Província',
    point_country: 'País',
    point_contact_name: 'Persona de contacte',
    point_phone: 'Telèfon',
    point_email: 'Correu electrònic',
    point_delivery_instructions: 'Instruccions d’entrega',
    point_is_default: 'Adreça predeterminada',
    statusActive: 'Actiu',
    statusInactive: 'Inactiu',
    booleanTrue: 'Sí',
    booleanFalse: 'No',
    // Exemples de la plantilla
    exampleClientName: 'Pastisseries Girona SL',
    exampleClientCommercial: 'Pastisseries Girona',
    exampleClientContact: 'Marta Vidal',
    exampleClientAddress: 'Passeig de la Devesa, 4 · 17001 Girona',
    exampleClientNotes: 'Factura a final de mes',
    examplePointName: 'Botiga Girona Centre',
    examplePointAddress: 'Carrer Nou, 21',
    examplePointCity: 'Girona',
    examplePointProvince: 'Girona',
    examplePointCountry: 'Espanya',
    examplePointContact: 'Jordi Serra',
    examplePointInstructions: 'Entrega abans de les 9 h · moll del darrere',
    exampleWarehouseName: 'Magatzem central',
    exampleWarehouseAddress: 'Polígon Mas Xirgu, nau 7',
    // Observacions per fila
    msgRequiredMissing: 'Falta el camp obligatori «{{field}}»',
    msgInvalidEmail: 'Correu electrònic no vàlid: «{{value}}»',
    msgInvalidStatus: 'Estat no reconegut: «{{value}}». Fes servir «Actiu» o «Inactiu».',
    msgInvalidBoolean: 'Valor sí/no no vàlid a «{{field}}»: «{{value}}»',
    msgInvalidNumber: 'Valor numèric no vàlid a «{{field}}»: «{{value}}»',
    msgAmbiguousTaxId:
      'Hi ha més d’un client actiu amb el NIF «{{value}}»: informa el codi per dir quin és.',
    msgUnidentifiable:
      'Sense codi ni NIF no es pot comprovar si ja hi és; es crearà com a client nou.',
    msgDuplicateInFile: 'Repetit al fitxer (ja hi és a la fila {{row}})',
    msgCodeTaken: 'El codi «{{code}}» ja és d’un altre client ({{name}}).',
    msgCodeFormat:
      'El codi «{{code}}» de «{{field}}» no segueix el format de codis de l’obrador (com {{example}}). No es corregeix sol: arregla’l al fitxer o deixa’l buit i se’n generarà un.',
    msgTaxIdTaken: 'El NIF «{{value}}» ja és d’un altre client ({{name}}).',
    msgTaxIdInFile: 'El NIF «{{value}}» ja surt a la fila {{row}} del fitxer.',
    msgUnknownClientCode:
      'El codi de client «{{code}}» no existeix ni surt a la pestanya «Clients».',
    msgClientRowFailed:
      'El client «{{code}}» no s’importarà (la seva fila té errors), així que aquest punt tampoc.',
    msgPointDuplicateInFile:
      'Aquest punt ja surt a la fila {{row}} per al mateix client.',
    msgPointAmbiguousName:
      'El client ja té més d’un punt que es diu «{{name}}»: informa el codi del punt per dir quin és.',
    msgPointAddressRequired: 'Un punt nou necessita una adreça.',
    msgTwoDefaults:
      'Aquest client ja té la fila {{row}} marcada com a adreça predeterminada; només pot haver-n’hi una.',
    msgDefaultMoves: 'Passarà a ser l’adreça predeterminada del client',
    // Passa 3 — revisió
    previewTitle: '3. Revisa abans d’importar',
    previewClients: 'Clients',
    previewPoints: 'Punts d’enviament',
    previewSummary_one:
      '{{count}} fila · {{created}} a crear · {{updated}} a actualitzar · {{unchanged}} sense canvis · {{skipped}} a ometre · {{error}} amb error',
    previewSummary_other:
      '{{count}} files · {{created}} a crear · {{updated}} a actualitzar · {{unchanged}} sense canvis · {{skipped}} a ometre · {{error}} amb error',
    noPointRows: 'El fitxer no porta cap punt d’enviament.',
    colRow: 'Fila',
    colAction: 'Acció',
    colClient: 'Client',
    colPoint: 'Punt',
    colMessages: 'Observacions',
    noMessages: '—',
    codeWillBeGenerated: 'codi automàtic',
    strategyLabel: 'Què fem amb els clients i els punts que ja hi són?',
    strategyHint:
      'Només s’escriuen les columnes que el full porta emplenades. Una cel·la buida no esborra mai una dada, i un punt d’enviament que el full no menciona no es toca.',
    strategy_update_existing: 'Actualitza’ls amb les dades del full',
    strategy_skip_existing: 'Deixa’ls tal com estan',
    actionCreated: 'Nou',
    actionUpdated: 'Actualitza',
    actionUnchanged: 'Sense canvis',
    actionSkipped: 'Omesa',
    actionError: 'Error',
    noteWillUpdate: 'Canvia: {{fields}}',
    noteWillSkip: 'Ja existeix; no es tocarà (canviaria: {{fields}})',
    noteUnchanged: 'Ja existeix amb la mateixa informació',
    noteWillDeactivate: 'Es donarà de baixa',
    noteWillReactivate: 'Es reactivarà',
    deactivationReason: 'Importació de clients: el fitxer el marca com a inactiu',
    nothingToChange: 'Cap fila canvia res: tot el que porta el fitxer ja hi és igual.',
    nothingToImport: 'Cap fila es pot importar. Corregeix els errors i torna a pujar el fitxer.',
    commitLabel: 'Importa',
    commitClients_one: '{{count}} client',
    commitClients_other: '{{count}} clients',
    commitPoints_one: '{{count}} punt',
    commitPoints_other: '{{count}} punts',
    committing: 'Important…',
    back: 'Enrere',
    // Passa 4 — resultat
    resultTitle: 'Resultat de la importació',
    resultClients: 'Clients',
    resultPoints: 'Punts d’enviament',
    resultSummary: 'Importació feta: {{clients}} i {{points}}',
    resultNoChanges: 'No s’ha canviat res.',
    resultCreated: 'Creats',
    resultUpdated: 'Actualitzats',
    resultUnchanged: 'Sense canvis',
    resultSkipped: 'Omesos',
    resultErrors: 'Errors',
    newImport: 'Importa un altre fitxer',
    close: 'Tanca',
  },
  'backoffice.settings': {
    title: 'Configuració',
    subtitle: 'Administració de l’obrador',
    // Hub (grouped settings index)
    hubSearchPlaceholder: 'Cerca un ajust',
    hubSearchNoResults: 'Cap ajust no coincideix amb la cerca',
    groupObrador: 'Obrador',
    groupProduction: 'Producció',
    groupCatalog: 'Catàleg',
    groupLabeling: 'Etiquetatge i normativa',
    groupThirdParties: 'Tercers',
    descGeneral: 'Numeració, llindars i sessió d’operari',
    descShifts: 'Torns de treball de l’obrador',
    descStructure: 'Zones, seccions i ubicacions: on es treballa i on són les existències',
    descCategories: 'Classificació d’articles del catàleg',
    descUnits: 'Unitats de mesura',
    descTaxes: 'IVA i altres impostos aplicables',
    descPackagingUnits: 'Unitats d’embalatge i presentació',
    descArticleFamilies: 'Conservació, ús, preparació i envàs',
    descIngredients: 'Mestre d’ingredients de les receptes',
    descProcesses: 'Passos reutilitzables de les receptes',
    descResources: 'Forns, pastadores i altres equips',
    descLegislation: 'Normativa de referència per a les FT',
    descLabelTemplates: 'Dissenys d’etiqueta per a lots',
    descBranding: 'Logotip i identitat de les FT',
    descClients: 'Mestre de clients per a vendes',
    descSuppliers: 'Mestre de proveïdors per a compres',
    descDevices: 'Tauletes i dispositius de planta',
    descAuditLog: 'Qui ha canviat què i quan',
    // Tabs
    tabGeneral: 'General',
    tabShifts: 'Torns',
    tabStructure: 'Estructura de l’obrador',
    tabCategories: 'Categories',
    tabUnits: 'Unitats',
    tabTaxes: 'Impostos',
    tabPackagingUnits: 'Unitats d’embalatge',
    tabClients: 'Clients',
    tabSuppliers: 'Proveïdors',
    tabDevices: 'Dispositius',
    tabAuditLog: 'Registre d’activitat',
    // Registre d'activitat (F0.9)
    auditTitle: 'Registre d’activitat',
    auditSubtitle: 'Historial d’altes, canvis i baixes de l’obrador: qui, quan i què',
    auditEmpty: 'Encara no hi ha activitat registrada',
    auditFilterAllActions: 'Totes les accions',
    auditFilterAllEntities: 'Tots els elements',
    auditLoadMore: 'Carrega’n més',
    // Shared
    general: 'General',
    numbering: 'Numeració',
    // Regla de composició de la declaració d'ingredients (FT + etiqueta)
    ingredientsRuleTitle: 'Declaració d’ingredients',
    ingredientsRuleIntro:
      'Com s’escriu la llista d’ingredients de la fitxa tècnica i de l’etiqueta. La mateixa regla val per a totes dues: l’etiqueta imprimeix el text que va desar la versió de la fitxa.',
    ingredientsRule: 'Regla de composició',
    ingredientsRuleHint:
      'S’aplica a les fitxes que es generin a partir d’ara; les versions ja publicades conserven el seu text.',
    ingredientsRule_raw_material: 'Només la matèria primera',
    ingredientsRule_ingredients: 'Només els ingredients',
    ingredientsRule_both: 'Matèria primera i ingredients',
    ingredientsRuleExample: 'Exemple',
    thresholds: 'Llindars',
    quietHours: 'Hores de silenci',
    users: 'Usuaris',
    devices: 'Dispositius',
    sectorVocabulary: 'Vocabulari per sector',
    loadError: 'No s’han pogut carregar les dades de configuració',
    saved: 'Canvis desats',
    saveError: 'No s’han pogut desar els canvis',
    noPermission: 'Només els administradors poden accedir a la configuració',
    add: 'Afegeix',
    edit: 'Edita',
    deactivate: 'Dona de baixa',
    reactivate: 'Reactiva',
    showInactive: 'Mostra els donats de baixa',
    selectAllRows: 'Selecciona totes les files visibles',
    selectRow: 'Selecciona {{name}}',
    bulkSelected_one: '{{count}} seleccionat',
    bulkSelected_other: '{{count}} seleccionats',
    clearSelection: 'Buida la selecció',
    bulkDeactivateConfirm_one: 'Dona de baixa ({{count}})',
    bulkDeactivateConfirm_other: 'Dona de baixa ({{count}})',
    bulkDeactivateClientsTitle_one: 'Dona de baixa {{count}} client',
    bulkDeactivateClientsTitle_other: 'Dona de baixa {{count}} clients',
    bulkDeactivateClientsBody_one: 'Es donarà de baixa el client seleccionat. És una baixa lògica: es conserva l’històric, la traçabilitat i els documents vinculats, i es pot reactivar. El motiu queda registrat.',
    bulkDeactivateClientsBody_other: 'Es donaran de baixa els {{count}} clients seleccionats. És una baixa lògica: es conserva l’històric, la traçabilitat i els documents vinculats, i es poden reactivar. El motiu queda registrat a cadascun.',
    bulkDeactivateSuppliersTitle_one: 'Dona de baixa {{count}} proveïdor',
    bulkDeactivateSuppliersTitle_other: 'Dona de baixa {{count}} proveïdors',
    bulkDeactivateSuppliersBody_one: 'Es donarà de baixa el proveïdor seleccionat. És una baixa lògica: es conserva l’històric, la traçabilitat i els documents vinculats, i es pot reactivar. El motiu queda registrat.',
    bulkDeactivateSuppliersBody_other: 'Es donaran de baixa els {{count}} proveïdors seleccionats. És una baixa lògica: es conserva l’històric, la traçabilitat i els documents vinculats, i es poden reactivar. El motiu queda registrat a cadascun.',
    bulkDeactivatePartial: 'S’han donat de baixa {{done}} de {{total}}. No s’han pogut donar de baixa: {{failed}}. La selecció queda amb els que han fallat.',
    bulkDeactivateCount_one: 'Dona de baixa ({{count}})',
    bulkDeactivateCount_other: 'Dona de baixa ({{count}})',
    bulkReactivateCount_one: 'Reactiva ({{count}})',
    bulkReactivateCount_other: 'Reactiva ({{count}})',
    bulkDeactivateRecordsTitle_one: 'Dona de baixa {{count}} registre',
    bulkDeactivateRecordsTitle_other: 'Dona de baixa {{count}} registres',
    bulkDeactivateRecordsBody_one: 'Es donarà de baixa el registre seleccionat. És una baixa lògica: es conserva l’històric, la traçabilitat i els documents vinculats, i es pot reactivar. El motiu queda registrat.',
    bulkDeactivateRecordsBody_other: 'Es donaran de baixa els {{count}} registres seleccionats. És una baixa lògica: es conserva l’històric, la traçabilitat i els documents vinculats, i es poden reactivar. El motiu queda registrat a cadascun.',
    bulkReactivateConfirm_one: 'Reactiva ({{count}})',
    bulkReactivateConfirm_other: 'Reactiva ({{count}})',
    bulkReactivateClientsTitle_one: 'Reactiva {{count}} client',
    bulkReactivateClientsTitle_other: 'Reactiva {{count}} clients',
    bulkReactivateClientsBody_one: 'Es reactivarà el client seleccionat: tornarà a sortir als desplegables i es podrà usar de nou.',
    bulkReactivateClientsBody_other: 'Es reactivaran els {{count}} clients seleccionats: tornaran a sortir als desplegables i es podran usar de nou.',
    bulkReactivateSuppliersTitle_one: 'Reactiva {{count}} proveïdor',
    bulkReactivateSuppliersTitle_other: 'Reactiva {{count}} proveïdors',
    bulkReactivateSuppliersBody_one: 'Es reactivarà el proveïdor seleccionat: tornarà a sortir als desplegables i es podrà usar de nou.',
    bulkReactivateSuppliersBody_other: 'Es reactivaran els {{count}} proveïdors seleccionats: tornaran a sortir als desplegables i es podran usar de nou.',
    bulkReactivateRecordsTitle_one: 'Reactiva {{count}} registre',
    bulkReactivateRecordsTitle_other: 'Reactiva {{count}} registres',
    bulkReactivateRecordsBody_one: 'Es reactivarà el registre seleccionat: tornarà a sortir als desplegables i es podrà usar de nou.',
    bulkReactivateRecordsBody_other: 'Es reactivaran els {{count}} registres seleccionats: tornaran a sortir als desplegables i es podran usar de nou.',
    bulkReactivatePartial: 'S’han reactivat {{done}} de {{total}}. No s’han pogut reactivar: {{failed}}. La selecció queda amb els que han fallat.',
    searchGeneric: 'Cerca',
    sectionIdentity: 'Identificació',
    sectionContact: 'Contacte',
    sectionAddress: 'Adreça',
    sectionAddressDelivery: 'Adreça i entrega',
    sectionAddressNotes: 'Adreça i notes',
    filterTypeAll: 'Totes les tipologies',
    filterTypeNone: 'Sense tipologia fixada',
    inactiveBadge: 'Baixa',
    empty: 'Encara no hi ha cap registre',
    emptyFiltered: 'Cap registre no coincideix amb la cerca',
    searchByCodeOrName: 'Cerca per codi o nom',
    colName: 'Nom',
    colStatus: 'Estat',
    colActions: 'Accions',
    // General / tenant_settings
    generalTitle: 'Paràmetres generals',
    generalSubtitle: 'Numeració, llindars i sessió d’operari de l’obrador',
    lotNumberingFormat: 'Format de numeració de lot',
    lotNumberingHint: 'Inclou {seq} per a la seqüència, p. ex. L-{seq:05}',
    workOrderNumberingFormat: 'Format de numeració d’OT',
    workOrderNumberingHint: 'Inclou {seq} per a la seqüència, p. ex. OT-{seq:05}',
    purchaseOrderNumberingFormat: 'Format de número de comanda de compra',
    purchaseOrderNumberingHint: 'Inclou {seq} per a la seqüència, p. ex. OC-{seq:05}',
    receptionNumberingFormat: 'Format de número d\'albarà de compra',
    receptionNumberingHint: 'Inclou {seq} per a la seqüència, p. ex. AR-{seq:05}',
    salesOrderNumberingFormat: 'Format de numeració de comandes de venda',
    salesOrderNumberingHint: 'Inclou {seq} per a la seqüència, p. ex. CV-{seq:05}',
    shipmentNumberingFormat: 'Format de numeració d’albarans de venda',
    shipmentNumberingHint: 'Inclou {seq} per a la seqüència, p. ex. AV-{seq:05}',
    purchaseReturnNumberingFormat: 'Format de número d’albarà de devolució',
    purchaseReturnNumberingHint: 'Inclou {seq} per a la seqüència, p. ex. DV-{seq:05}',
    supplierCodeNumberingFormat: 'Format de codi de proveïdor',
    supplierCodeNumberingHint:
      'Inclou {seq} per a la seqüència, p. ex. PRV-{seq:04}. Només s’aplica quan l’alta no porta codi.',
    clientCodeNumberingFormat: 'Format de codi de client',
    clientCodeNumberingHint:
      'Inclou {seq} per a la seqüència, p. ex. CLI-{seq:04}. Només s’aplica quan l’alta no porta codi.',
    zoneCodeNumberingFormat: 'Format de codi de zona',
    zoneCodeNumberingHint:
      'Inclou {seq} per a la seqüència, p. ex. ZON-{seq:03}. Només s’aplica quan l’alta no porta codi.',
    numberingPlaceholderRequired: 'Cal incloure el marcador {seq}',
    defaultMermaThresholdPct: 'Llindar de merma per defecte (%)',
    defaultMermaThresholdHint: 'Avís informatiu quan la merma supera aquest percentatge. Mai bloqueja.',
    weighingDeviationThresholdPct: 'Llindar de desviació de pesatge (%)',
    weighingDeviationThresholdHint: 'Avís visual quan un pesatge es desvia més d’aquest percentatge.',
    expiryAlertLeadDays: 'Dies d’avís previ de caducitat',
    expiryAlertLeadDaysHint: 'Dies abans de la caducitat en què s’emet un avís.',
    operatorSessionHours: 'Durada de la sessió d’operari (hores)',
    operatorSessionHoursHint:
      'Límit del torn a planta: passat aquest temps, planta torna a demanar el PIN.',
    operatorIdleMinutes: 'Bloqueig per inactivitat (minuts)',
    operatorIdleMinutesHint:
      'Amb l’app amb PIN, planta es bloqueja i demana el PIN si ningú no la toca durant aquest temps. 0 ho desactiva.',
    idleMinutesRange: 'Ha de ser un enter entre 0 i 240',
    quietHoursStart: 'Inici de les hores de silenci',
    quietHoursEnd: 'Fi de les hores de silenci',
    quietHoursHint: 'Franja sense notificacions push (HH:MM). Deixa-ho buit per desactivar-la.',
    pctRange: 'Ha de ser entre 0 i 100',
    timeFormat: 'Format d’hora no vàlid (HH:MM)',
    // Shifts
    shiftsTitle: 'Torns',
    shiftsSubtitle: 'Torns de treball de l’obrador',
    newShift: 'Nou torn',
    editShift: 'Edita el torn',
    shiftName: 'Nom del torn',
    shiftStart: 'Hora d’inici',
    shiftEnd: 'Hora de fi',
    shiftSortOrder: 'Ordre',
    colStart: 'Inici',
    colEnd: 'Fi',
    colOrder: 'Ordre',
    deleteShiftTitle: 'Dona de baixa el torn',
    deleteShiftBody: 'El torn deixarà d’estar disponible per a la planificació. La baixa és lògica i reversible.',
    // Estructura de l'obrador (zones · seccions · ubicacions)
    structureTitle: 'Estructura de l’obrador',
    structureSubtitle: 'On es treballa i on són les existències. Amb zones n’hi ha prou; seccions i ubicacions, només si calen.',
    structureEmpty: 'Encara no hi ha cap zona. Crea’n una i l’obrador ja podrà dir on són les existències.',
    newZone: 'Nova zona',
    editZone: 'Edita la zona',
    zoneCode: 'Codi',
    zoneCodeHint: 'Deixa-ho buit i Obrava el genera (p. ex. ZON-001). És el codi amb què l’importador d’articles hi lliga la zona.',
    zoneName: 'Nom de la zona',
    deleteZoneTitle: 'Dona de baixa la zona',
    deleteZoneBody: 'La zona deixarà d’estar disponible per a noves existències. L’historial es conserva i la baixa és reversible.',
    zoneHasChildren: 'Per donar-la de baixa, primer dona de baixa les seves seccions i ubicacions.',
    areaFunction: 'Tipus funcional',
    areaStockMode: 'Comportament logístic',
    storageCondition: 'Conservació',
    storageConditionNone: 'Sense especificar',
    allowsLocations: 'Permet ubicacions',
    allowsLocationsHint: 'Activa-ho per poder dir el punt exacte (prestatge, palet, cambra) on és cada existència.',
    allowsLocationsLocked: 'Té ubicacions actives: dona-les de baixa abans de desactivar-ho.',
    addSection: 'Afegeix secció',
    addLocation: 'Afegeix ubicacions',
    newSection: 'Nova secció',
    editSection: 'Edita la secció',
    sectionZone: 'Zona',
    sectionZonePlaceholder: 'Tria la zona',
    sectionCode: 'Codi',
    sectionCodeHint: 'Opcional.',
    sectionName: 'Nom de la secció',
    sectionDescription: 'Descripció',
    colDescription: 'Descripció',
    deleteSectionTitle: 'Dona de baixa la secció',
    deleteSectionBody: 'La secció deixarà d’estar disponible. L’historial es conserva i la baixa és reversible.',
    sectionHasLocations: 'Per donar-la de baixa, primer dona de baixa les seves ubicacions.',
    unassignedSectionsTitle: 'Seccions sense zona',
    unassignedSectionsBody: 'Aquestes seccions són d’abans de l’estructura. Edita-les per posar-les dins d’una zona.',
    newLocations: 'Noves ubicacions',
    editLocation: 'Edita la ubicació',
    locationParent: 'Dins de',
    locationCodes: 'Codis',
    locationCodesHint: 'Una ubicació per línia. El codi pot dir el passadís, el prestatge i el nivell (p. ex. C-02-03).',
    locationCode: 'Codi',
    locationDescription: 'Descripció',
    locationDescriptionHint: 'Opcional. Per exemple, «Prestatge de les farines».',
    locationDuplicate: 'Algun d’aquests codis ja el fa servir una altra ubicació activa.',
    locationCodesRepeated: 'Hi ha codis repetits a la llista.',
    deleteLocationTitle: 'Dona de baixa la ubicació',
    deleteLocationBody: 'La ubicació deixarà d’estar disponible per a noves existències. L’historial es conserva i la baixa és reversible.',
    locationsCount_one: '{{count}} ubicació',
    locationsCount_other: '{{count}} ubicacions',
    noLocations: 'Encara no hi ha cap ubicació.',
    // Reordenar l'estructura (OBR-89)
    reorder: 'Reordena',
    reorderDone: 'Fet',
    reorderHint: 'Arrossega pels punts o fes servir les fletxes. L’ordre es desa sol i el segueixen els selectors de lloc.',
    moveUp: 'Puja',
    moveDown: 'Baixa',
    dragToReorder: 'Arrossega per canviar l’ordre',
    reorderError: 'No s’ha pogut desar l’ordre. S’ha tornat a l’ordre desat.',
    colType: 'Tipus',
    // Categories (article_categories)
    categoriesTitle: 'Categories d’article',
    categoriesSubtitle: 'Categories del catàleg de l’obrador, editables i ampliables',
    newCategory: 'Nova categoria',
    editCategory: 'Edita la categoria',
    categoryName: 'Nom de la categoria',
    categoryParent: 'Categoria pare',
    categoryNoParent: 'Cap (categoria principal)',
    categoryParentHint: 'Opcional. Deixa-ho en «Cap» per a una categoria principal.',
    categoryParentLocked: 'Aquesta categoria ja té subcategories i no pot ser-ne una.',
    colParent: 'Categoria pare',
    deleteCategoryTitle: 'Dona de baixa la categoria',
    deleteCategoryBody:
      'La categoria deixarà d’estar disponible per a nous articles. Els articles ja assignats la conserven. La baixa és lògica i reversible.',
    // Units (units of measure)
    unitsTitle: 'Unitats de mesura',
    unitsSubtitle: 'Unitats de l’obrador; s’usen a articles, receptes, existències i etiquetes',
    newUnit: 'Nova unitat',
    editUnit: 'Edita la unitat',
    unitCode: 'Codi',
    unitCodeHint: 'Símbol curt que es mostra al costat de les quantitats (p. ex. kg, L, Un)',
    unitNameLabel: 'Nom',
    unitDimension: 'Magnitud',
    unitIsBase: 'Unitat base de la magnitud',
    unitIsBaseHint: 'Marca-la si és la unitat de referència d’aquesta magnitud.',
    unitCodeDuplicate: 'Ja existeix una unitat amb aquest codi',
    colCode: 'Codi',
    colDimension: 'Magnitud',
    colBase: 'Base',
    deleteUnitTitle: 'Dona de baixa la unitat',
    deleteUnitBody:
      'La unitat deixarà d’estar disponible per a nous articles i receptes. Els que ja la fan servir la conserven. La baixa és lògica i reversible.',
    // Taxes (impostos aplicables)
    taxesTitle: 'Impostos',
    taxesSubtitle:
      'Impostos de l’obrador; la fitxa d’article en tria un en lloc d’escriure’n el tipus',
    newTax: 'Nou impost',
    editTax: 'Edita l’impost',
    taxName: 'Nom',
    taxNameHint: 'Com se’n parla a l’obrador (p. ex. IVA 21 %, IVA reduït)',
    taxRate: 'Tipus (%)',
    taxIsDefault: 'Impost per defecte',
    taxIsDefaultHint: 'És el que s’ofereix en una fitxa nova. Només un per obrador.',
    taxNameDuplicate: 'Ja existeix un impost amb aquest nom',
    colRate: 'Tipus',
    deleteTaxTitle: 'Dona de baixa l’impost',
    deleteTaxBody:
      'L’impost deixarà d’estar disponible per a noves fitxes. Els articles que ja el fan servir el conserven. La baixa és lògica i reversible.',
    // Packaging / logistic units (packaging_units)
    packagingUnitsTitle: 'Unitats d’embalatge',
    packagingUnitsSubtitle:
      'Unitats d’embalatge i logístiques de l’obrador (sac, caixa, palet…); s’usen a envasat i expedicions',
    newPackagingUnit: 'Nova unitat d’embalatge',
    editPackagingUnit: 'Edita la unitat d’embalatge',
    packagingUnitNameHasDigits:
      '«{{name}}» porta la mida al nom. A Obrava la unitat és genèrica —«Sac»— i la mida va a la conversió de cada article: el mateix sac fa 25 kg de farina i 10 de sal. Pots desar-la igualment si és el que vols.',
    packagingUnitName: 'Nom',
    packagingUnitNameDuplicate: 'Ja existeix una unitat d’embalatge amb aquest nom',
    deletePackagingUnitTitle: 'Dona de baixa la unitat d’embalatge',
    deletePackagingUnitBody:
      'La unitat d’embalatge deixarà d’estar disponible. La baixa és lògica i reversible.',
    // Clients
    clientsTitle: 'Clients',
    clientsSubtitle: 'Clients de l’obrador; els compradors dels productes acabats',
    newClient: 'Nou client',
    importClients: 'Importa',
    clientCode: 'Codi',
    clientCodeHint: 'Deixa-ho buit i Obrava el genera (p. ex. CLI-0001).',
    clientCommercialName: 'Nom comercial',
    clientCommercialNameHint: 'Opcional. Només si difereix de la raó social.',
    editClient: 'Edita el client',
    clientName: 'Nom',
    clientTaxId: 'NIF / CIF',
    clientTaxIdHint: 'Opcional. Identificació fiscal del client.',
    clientContactName: 'Persona de contacte',
    clientPhone: 'Telèfon',
    clientEmail: 'Correu electrònic',
    clientAddress: 'Adreça fiscal',
    clientAddressHint: 'Opcional. Adreça fiscal o de facturació del client.',
    clientNotes: 'Notes',
    clientNotesHint: 'Opcional. Observacions, condicions de lliurament…',
    sectionInstructions: 'Instruccions',
    clientShippingTitle: 'Punts d’enviament',
    clientShippingHint:
      'Punts d’entrega del client (botigues, centres, locals…). La marcada per defecte es proposa a les comandes de venda.',
    clientShippingName: 'Nom del punt d’entrega',
    clientShippingAddress: 'Adreça d’entrega',
    clientShippingDefault: 'Per defecte',
    clientShippingAdd: 'Afegeix un punt d’enviament',
    clientShippingEdit: 'Edita',
    clientShippingEmpty: 'Aquest client no té cap punt d’enviament.',
    clientShippingNew: 'Nou punt d’enviament',
    clientShippingEditTitle: 'Punt d’enviament',
    clientShippingDefaultBadge: 'Predeterminat',
    clientShippingNeedsAddress: 'El punt d’enviament necessita nom i adreça.',
    clientShippingIncomplete: 'Cada punt d’enviament necessita nom i adreça.',
    clientEmailInvalid: 'Correu electrònic no vàlid',
    deleteClientTitle: 'Dona de baixa el client',
    deleteClientBody:
      'El client deixarà d’estar disponible. La baixa és lògica i reversible.',
    // Suppliers (proveïdors)
    suppliersTitle: 'Proveïdors',
    suppliersSubtitle: 'Proveïdors de l’obrador; alimenten el proveïdor per defecte dels articles del catàleg',
    newSupplier: 'Nou proveïdor',
    editSupplier: 'Edita el proveïdor',
    importSuppliers: 'Importa',
    supplierCode: 'Codi',
    supplierCodeHint: 'Deixa-ho buit i Obrava el genera (p. ex. PRV-0001).',
    supplierName: 'Nom o raó social',
    supplierCommercialName: 'Nom comercial',
    supplierCommercialNameHint: 'Opcional. Només si difereix de la raó social.',
    supplierTaxId: 'NIF / CIF',
    supplierTaxIdHint: 'Opcional. Identificació fiscal del proveïdor.',
    supplierContactName: 'Persona de contacte',
    supplierPhone: 'Telèfon',
    supplierEmail: 'Correu general',
    supplierAddress: 'Adreça principal',
    supplierAddressHint: 'Opcional. Adreça fiscal del proveïdor.',
    supplierNotes: 'Notes',
    colDeliveryDays: 'Dies d\'entrega',
    supplierDeliveryDays: 'Dies habituals d\'entrega',
    supplierDeliveryDaysHint:
      'Els dies que aquest proveïdor reparteix. En crear una comanda, la data d\'entrega prevista es proposarà sola amb el primer dia que toqui; sempre la podràs canviar.',
    supplierEmailInvalid: 'Correu electrònic no vàlid',
    supplierQualityEmail: 'Correu de qualitat',
    supplierQualityEmailHint: 'Opcional. On van les sol·licituds i renovacions de fitxes tècniques. Independent del correu general i del de comandes.',
    supplierQualityEmailInvalid: 'El correu de qualitat no és vàlid.',
    colTaxId: 'NIF / CIF',
    colContact: 'Contacte',
    deleteSupplierTitle: 'Dona de baixa el proveïdor',
    deleteSupplierBody:
      'El proveïdor deixarà d’estar disponible per a nous articles i noves compres. Els vincles amb articles (referència, preu, unitat de compra), les comandes i els albarans el conserven, marcats com a inactius. Si era el proveïdor principal d’un article, l’article quedarà sense proveïdor principal fins que en triïs un altre: no se n’assigna cap de manera automàtica. La baixa és lògica i reversible.',
    // Users & roles
    usersTitle: 'Usuaris',
    usersSubtitle: 'Membres de l’obrador i assignació de rols',
    colEmail: 'Correu',
    colAccess: 'Accés',
    appPlanta: 'App planta',
    appBackoffice: 'Backoffice',
    noAccess: 'Cap',
    appsLegend:
      'Cada columna mostra amb quins rols entra a aquella app; un guió vol dir que no hi entra. Els rols se sumen: un administrador que també treballi a planta porta, a més, el rol d’Operari.',
    colSection: 'Secció',
    memberSection: 'Secció de treball',
    memberSectionHint:
      'Determina quines OT arriben al seu dispositiu: rebrà les assignades a ell i les assignades a aquesta secció.',
    noSection: 'Sense secció',
    assignRole: 'Assigna rol',
    revokeRole: 'Revoca rol',
    selectRole: 'Selecciona un rol',
    noRoles: 'Sense rols assignats',
    rolesUpdated: 'Rols actualitzats',
    rolesError: 'No s’han pogut actualitzar els rols',
    inactiveUser: 'Inactiu',
    // Add member
    addUserTitle: 'Afegeix un usuari',
    addUserName: 'Nom complet',
    addUserEmail: 'Correu',
    addUserRole: 'Rol inicial',
    addUserPassword: 'Contrasenya (opcional)',
    addUserPasswordPlaceholder: 'Mínim 6 caràcters',
    addUserPasswordHint:
      'Si poses una contrasenya, l’usuari hi entrarà directament (comunica-la-hi tu). Si la deixes en blanc, rebrà un correu per establir-la.',
    addUserCta: 'Afegeix l’usuari',
    addUserSubmitting: 'Afegint…',
    addUserNameRequired: 'Cal indicar el nom',
    addUserEmailInvalid: 'Correu no vàlid',
    addUserPasswordWeak: 'La contrasenya ha de tenir com a mínim 6 caràcters',
    addUserCreated: 'Usuari afegit. S’ha enviat un correu a {{email}} per establir la contrasenya.',
    addUserCreatedWithPassword: 'Usuari afegit ({{email}}) amb la contrasenya indicada. Ja pot iniciar sessió.',
    addUserAlreadyMember: 'Aquest correu ja és membre de l’obrador.',
    addUserMenuAccessDenied:
      'Tens amagada la gestió d’usuaris en aquest obrador: no hi pots afegir ningú.',
    addUserError: 'No s’ha pogut afegir l’usuari.',
    sortBy: 'Ordena per',
    sortByRole: 'Rol',
    sortByName: 'Nom',
    searchNoResults: 'Cap usuari coincideix amb la cerca.',
    editRolesTitle: 'Rols i permisos',
    deactivateUserTitle: 'Dona de baixa l’usuari',
    deactivateUserBody:
      'Es bloqueja l’accés d’aquest usuari. Es conserva l’historial i és reversible. Indica el motiu de la baixa.',
    memberLastAdminError:
      'No es pot fer: l’obrador es quedaria sense cap administrador que pugui gestionar els usuaris.',
    memberChangeError: 'No s’ha pogut fer el canvi.',
    // Menu access
    colMenuAccess: 'Accessos',
    menuAccessNoneHidden: 'Tot visible',
    menuAccessHidden_one: '{{count}} amagada',
    menuAccessHidden_other: '{{count}} amagades',
    menuAccessUnknown: 'No s’ha pogut llegir',
    menuAccessSelfHint: 'No pots canviar els teus propis accessos.',
    menuAccessDialogTitle: 'Accessos de {{name}}',
    menuAccessIntro:
      'Tria què veu aquesta persona al menú del Backoffice. Una opció amagada desapareix del menú i no s’hi pot entrar.',
    menuAccessScopeNote:
      'Amagar una opció és una restricció d’ús, no de seguretat: només la gestió d’usuaris i els rols de les persones es fan complir també al servidor.',
    menuAccessIntroPlanta:
      'Tria a quines opcions de l’App de planta té accés aquest operari: Ordres, Recepció, Vendes i Avisos. Una opció amagada desapareix de la barra de planta.',
    menuAccessScopeNotePlanta:
      'Amagar una opció és una restricció d’ús, no de seguretat. Les opcions es desen ara i l’App de planta les aplicarà a partir de la propera versió.',
    menuAccessNotForRole: 'No disponible pel rol',
    menuAccessProtectedHint: 'L’obrador ha de tenir sempre un administrador que la vegi.',
    menuAccessError: 'No s’han pogut desar els accessos.',
    menuAccessErrorLastAdmin:
      'No es pot amagar: l’obrador es quedaria sense cap administrador que pugui gestionar els usuaris.',
    menuAccessErrorSelfEdit:
      'No pots canviar els teus propis accessos. Demana-ho a un altre administrador.',
    menuAccessErrorDenied:
      'Tens amagada la gestió d’usuaris en aquest obrador: no pots canviar els accessos de ningú.',
    menuAccessErrorForbidden: 'Només un administrador pot canviar els accessos.',
    menuAccessErrorNotFound: 'Aquesta persona ja no és a l’obrador.',
    menuAccessErrorInvalidKeys:
      'Alguna de les opcions no es pot amagar des d’aquí. Recarrega la pàgina i torna-ho a provar.',
    // Operaris amb PIN (OBR-25, F5)
    operatorsTitle: 'Operaris',
    operatorsSubtitle: 'Persones que entren a planta amb un PIN',
    newOperator: 'Nou operari',
    searchTeam: 'Cerca per nom, correu o tauleta',
    colPin: 'PIN',
    colRoles: 'Rols',
    colLastUse: 'Últim ús',
    pinStatusActive: 'Actiu',
    pinStatusPending: 'Pendent',
    pinWithdrawn: 'PIN retirat',
    pinNeverUsed: 'Mai utilitzat',
    pinPendingHint: 'Sense PIN viu',
    pinNotShown: 'Regenera’l per veure’l',
    operatorsPendingBanner_one:
      'Hi ha {{count}} persona sense PIN viu. Regenera-li el PIN o dona-la de baixa.',
    operatorsPendingBanner_other:
      'Hi ha {{count}} persones sense PIN viu. Regenera’ls el PIN o dona’ls de baixa.',
    operatorsEmpty: 'Encara no hi ha cap operari. Crea’n un i lliura-li el PIN en mà.',
    operatorDeletedReason: 'Motiu: {{reason}}',
    operatorRegenerate: 'Regenera el PIN',
    newOperatorTitle: 'Nou operari',
    newOperatorName: 'Nom (opcional)',
    newOperatorNameHint: 'Si el deixes buit, es dirà «Operari» amb el número següent.',
    newOperatorRoles: 'Rols',
    newOperatorRolesHint: 'Només els rols de planta. Els del Backoffice es donen a Usuaris.',
    newOperatorRoleRequired: 'Tria almenys un rol.',
    newOperatorLinked: 'Aquesta persona ja té usuari amb correu',
    newOperatorLinkedNone: 'No',
    newOperatorLinkedHint:
      'Només serveix per recordar que és la mateixa persona; l’historial que ja té es queda amb el seu usuari.',
    newOperatorCta: 'Crea i mostra el PIN',
    pinRevealTitle: 'PIN de {{name}}',
    pinRevealBody:
      'Lliura-li el PIN en mà. També el tens sempre a la llista d’Operaris.',
    pinRevealWhere: 'Entra a planta des de qualsevol tauleta escrivint aquest PIN.',
    pinReveal: 'Mostra',
    pinHide: 'Amaga',
    pinCopy: 'Copia',
    pinCopied: 'Copiat',
    pinAutoMask: 'Es tapa sol d’aquí a un minut.',
    pinRevealDone: 'Fet',
    regeneratePinTitle: 'Regenera el PIN de {{name}}',
    regeneratePinBody:
      'El PIN actual deixarà de funcionar a l’acte. Se’n crearà un de nou, que veuràs a la llista.',
    regeneratePinCta: 'Regenera',
    revokeOperatorTitle: 'Dona de baixa {{name}}',
    revokeOperatorBody:
      'Deixarà d’entrar a planta a l’acte i el seu PIN es retira. La baixa és lògica: l’historial es conserva i es pot reactivar.',
    reactivateOperatorTitle: 'Reactiva {{name}}',
    reactivateOperatorBody:
      'Tornarà a la llista amb un PIN nou (l’antic no es recupera), que veuràs a la llista.',
    reactivateOperatorCta: 'Reactiva i mostra el PIN',
    editOperatorTitle: 'Edita {{name}}',
    editOperatorNameSave: 'Desa el nom',
    // Errors de la gestió d’operaris i d’aparells
    pinErrorTryLater: 'No s’ha pogut completar ara mateix. Torna-ho a provar.',
    pinErrorUnauthorized: 'La sessió ha caducat. Torna a entrar.',
    pinErrorForbidden: 'Només un administrador pot gestionar els operaris.',
    pinErrorMenuAccessDenied:
      'Tens amagada l’opció d’Operaris (o la de Configuració): no pots fer això.',
    pinErrorUsePinManage: 'Els operaris amb PIN es gestionen des de la pestanya Operaris.',
    pinErrorNotFound:
      'No s’ha trobat: potser ja no existeix. Refresca la pantalla i torna-ho a provar.',
    pinErrorReasonRequired: 'Cal escriure un motiu.',
    pinErrorRoleNotAllowed: 'Aquest rol no es pot donar a un operari amb PIN.',
    pinErrorAlreadyIssued: 'Aquesta petició ja s’havia fet. Si no vas veure el PIN, regenera’l.',
    pinErrorCreatedNoPin:
      'La persona ja s’ha creat, però el PIN no s’ha lliurat. Tanca aquest diàleg i regenera-li el PIN des de la llista.',
    pinErrorAlreadyPin: 'Aquesta persona ja té un PIN.',
    pinErrorDuplicateName:
      'Ja hi ha un operari amb aquest nom: canvia el nom d’un dels dos i torna-ho a provar.',
    pinErrorPersonDeleted: 'Aquesta persona està donada de baixa. Reactiva-la primer.',
    pinErrorPersonInactive: 'Aquesta persona està desactivada.',
    pinErrorNotDeleted: 'Aquesta persona no està donada de baixa.',
    pinErrorNoLivePin: 'Aquesta persona no té cap PIN viu.',
    pinErrorConvertPlantaOnly: 'Només es pot passar a PIN qui treballa només a planta.',
    pinErrorServer: 'Hi ha un problema al servidor. Avisa l’equip d’Obrava.',
    pinErrorGeneric: 'No s’ha pogut fer. Torna-ho a provar.',
    // Usuaris: «Passa a PIN» i «Crea també un PIN» (OBR-25, F5)
    convertToPinAction: 'Passa a PIN',
    createAlsoPinAction: 'Crea també un PIN',
    convertToPinTitle: 'Passa {{name}} a PIN',
    convertToPinBody:
      'Deixarà d’entrar amb correu i contrasenya i entrarà a planta amb un PIN. L’historial es conserva i el seu compte amb correu no s’esborra.',
    convertToPinCta: 'Passa a PIN i mostra’l',
    accessCodeTitle: 'Codi d’accés',
    accessCodeHint: 'Rebrà un correu amb un enllaç per triar-ne un de nou. Fins que ho faci, el codi actual continua sent vàlid.',
    accessCodeAction: 'Envia un enllaç per canviar el codi d’accés',
    accessCodeSending: 'Enviant…',
    accessCodeSent: 'S’ha enviat a {{email}} un enllaç per canviar el codi d’accés.',
    accessCodeError: 'No s’ha pogut enviar l’enllaç.',
    accessCodeMenuAccessDenied: 'Tens amagada la gestió d’usuaris en aquest obrador: no hi pots enviar codis.',
    accessCodeUserInactive: 'Aquesta persona ja no és activa a l’obrador.',
    accessCodeNoEmail: 'Aquesta persona no té correu: entra amb PIN.',
    accessCodeAuditError: 'S’ha enviat l’enllaç, però no s’ha pogut registrar a l’activitat.',
    pinOfferTitle: 'PIN per a planta',
    pinOfferConvertHint:
      'Treballa només a planta: pot entrar-hi amb un PIN en lloc de correu i contrasenya.',
    pinOfferAlsoHint:
      'Té accés al Backoffice. Si també treballa a planta, se li pot crear un PIN a part.',
    createAlsoPinTitle: 'Crea també un PIN per a {{name}}',
    createAlsoPinBody:
      'Es crea una persona nova que entra a planta amb PIN. El seu usuari amb correu no canvia, i el que faci a planta amb el PIN quedarà registrat a nom de la persona nova.',
    // Devices
    devicesTitle: 'Dispositius',
    devicesSubtitle: 'Maquinari registrat de l’obrador',
    newDevice: 'Nou dispositiu',
    editDevice: 'Edita el dispositiu',
    deviceName: 'Nom del dispositiu',
    deviceType: 'Tipus',
    deviceIdentifier: 'Identificador',
    deviceIdentifierHint: 'Número de sèrie, MAC o adreça (opcional)',
    deviceStatus: 'Estat',
    colIdentifier: 'Identificador',
    colPrinterAddress: 'Adreça',
    printerNotConfigured: 'Sense adreça',
    devicePrinterPlantaNote:
      'De moment les etiquetes s’imprimeixen des de l’app de planta, amb la tauleta a la mateixa xarxa que la impressora.',
    devicePrinterHost: 'Adreça de la impressora',
    devicePrinterHostHint:
      'IP o nom a la xarxa de l’obrador, per exemple 192.168.1.80. Sense http:// ni port.',
    devicePrinterHostInvalid:
      'Adreça no vàlida. Escriu una IP (192.168.1.80) o un nom, sense http:// ni port.',
    devicePrinterPort: 'Port',
    devicePrinterPortHint: 'Normalment 9100',
    devicePrinterPortInvalid: 'El port ha de ser un número entre 1 i 65535.',
    devicePrinterLabelWidth: 'Amplada de l’etiqueta (mm)',
    devicePrinterLabelHeight: 'Alçada de l’etiqueta (mm)',
    devicePrinterSizeHint:
      'La mida del rotlle carregat. Si es deixa buit, s’imprimeix en 50 × 30 mm.',
    devicePrinterSizeInvalid: 'Cal l’amplada i l’alçada, entre 10 i 200 mm.',
    deleteDeviceTitle: 'Dona de baixa el dispositiu',
    deleteDeviceBody: 'El dispositiu deixarà d’estar disponible. La baixa és lògica i reversible.',
    tabArticleFamilies: 'Famílies d’article',
    tabIngredients: 'Ingredients',
    tabProcesses: 'Processos',
    tabLegislation: 'Legislació i normativa',
    tabResources: 'Recursos',
    tabLabelTemplates: 'Formats d’etiqueta',
    masterName: 'Nom',
    conservationConditionsTitle: 'Condicions de conservació',
    conservationConditionsSubtitle: 'Condicions de conservació dels articles (ambient, refrigerat, congelat…); s’usen a la fitxa d’article i a l’etiqueta',
    newConservationCondition: 'Nova condició de conservació',
    editConservationCondition: 'Edita la condició de conservació',
    conservationConditionDuplicate: 'Ja existeix una condició de conservació amb aquest nom',
    deleteConservationConditionTitle: 'Dona de baixa la condició de conservació',
    deleteConservationConditionBody: 'La condició deixarà d’estar disponible per a nous articles. Els articles que ja la fan servir la conserven. La baixa és lògica i reversible.',
    usageTypesTitle: 'Tipus d’ús',
    usageTypesSubtitle: 'Tipus d’ús dels articles (consum directe, ús en elaboració…); s’usen a la fitxa d’article',
    newUsageType: 'Nou tipus d’ús',
    editUsageType: 'Edita el tipus d’ús',
    usageTypeDuplicate: 'Ja existeix un tipus d’ús amb aquest nom',
    deleteUsageTypeTitle: 'Dona de baixa el tipus d’ús',
    deleteUsageTypeBody: 'El tipus d’ús deixarà d’estar disponible per a nous articles. Els articles que ja el fan servir el conserven. La baixa és lògica i reversible.',
    preparationModesTitle: 'Modes de preparació',
    preparationModesSubtitle: 'Modes de preparació dels articles (llest per consumir, coure abans…); s’usen a la fitxa d’article',
    newPreparationMode: 'Nou mode de preparació',
    editPreparationMode: 'Edita el mode de preparació',
    preparationModeDuplicate: 'Ja existeix un mode de preparació amb aquest nom',
    deletePreparationModeTitle: 'Dona de baixa el mode de preparació',
    deletePreparationModeBody: 'El mode de preparació deixarà d’estar disponible per a nous articles. Els articles que ja el fan servir el conserven. La baixa és lògica i reversible.',
    containerTypesTitle: 'Tipus d’envàs',
    containerTypesSubtitle: 'Envasos de venda (pot, ampolla, safata…); diferents de les unitats d’embalatge logístiques',
    newContainerType: 'Nou tipus d’envàs',
    editContainerType: 'Edita el tipus d’envàs',
    containerTypeDuplicate: 'Ja existeix un tipus d’envàs amb aquest nom',
    deleteContainerTypeTitle: 'Dona de baixa el tipus d’envàs',
    deleteContainerTypeBody: 'El tipus d’envàs deixarà d’estar disponible per a nous articles. Els articles que ja el fan servir el conserven. La baixa és lògica i reversible.',
    complianceItemsTitle: 'Legislació i compliment',
    complianceItemsSubtitle: 'Ítems de legislació i compliment («No conté OMG», certificacions…); s’usen a la fitxa tècnica',
    newComplianceItem: 'Nou ítem de compliment',
    editComplianceItem: 'Edita l’ítem de compliment',
    complianceItemDuplicate: 'Ja existeix un ítem de compliment amb aquest nom',
    deleteComplianceItemTitle: 'Dona de baixa l’ítem de compliment',
    deleteComplianceItemBody: 'L’ítem deixarà d’estar disponible per a nous articles. Els articles que ja el fan servir el conserven. La baixa és lògica i reversible.',
    ingredientsTitle: 'Ingredients',
    ingredientsSubtitle: 'Ingredients de la composició declarada dels articles; també es poden crear al vol des de la fitxa d’article',
    newIngredient: 'Nou ingredient',
    editIngredient: 'Edita l’ingredient',
    ingredientDuplicate: 'Ja existeix un ingredient amb aquest nom',
    deleteIngredientTitle: 'Dona de baixa l’ingredient',
    deleteIngredientBody: 'L’ingredient deixarà d’estar disponible per a nous articles. Els articles que ja el declaren el conserven. La baixa és lògica i reversible.',
    processesTitle: 'Processos',
    processesSubtitle: 'Processos d’elaboració: la plantilla dels passos d’una recepta (durada, temperatura, control, instruccions i recursos per defecte)',
    newProcess: 'Nou procés',
    editProcess: 'Edita el procés',
    processDescription: 'Descripció',
    processDuration: 'Durada (min)',
    processTemperature: 'Temperatura (°C)',
    processControlType: 'Tipus de control',
    processInstructions: 'Instruccions de base',
    processInstructionsHint:
      'Es copien al pas quan se’n crea un des d’aquest procés; després es poden adaptar a cada recepta.',
    processResources: 'Recursos per defecte',
    processResourcesHint:
      'Els tipus de recurs que aquest procés proposa. Es copien al pas i després es poden canviar a cada recepta.',
    colProcessResources: 'Recursos',
    processResourceDuplicate: 'Aquest tipus de recurs ja és a la llista: dues unitats són una sola línia amb quantitat 2',
    processDuplicateDescription: 'Ja existeix un procés amb aquesta descripció',
    colProcessDuration: 'Durada (min)',
    colProcessTemperature: 'Temperatura (°C)',
    colProcessControl: 'Control',
    deleteProcessTitle: 'Dona de baixa el procés',
    deleteProcessBody: 'El procés deixarà d’estar disponible per a noves receptes. Les receptes que ja el fan servir el conserven. La baixa és lògica i reversible.',
    legislationTitle: 'Legislació i normativa',
    legislationSubtitle: 'Textos legals i normatius de l’obrador; s’imprimeixen, en aquest ordre, com a text final de totes les fitxes tècniques',
    newLegislationNote: 'Nou text',
    editLegislationNote: 'Edita el text',
    legislationNoteTitle: 'Títol',
    legislationNoteBody: 'Text',
    legislationNoteSortOrder: 'Ordre',
    colLegislationBody: 'Text',
    colLegislationSortOrder: 'Ordre',
    deleteLegislationNoteTitle: 'Dona de baixa el text',
    deleteLegislationNoteBody: 'El text deixarà d’aparèixer a les noves fitxes tècniques. Les fitxes ja aprovades el conserven. La baixa és lògica i reversible.',
    labelTemplatesTitle: 'Formats d’etiqueta',
    labelTemplatesSubtitle: 'Formats d’etiqueta de l’obrador; l’etiqueta es genera en tancar l’OT o en envasar, mai des de la fitxa tècnica',
    newLabelTemplate: 'Nou format d’etiqueta',
    editLabelTemplate: 'Edita el format d’etiqueta',
    labelTemplateName: 'Nom',
    labelTemplateType: 'Tipus d’etiqueta',
    labelType_internal_lot: 'Lot intern',
    labelType_sales_legal: 'Etiqueta legal de venda',
    labelTemplateIsDefault: 'Format per defecte del seu tipus',
    labelTemplateIsDefaultHint: 'En marcar-lo, substitueix el format per defecte anterior d’aquest tipus.',
    labelTemplateDefaultBadge: 'Per defecte',
    colDefault: 'Per defecte',
    deleteLabelTemplateTitle: 'Dona de baixa el format d’etiqueta',
    deleteLabelTemplateBody: 'El format deixarà d’estar disponible per a noves etiquetes. La baixa és lògica i reversible.',
    categoryArticleType: 'Tipologia',
    categoryArticleTypeHint: 'Opcional. Limita la categoria a una tipologia d’article; «Totes» la fa vàlida per a totes.',
    categoryAllTypes: 'Totes',
    colArticleType: 'Tipologia',
    skuSection: 'Codificació d’articles (SKU)',
    skuModeLabel: 'Assignació de codis',
    skuModeHint: 'Com s’assigna el codi (SKU) en crear un article nou.',
    skuMode_free: 'Manual: el codi s’escriu en crear l’article',
    skuMode_sequential: 'Automàtic: una numeració única per a tot el catàleg',
    skuMode_per_type: 'Automàtic per tipologia: una numeració per a cada tipus d’article',
    skuFormat: 'Màscara del codi',
    skuFormatHint: 'Inclou {seq} per a la seqüència, p. ex. ART-{seq:05}',
    salesBarcodeEnabled: 'Codis de barres en articles de venda',
    salesBarcodeEnabledHint: 'Mostra el camp de codi de barres (EAN) als articles que es venen.',
    tabBranding: 'Marca i FT',
    // Recursos (tipus de recurs + recursos)
    resourceTypesTitle: 'Tipus de recurs',
    resourceTypesSubtitle:
      'Com classifiques el que consumeix una elaboració (Forn, Operari, Cambra de fred…); defineix els teus abans de donar d’alta recursos',
    newResourceType: 'Nou tipus de recurs',
    editResourceType: 'Edita el tipus de recurs',
    resourceTypeDuplicate: 'Ja existeix un tipus de recurs amb aquest nom',
    deleteResourceTypeTitle: 'Dona de baixa el tipus de recurs',
    deleteResourceTypeBody:
      'El tipus deixarà d’estar disponible per a nous recursos i receptes. Els que ja el fan servir el conserven. La baixa és lògica i reversible.',
    resourcesTitle: 'Recursos',
    resourcesSubtitle:
      'El que ocupa una elaboració i no és matèria primera (Forn 1, Amassadora 02, un operari) i on és; la recepta en demana el tipus i la planificació tria quin',
    newResource: 'Nou recurs',
    editResource: 'Edita el recurs',
    resourceType: 'Tipus de recurs',
    selectResourceType: 'Selecciona un tipus',
    resourcePlace: 'On és',
    resourcePlaceHint:
      'Zona, secció o ubicació de l’obrador. Serveix per proposar el recurs més proper quan es planifica una OT.',
    resourcePlaceNone: 'Tot l’obrador',
    colResourcePlace: 'Lloc',
    resourceCostPerHour: 'Cost per hora (€)',
    resourceCostPerHourHint:
      'Opcional. Es desa per al càlcul de costos; encara no es calcula res.',
    resourceNotes: 'Notes',
    resourceDuplicate: 'Ja existeix un recurs amb aquest nom',
    colResourceType: 'Tipus',
    colResourceCost: 'Cost/hora',
    deleteResourceTitle: 'Dona de baixa el recurs',
    deleteResourceBody:
      'El recurs deixarà d’estar disponible per a noves OT. Les OT que ja el tenen triat el conserven. La baixa és lògica i reversible.',
    supplierOrdersSection: 'Enviament de comandes',
    supplierOrdersSectionHint: 'Com rep les comandes: per correu des d’Obrava, per WhatsApp o per altres mitjans.',
    supplierOrderChannel: 'Canal',
    supplierOrderChannelEmail: 'Correu electrònic (des d’Obrava)',
    supplierOrderChannelWhatsapp: 'WhatsApp (al número de comandes)',
    supplierOrderChannelManual: 'Altres mitjans (telèfon, en persona…)',
    supplierOrderEmails: 'Correus de comandes',
    supplierOrderEmailsHint: 'Separa’ls amb comes. Obligatori si el canal és correu: el correu general de contacte no el substitueix.',
    supplierOrderEmailsInvalid: 'Hi ha correus de comandes que no són vàlids: {{list}}',
    supplierOrderEmailsRequired: 'El canal és Correu electrònic: indica el correu de comandes (el correu general no el substitueix).',
    supplierOrderPhone: 'Telèfon/WhatsApp de comandes',
    supplierOrderPhoneHint: 'Número on el proveïdor rep les comandes, amb el prefix del país si és possible (+34 600 000 000). Independent del telèfon general.',
    supplierOrderPhoneInvalid: 'El telèfon/WhatsApp de comandes no és vàlid: fes servir de 7 a 15 xifres, amb espais i el prefix + si cal.',
    supplierOrderPhoneRequired: 'El canal és WhatsApp: indica el telèfon/WhatsApp de comandes (el telèfon general no el substitueix).',
    supplierOrderEmailsTooMany: 'Com a molt, deu correus de comandes.',
    supplierOrderLanguage: 'Idioma',
    supplierOrderLanguageSender: 'El de qui envia',
    supplierOrderSubject: 'Assumpte (opcional)',
    supplierOrderMessage: 'Missatge (opcional)',
    supplierOrderTemplateHint: 'Si el deixes en blanc, se’n posa un per defecte. Pots fer servir {{tokens}}.',
    supplierOrderWhatsappMessage: 'Missatge de WhatsApp (opcional)',
    supplierOrderWhatsappHint: 'Independent del missatge del correu. Si el deixes en blanc, se’n posa un per defecte. Pots fer servir {{tokens}}. El bloc d’articles va on posis {{linesToken}}, millor en una línia pròpia (si no el poses, va després del text). WhatsApp no pot dur el PDF: no hi parlis de cap adjunt.',
    supplierOrderWhatsappPreview: 'Previsualització (amb dades d’exemple)',
    supplierOrderWhatsappPreviewTooLong: 'Aquest text és massa llarg per a l’enllaç de WhatsApp: escurça’l.',
    supplierOrderWhatsappSampleArticle1: 'Farina de blat',
    supplierOrderWhatsappSampleArticle2: 'Llevat fresc',
    supplierOrderWhatsappSampleSupplier: 'Proveïdor d’exemple',
    supplierOrderAttachPdf: 'Adjunta el PDF de la comanda',
    supplierOrderAutoSend: 'Envia automàticament en confirmar',
    supplierOrderAutoSendHint: 'En confirmar una comanda d’aquest proveïdor, s’envia sola per correu.',
  },

  'backoffice.areaFunction': {
    storage: 'Magatzem',
    production: 'Producció',
    reception: 'Recepció',
    shipping: 'Expedició',
    quality: 'Qualitat',
    other: 'Altres',
  },

  'backoffice.areaStockMode': {
    stock: 'Estoc',
    provisioning: 'Aprovisionament',
    none: 'Sense existències',
  },

  'backoffice.areaStockModeHint': {
    stock: 'Hi viuen existències de manera habitual (magatzems, cambres).',
    provisioning: 'Zona de treball: hi arriba material per consumir-lo o transformar-lo, però no hi viu.',
    none: 'No s’hi controlen existències (neteja, vestidors…).',
  },

  'backoffice.storageCondition': {
    ambient: 'Ambient',
    refrigerated: 'Refrigerat',
    frozen: 'Congelat',
  },

  'backoffice.unitDimension': {
    mass: 'Massa',
    volume: 'Volum',
    count: 'Recompte',
    length: 'Longitud',
  },

  'backoffice.deviceType': {
    label_printer: 'Etiquetadora',
    scale: 'Bàscula',
    barcode_reader: 'Lector de codis',
    sensor: 'Sensor',
    tablet: 'Tauleta',
  },

  'backoffice.deviceStatus': {
    registered: 'Registrat',
    active: 'Actiu',
    offline: 'Sense connexió',
    error: 'Error',
    decommissioned: 'Retirat',
  },

  'backoffice.roleLabel': {
    admin: 'Administrador',
    production_manager: 'Responsable de producció',
    operator: 'Operari',
    quality: 'Qualitat',
    purchasing: 'Compres',
    sales: 'Vendes',
    viewer: 'Consulta',
  },

  'backoffice.traceability': {
    title: 'Traçabilitat',
    backward: 'Cap enrere',
    forward: 'Cap endavant',
    byLot: 'Per lot',
  },

  'platformConsole.common': {
    consoleName: 'Consola de Plataforma',
    themeLight: 'Clar',
    themeDark: 'Fosc',
    saving: 'Desant…',
    creating: 'Creant…',
    actionError: 'No s’ha pogut completar l’acció',
    loadError: 'No s’han pogut carregar les dades',
    never: 'Mai',
    lang_ca: 'Català',
    lang_es: 'Español',
    lang_en: 'English',
    settings: 'Configuració',
    language: 'Idioma',
    appearance: 'Aparença',
    themeSystem: 'Sistema',
    done: 'Fet',
    theme: 'Tema',
    account: 'Compte i perfil',
    copyVersion: 'Copia la versió',
    versionCopied: 'Versió copiada',
    collapse: 'Minimitza',
    openMenu: 'Obre el menú',
    closeMenu: 'Tanca el menú',
    comingSoon: 'Properament',
  },

  'platformConsole.auth': {
    consoleTitle: 'Consola de Plataforma Obrava',
    consoleSubtitle: 'Accés reservat a l’equip Obrava',
    signInTitle: 'Inicia sessió',
    email: 'Correu electrònic',
    password: 'Contrasenya',
    signInCta: 'Entra',
    signingIn: 'Entrant…',
    signInError: 'Credencials incorrectes',
    showPassword: 'Mostra la contrasenya',
    hidePassword: 'Amaga la contrasenya',
    notAuthorizedTitle: 'No autoritzat',
    notAuthorizedBody:
      'Aquest compte no és un super usuari de plataforma. La consola és exclusiva per a l’equip Obrava.',
    backToSignIn: 'Torna a iniciar sessió',
    mfaRequiredTitle: 'Verificació en dos passos',
    mfaRequiredSubtitle: 'Introdueix el codi de 6 dígits de la teva app d’autenticació',
    mfaEnrollTitle: 'Configura la verificació en dos passos',
    mfaEnrollSubtitle:
      'La verificació en dos passos és obligatòria per accedir a la consola. Afegeix aquest compte a la teva app d’autenticació (Google Authenticator, 1Password…).',
    mfaSecretLabel: 'Clau secreta',
    mfaUriLabel: 'URL d’configuració (otpauth)',
    mfaScanHint: 'Escaneja el codi QR amb la teva app d’autenticació (Google Authenticator, 1Password…), o introdueix la clau manualment.',
    mfaSecretHint:
      'Introdueix manualment la clau a la teva app, o enganxa l’URL otpauth. Després confirma amb el codi generat.',
    mfaCodeLabel: 'Codi de 6 dígits',
    mfaCodePlaceholder: '000000',
    mfaVerifyCta: 'Verifica',
    mfaVerifying: 'Verificant…',
    mfaEnrollCta: 'Activa i verifica',
    mfaInvalidCode: 'Codi incorrecte. Torna-ho a provar.',
    mfaEnrollError: 'No s’ha pogut iniciar la configuració',
    mfaCodeFormat: 'El codi ha de tenir 6 dígits',
    copy: 'Copia',
    copied: 'Copiat',
  },

  'platformConsole.obradors': {
    title: 'Obradors',
    subtitle: 'Gestió d’obradors (tenants) de la plataforma',
    create: 'Crea obrador',
    empty: 'Encara no hi ha cap obrador',
    emptyFiltered: 'Cap obrador coincideix amb els filtres',
    searchPlaceholder: 'Cerca per nom o NIF',
    showInactive: 'Mostra inactius',
    filterStatus: 'Estat',
    filterSector: 'Sector',
    filterPlan: 'Pla',
    colName: 'Nom comercial',
    colTaxId: 'NIF',
    colSector: 'Sector',
    colPlan: 'Pla',
    colStatus: 'Estat',
    colCreatedAt: 'Data d’alta',
    colLastAccess: 'Últim accés',
    suspend: 'Suspèn',
    reactivate: 'Reactiva',
    suspendReason: 'Motiu de la suspensió',
    reactivateReason: 'Motiu de la reactivació',
    supportAccess: 'Accés de suport',
    supportAccessReason: 'Motiu de l’accés',
  },

  'platformConsole.statusLabel': {
    active: 'Actiu',
    suspended: 'Suspès',
    deactivated: 'Desactivat',
  },

  'platformConsole.sectorLabel': {
    forn: 'Forn',
    pastisseria: 'Pastisseria',
    xarcuteria: 'Xarcuteria',
    precuinats: 'Precuinats',
    conserves: 'Conserves',
    generic: 'Genèric',
  },

  'platformConsole.planLabel': {
    obrador: 'Obrador',
    pime: 'PIME',
    cadena: 'Cadena',
  },

  'platformConsole.create': {
    title: 'Nou obrador',
    subtitle: 'Alta d’un obrador (tenant) a la plataforma',
    sectionFiscal: 'Dades de l’obrador',
    sectionPlan: 'Pla i idioma',
    sectionAdmin: 'Admin inicial',
    name: 'Nom comercial',
    legalName: 'Raó social',
    taxId: 'NIF',
    sector: 'Sector',
    plan: 'Pla',
    language: 'Idioma',
    adminEmail: 'Email de l’admin inicial',
    submit: 'Crea obrador',
    cancel: 'Cancel·la',
    deployNote:
      'En crear l’obrador es desplega l’estructura base per defecte (magatzems, famílies, torns, zones). El compte de l’admin es dona d’alta després, des de la fitxa de l’obrador.',
    createError: 'No s’ha pogut crear l’obrador',
    taxIdTaken: 'Aquest NIF ja pertany a un obrador actiu.',
  },

  'platformConsole.detail': {
    title: 'Fitxa de l’obrador',
    sectionFiscal: 'Dades fiscals',
    sectionPlan: 'Pla i sector',
    sectionStatus: 'Estat',
    sectionDanger: 'Baixa de l’obrador',
    name: 'Nom comercial',
    legalName: 'Raó social',
    taxId: 'NIF',
    sector: 'Sector',
    plan: 'Pla',
    createdAt: 'Data d’alta',
    lastAccess: 'Últim accés',
    save: 'Desa els canvis',
    saved: 'Canvis desats',
    saveError: 'No s’han pogut desar els canvis',
    saveTitle: 'Desa els canvis de l’obrador',
    saveBody:
      'La modificació de les dades fiscals i del pla queda registrada a l’auditoria. Indica el motiu del canvi.',
    suspend: 'Suspèn obrador',
    reactivate: 'Reactiva obrador',
    deactivate: 'Desactiva obrador',
    suspendTitle: 'Suspèn l’obrador',
    suspendBody:
      'Es bloqueja immediatament tot l’accés (backoffice, app i API). Les dades es conserven íntegres i és reversible.',
    reactivateTitle: 'Reactiva l’obrador',
    reactivateBody: 'Es restaura l’accés amb totes les dades intactes.',
    deactivateTitle: 'Desactiva l’obrador',
    deactivateBody:
      'Baixa lògica reversible: es bloqueja tot l’accés i l’obrador desapareix dels llistats. Les dades regulatòries (lots, OTs, traçabilitat, APPCC) es conserven íntegres. La purga física només es fa per procés diferit, mai des d’aquí.',
    reasonLabel: 'Motiu',
    reasonPlaceholder: 'Indica el motiu (obligatori)',
    reasonRequired: 'Cal indicar un motiu',
    notFound: 'No s’ha trobat l’obrador',
    deletedBanner: 'Aquest obrador està desactivat. Reactiva’l per editar-lo.',
    suspendedBanner: 'Aquest obrador està suspès.',
    sectionOwner: 'Usuari principal',
    // Registre d'activitat (F0.9)
    sectionActivity: 'Registre d’activitat',
    activityNotice:
      'Consultar aquest registre queda enregistrat al log de l’obrador i és visible pel client.',
    activityEmpty: 'Sense activitat registrada',
    activityError: 'No s’ha pogut carregar el registre d’activitat',
    activityLoadMore: 'Carrega’n més',
    activityFilterAllActions: 'Totes les accions',
    activityFilterAllEntities: 'Tots els elements',
    ownerName: 'Nom',
    ownerEmail: 'Correu',
    ownerStatus: 'Estat del compte',
    ownerStatusActive: 'Actiu',
    ownerStatusBlocked: 'Bloquejat',
    ownerStatusInvited: 'Convidat (pendent d’alta)',
    ownerStatusNone: 'Sense propietari assignat',
    ownerNone: 'Aquest obrador encara no té cap propietari ni correu d’administrador.',
    ownerLoadError: 'No s’ha pogut carregar l’usuari principal.',
    ownerResetCta: 'Envia un codi d’accés nou',
    ownerResetHint:
      'El propietari rebrà un codi al seu correu per establir una contrasenya nova. La consola no mostra cap contrasenya.',
    ownerResetSent: 'S’ha enviat un codi d’accés a {{email}}.',
    ownerResetError: 'No s’ha pogut enviar el codi d’accés.',
    ownerCreateHint:
      'Dona d’alta el propietari directament. Es crearà el seu compte i rebrà un correu per establir la seva contrasenya; no cal esperar que es registri.',
    ownerCreateName: 'Nom del propietari',
    ownerCreateEmail: 'Correu del propietari',
    ownerCreateNameRequired: 'Cal indicar el nom',
    ownerCreateEmailInvalid: 'Correu no vàlid',
    ownerCreatePassword: 'Contrasenya (opcional)',
    ownerCreatePasswordPlaceholder: 'Mínim 6 caràcters',
    ownerCreatePasswordHint:
      'Si poses una contrasenya, el propietari hi entrarà directament (comunica-la-hi tu). Si la deixes en blanc, rebrà un correu per establir-la ell mateix.',
    ownerCreatePasswordWeak: 'La contrasenya ha de tenir com a mínim 6 caràcters',
    ownerCreateCta: 'Dona d’alta el propietari',
    ownerCreated: 'Compte creat. S’ha enviat un correu a {{email}} per establir la contrasenya.',
    ownerCreatedWithPassword:
      'Compte creat per a {{email}} amb la contrasenya indicada. Ja pot iniciar sessió.',
    ownerCreatedNoEmail:
      'Compte creat per a {{email}}, però no s’ha pogut enviar el correu. Torna-ho a provar amb «Envia un codi d’accés nou».',
    ownerCreateError: 'No s’ha pogut crear el compte del propietari.',
    ownerEmailInUse: 'Aquest correu ja té un compte. Fes servir «Envia un codi d’accés nou».',
    ownerExistsLinkHint:
      'Aquest correu ({{email}}) ja té un compte a InsForge. Es vincularà a aquest obrador i hi podrà accedir amb la seva contrasenya actual, sense cap correu.',
    ownerLinkCta: 'Vincula a l’obrador',
    ownerLinked:
      'Compte vinculat a l’obrador. El propietari ja hi pot accedir amb la seva contrasenya actual.',
  },

  // ── Sector vocabulary OVERRIDE layers (shadow domain.* per dossier §7.8) ─────
  'sectors.forn': {
    finishedProduct: 'Producte de forn',
    'unit.loaves': 'barres',
  },
  'sectors.pastisseria': {
    finishedProduct: 'Producte de pastisseria',
  },
  'sectors.xarcuteria': {
    finishedProduct: 'Producte de xarcuteria',
    'unit.loaves': 'peces',
  },
  'backoffice.brandingTab': {
    logoSection: 'Logotip',
    logoHint: 'Es mostra a la capçalera de la fitxa tècnica. Imatge (PNG o JPG), màxim 2 MB.',
    logoUploading: 'Pujant…',
    logoRemove: 'Treu el logotip',
    logoInvalidType: 'El fitxer ha de ser una imatge.',
    logoTooLarge: 'La imatge supera els 2 MB.',
    logoUploadError: 'No s’ha pogut pujar el logotip.',
    companySection: 'Dades de l’empresa (capçalera de la FT)',
    companyHint: 'Aquestes dades apareixen a la capçalera de la fitxa tècnica. Emplena només les que tinguis.',
    fieldInvalid: 'Valor no vàlid',
    field_legal_name: 'Raó social',
    field_tax_id: 'NIF / CIF',
    field_address: 'Adreça',
    field_city: 'Població',
    field_postal_code: 'Codi postal',
    field_province: 'Província',
    field_phone: 'Telèfon',
    field_email: 'Correu electrònic',
    field_website: 'Lloc web',
    field_sanitary_registry: 'Registre sanitari (RGSEAA)',
    designSection: 'Disseny de la fitxa tècnica',
    designHint: 'Tria l’estil amb què es genera el PDF de la fitxa tècnica.',
    design_classic: 'Clàssic',
    designDesc_classic: 'Com la mostra: capçalera amb logotip i seccions emmarcades.',
    design_modern: 'Modern',
    designDesc_modern: 'Net, amb tipografia de pal sec i color d’accent.',
    design_compact: 'Compacte',
    designDesc_compact: 'Dens, tota la informació atapeïda en una pàgina.',
  },
  'backoffice.labelPrint': {
    proofCta: 'Prova d’etiqueta',
    cta: 'Genera etiqueta',
    generating: 'Generant…',
    error: 'No s’ha pogut generar l’etiqueta.',
    noTemplate: 'No hi ha cap format d’etiqueta configurat. Defineix-ne un a Configuració · Formats d’etiqueta.',
    noSheet: 'Aquest article encara no té cap fitxa tècnica aprovada: l’etiqueta sortirà sense ingredients, al·lèrgens ni informació nutricional.',
    sampleLot: 'LOT-DE-PROVA',
    sampleDate: '00/00/0000',
    ingredientsPrefix: 'Ingredients',
    copies: 'Còpies',
    issued: 'Etiqueta generada i registrada.',
  },

  'backoffice.labelEditor': {
    field_legal_denomination: 'Denominació legal',
    field_product_name: 'Nom comercial',
    field_ingredients: 'Ingredients',
    field_allergens: 'Al·lèrgens',
    field_nutrition: 'Informació nutricional',
    field_net_weight: 'Quantitat neta',
    field_conservation: 'Conservació',
    field_country_of_origin: 'País d\'origen',
    field_business_name: 'Empresa',
    field_business_address: 'Adreça de l\'empresa',
    field_sanitary_registry: 'Registre sanitari',
    field_lot_number: 'Número de lot',
    field_production_date: 'Data de producció',
    field_expiry_date: 'Data de caducitat',
    field_best_before: 'Consum preferent',
    field_quantity: 'Quantitat',
    field_sku: 'Codi (SKU)',
    field_custom_text: 'Text lliure',
    field_qr: 'Codi QR',
    field_barcode: 'Codi de barres',
    field_logo: 'Logotip',
    field_image: 'Imatge del producte',
    field_spacer: 'Espai',
    align_left: 'Esquerra',
    align_center: 'Centre',
    align_right: 'Dreta',
    editTitle: 'Edita el format d\'etiqueta',
    newTitle: 'Nou format d\'etiqueta',
    back: 'Torna a la llista',
    dimensionsTitle: 'Dimensions (mm)',
    widthMm: 'Amplada (mm)',
    heightMm: 'Alçada (mm)',
    paddingMm: 'Marge intern (mm)',
    showBorder: 'Mostra la vora',
    blocksTitle: 'Contingut de l\'etiqueta',
    resetDefault: 'Restaura el disseny per defecte',
    blockField: 'Camp',
    blockCaption: 'Prefix (opcional)',
    blockText: 'Text',
    blockFont: 'Mida (pt)',
    blockAlign: 'Alineació',
    blockBold: 'Negreta',
    addBlock: 'Afegeix bloc',
    previewTitle: 'Vista prèvia',
    previewHint: 'Resultat aproximat · {{w}}×{{h}} mm · {{type}}. Dades de mostra.',
    missingMandatory: 'Falten camps obligatoris a l\'etiqueta de venda: {{fields}}.',
    invalidLayout: 'El disseny de l\'etiqueta no és vàlid. Revisa les dimensions i els blocs.',
  },
  ...existenciesCa,
  ...qualityCa,
  ...compresCa,
  ...catalegCa,
} as const;

export type CaResources = typeof ca;
